import { useEffect, useState, useRef, useMemo, useCallback, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDynamicTableHeight } from "@/hooks/use-dynamic-table-height";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

import { ScrollableTabsContainer } from "@/components/ui/scrollable-tabs-container";
import { StickyFooter } from "@/components/ui/sticky-footer";
import { ClientCombobox } from "@/components/ui/client-combobox";
import { ClientChangeConfirmDialog } from "@/components/client-change-confirm-dialog";
import { EmployeeCombobox } from "@/components/ui/employee-combobox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Calendar,
  DollarSign,
  Users,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Activity,
  BarChart2,
  Info,
  BarChart3,
  Edit,
  MoreVertical,
  UserCheck,
  UserX,
  ArrowUpDown,
  Settings2,
  Plus,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Trash2,
  LayoutDashboard,
  Landmark,
  PieChart,
  Building2,
  Layers,
  UsersRound,
  Milestone as MilestoneIcon,
  Banknote,
  GitPullRequestArrow,
  ArrowLeft,
  History,
  Download,
  ShoppingCart,
  Receipt,
  CreditCard,
  Loader2,
  Columns,
  Search,
  Pencil,
  X,
  ShieldCheck,
  SlidersHorizontal,
  CalendarDays,
  ExternalLink,
  AlertTriangle,
  MapPin,
  CheckCircle2,
  Lock,
  MessageSquarePlus,
  Send,
  Clock3,
} from "lucide-react";
import { SensitiveData } from "@/components/sensitive-data";
import {
  BarChart as RechartsBarChart,
  Bar,
  LineChart as RechartsLineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell as RechartsCell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  ChartContainer,
  ChartTooltipContent,
  ChartLegendContent,
  ChartTooltip,
  ChartLegend,
  ChartStyle,
} from "@/components/ui/chart";
import type { ChartConfig } from "@/components/ui/chart";
import {
  BudgetCard,
  SalesBudgetView,
  CostBudgetView,
  ResourceBudgetWrapper,
  BudgetSummaryCards,
  BudgetAlertsSection,
  EditBudgetModal,
  BudgetHistoryModal,
  BudgetExportModal,
} from "@/components/blocks/budget";
import { EditResourceBudgetPage } from "@/components/blocks/budget/edit-resource-budget-page";
import { EditCostBudgetPage } from "@/components/blocks/budget/edit-cost-budget-page";
import { EditSalesBudgetPage } from "@/components/blocks/budget/edit-sales-budget-page";
import { ResourceBudgetMetrics } from "@/components/blocks/budget/resource-budget-metrics";
import { UnfulfilledRolesTable } from "@/components/blocks/unfulfilled-roles-table";
import { InvoiceGenerationWizard } from "@/components/billing/InvoiceGenerationWizard";
import { InvoiceDetailsSheet } from "@/components/billing/InvoiceDetailsSheet";
import { FixedFeeBillingTab } from "@/components/billing/FixedFeeBillingTab";
import { ProjectAuditTab } from "@/components/blocks/audit/project-audit-tab";
import type { AuditInvoice, AuditEvent } from "@/components/blocks/audit/project-audit-tab";
import {
  PROJECT_ROLE_LABELS,
  PROJECT_ROLE_DISPLAY_ORDER,
  type ProjectRoleType,
  type ProjectRoleConfig,
  type ProjectRoleAssignment,
  type ProjectNote,
} from "@/types";
import { useAuth } from "@/providers/AuthProvider";
import { appendAuditEntry } from "@/lib/audit-log-storage";
import { ResourceRequestModal } from "@/components/resource-request-modal";
import { useInvoiceTemplates } from "@/contexts/InvoiceTemplateContext";
import { getProjectBillingConfig, updateProjectBillingConfig } from "@/services/billingService";
import { CreateDraftDialog } from "@/components/billing/CreateDraftDialog";
import { useBudgetPermissions } from "@/hooks/use-budget-permissions";
import { useBudgetData, useUpdateBudget } from "@/hooks/use-budget-data";
import { useResourceBudgetSettings } from "@/hooks/use-resource-budget-settings";
import { enrichBudgetWithAllocatedHours } from "@/lib/budget-team-utils";
import { useCompany } from "@/contexts/CompanyContext";
import type { BudgetData } from "@/types";
import PageHeaderBlock from "@/registry/evo-shadcn/blocks/page-header";
import { toast } from "sonner";
import {
  CostCentresTable,
  DEFAULT_COST_CENTRE_COLUMNS,
  type CostCentre,
  type CostCentreColumnDefinition,
} from "@/components/blocks/cost-centres/cost-centres-table";
import { CostCentresColumnConfigurator } from "@/components/blocks/cost-centres/cost-centres-column-configurator";
import { ColumnConfiguratorDrawer, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import {
  DataTableEnhanced,
  columnDefinitionsToVisibility,
  cycleColumnSort,
  type ColumnSort,
  type ColumnDef,
} from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import { calculatePagination, paginateArray, type PaginationMetadata } from '@/types/pagination';
import {
  CostCentresHierarchicalTable,
  type HierarchicalCostCentre,
} from "@/components/blocks/cost-centres/cost-centres-hierarchical-table";
import { PurchasesCommitmentsTab } from "@/components/blocks/purchases/purchases-commitments-tab";
import { TimeEntriesTab } from "@/components/blocks/time/time-entries-tab";
import { ExpenseEntriesTab } from "@/components/blocks/expenses/expense-entries-tab";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfigureStructureModal } from "@/components/blocks/cost-centres/configure-structure-modal";
import { CostCentreDetailsPanel } from "@/components/blocks/cost-centres/cost-centre-details-panel";
import { CreateCostCentrePanel } from "@/components/blocks/cost-centres/create-cost-centre-panel";
import { CostCentreFullModal } from "@/components/blocks/cost-centres/cost-centre-full-modal";
import { formatHours } from "@/lib/budget-utils";
import { getSchedulingThresholds, deriveHealthStatus } from "@/lib/scheduling-thresholds-storage";
import { useSchedulerConnection } from "@/hooks/use-scheduler-connection";
import { fetchSchedulingBookings, fetchResourceRequests, type SchedulingBookingsResponse, type ResourceRequestResponse } from "@/services/schedulingService";
import {
  mockSchedulerFeatureFlag,
  mockSchedulingSummary,
  mockSchedulingBookings,
  mockResourceRequests,
  type ResourceRequest,
  mockRoleCoverage,
  mockSchedulingWarnings,
  mockSchedulerConnection,
  type SchedulingSummary,
  type SchedulingBooking,
  type RoleCoverage,
  type SchedulingWarning,
  type BookingStatus,
  type RoleCoverageStatus,
  mockTeamBookingStatuses,
  mockUnbookedTimesheetersCount,
  mockUnbookedTimesheetersNames,
} from "@/lib/mock-scheduling-data";
import {
  fetchProjectById,
  fetchClients,
  fetchEmployees,
  fetchResourceAllocations,
  fetchCostCentresByProjectId,
  fetchTimeEntriesByProjectId,
  fetchExpensesByProjectId,
  fetchMilestonesByProjectId,
  fetchChangeRequestsByProjectId,
  createCostCentre,
  updateProject,
  getDataMode,
  createResourceAllocation,
  deleteResourceAllocation,
  createPersonBudget,
  saveColumnPreferences,
  fetchColumnPreferences,
  fetchResourceBudgetIdForProject,
  fetchBillingWipItems,
  fetchBillingWipItemsPaged,
  fetchBillingInvoices,
  createBillingDraft,
  updateBillingDraft,
  deleteBillingDraft,
  postBillingDraftToFinancials,
  voidBillingInvoice,
  mapInvoiceStatus,
  mapWipSourceType,
  fetchProjectNotes,
  createProjectNote,
  saveProjectObjectives,
  saveProjectKeyDeliverables,
} from "@/lib/data-service";
import type { FetchExpensesOptions } from "@/lib/data-service";
import type { BillingWipItem, BillingInvoice, PostToFinancialsResponse, PaginatedBillingWipResponse, WipSummary } from "@/lib/data-service";
import { useErrorHandler } from "@/hooks/use-error-handler";
import {
  formatCurrency,
  formatPercent,
  formatDate,
  formatDateForInput,
  getHealthBadgeColor,
  getStatusColor,
  formatStatus,
  getVarianceColor,
  cn,
} from "@/lib/utils";
import { ClientSidePanel } from '@/components/client-side-panel';
import { useClientPanel } from '@/hooks/use-client-panel';
import { getProjectContentSettings } from '@/lib/project-content-settings-storage';
import { getBudgetHealthThresholds } from '@/lib/budget-health-thresholds-storage'; // Task 5
import { calculateGP, calculateBudgetGP } from '@/lib/gp-calculation'; // Task 23
import { getFinanceSettings } from '@/lib/finance-settings-storage'; // Task 23
import {
  fetchCustomFieldDefinitions,
  fetchProjectCustomFieldValues,
  saveProjectCustomFieldValues,
  fetchProjectRoleAssignments,
  saveProjectRoleAssignments,
  fetchProjectRoleConfigs,
} from '@/lib/data-service';
import type { CustomFieldDefinitionResponse } from '@/lib/data-service';
import { ROUTES } from "@/routes";
import type {
  Project,
  Client,
  Employee,
  TimeEntry,
  Expense,
  Milestone,
  ResourceAllocation,
  ChangeRequest,
  CompletionPercentAuditEntry,
} from "@/types";
import { PROJECT_STATUS_LABELS, type ProjectStatusId } from "@/types";
import { mockCompletionAuditData } from "@/lib/mock-data";
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';

interface ProjectDetailsPageProps {
  projectId?: string;
  onBack?: () => void;
}

interface TeamMember {
  id: number | string;
  employeeId: number;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  allocatedHours: number;
  actualHours: number;
  remaining: number;
  startDate: string;
  endDate: string;
  status: "On Track" | "At Risk" | "Over Allocated";
  utilisationPercent: number;
}

// Default invoice column definitions (extracted for useColumnPreferences)
const DEFAULT_INVOICE_COLUMNS: ColumnDefinition[] = [
  {
    id: 'invoice-number',
    name: 'Invoice #',
    description: 'Invoice number',
    category: 'Status',
    visible: true,
    locked: true,
    order: 0,
  },
  {
    id: 'date',
    name: 'Date',
    description: 'Invoice date',
    category: 'Timeline',
    visible: true,
    order: 1,
  },
  {
    id: 'description',
    name: 'Description',
    description: 'Invoice description',
    category: 'Status',
    visible: true,
    order: 2,
  },
  {
    id: 'amount',
    name: 'Amount',
    description: 'Invoice amount',
    category: 'Financial',
    visible: true,
    order: 3,
    align: 'right' as const,
  },
  {
    id: 'days-overdue',
    name: 'Days Overdue',
    description: 'Number of days past due date',
    category: 'Status',
    visible: true,
    order: 4,
    align: 'right' as const,
  },
  {
    id: 'status',
    name: 'Status',
    description: 'Invoice status',
    category: 'Status',
    visible: true,
    order: 5,
  },
];

// Default WIP column definitions (extracted for useColumnPreferences)
const DEFAULT_WIP_COLUMNS: ColumnDefinition[] = [
  {
    id: 'date',
    name: 'Date',
    description: 'Work date',
    category: 'Timeline',
    visible: true,
    locked: true,
    order: 0,
  },
  {
    id: 'resource',
    name: 'Resource',
    description: 'Person or resource',
    category: 'Resource',
    visible: true,
    order: 1,
  },
  {
    id: 'task',
    name: 'Task',
    description: 'Task name',
    category: 'Status',
    visible: true,
    order: 2,
  },
  {
    id: 'description',
    name: 'Description',
    description: 'Work description',
    category: 'Status',
    visible: true,
    order: 3,
  },
  {
    id: 'type',
    name: 'Type',
    description: 'Time or Expense',
    category: 'Status',
    visible: true,
    order: 4,
  },
  {
    id: 'quantity',
    name: 'Qty',
    description: 'Quantity (hours or items)',
    category: 'Financial',
    visible: true,
    order: 5,
    align: 'right' as const,
  },
  {
    id: 'rate',
    name: 'Rate',
    description: 'Billing rate',
    category: 'Financial',
    visible: true,
    order: 6,
    align: 'right' as const,
  },
  {
    id: 'amount',
    name: 'Amount',
    description: 'Total amount',
    category: 'Financial',
    visible: true,
    order: 7,
    align: 'right' as const,
  },
  {
    id: 'costCentre',
    name: 'Cost Centre',
    description: 'External cost centre code',
    category: 'Financial',
    visible: true,
    order: 8,
  },
];

// ---------------------------------------------------------------------------
// ResourcingTabContent — helpers
// ---------------------------------------------------------------------------

/** Returns Tailwind classes for a BookingStatus pill badge */
function getBookingStatusStyle(status: BookingStatus): string {
  switch (status) {
    case 'Confirmed':  return 'bg-green-100 text-green-800 border-green-200';
    case 'Planned':    return 'bg-blue-100 text-blue-800 border-blue-200 border-dashed';
    case 'Cancelled':  return 'bg-red-100 text-red-800 border-red-200 line-through';
    case 'Completed':  return 'bg-gray-100 text-gray-600 border-gray-200';
    default:           return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

/** Returns Tailwind classes + label for a RoleCoverageStatus cell */
function getRoleCoverageStyle(status: RoleCoverageStatus): {
  dot: string;
  label: string;
  text: string;
} {
  switch (status) {
    case 'FullyCovered':
      return { dot: 'bg-green-500', label: 'Fully Covered',      text: 'text-green-700' };
    case 'PartiallyCovered':
      return { dot: 'bg-yellow-500', label: 'Partially Covered', text: 'text-yellow-700' };
    case 'NotCovered':
      return { dot: 'bg-red-500',    label: 'Not Covered',       text: 'text-red-700'   };
    case 'OverScheduled':
      return { dot: 'bg-orange-500', label: 'Over-scheduled',    text: 'text-orange-700'};
    default:
      return { dot: 'bg-gray-400',   label: 'Unknown',           text: 'text-gray-600'  };
  }
}

/** Returns Tailwind classes for a warning severity banner — kept for Warnings sub-tab only */
function getWarningSeverityStyle(severity: 'critical' | 'warning' | 'info'): {
  bg: string;
  border: string;
  icon: string;
  text: string;
} {
  switch (severity) {
    case 'critical':
      return { bg: 'bg-red-50',    border: 'border-red-200',    icon: 'text-red-500',    text: 'text-red-800'    };
    case 'warning':
      return { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: 'text-yellow-500', text: 'text-yellow-800' };
    case 'info':
      return { bg: 'bg-blue-50',   border: 'border-blue-200',   icon: 'text-blue-500',   text: 'text-blue-800'   };
    default:
      return { bg: 'bg-gray-50',   border: 'border-gray-200',   icon: 'text-gray-400',   text: 'text-gray-700'   };
  }
}

// ---------------------------------------------------------------------------
// ResourcingTabContent — component
// ---------------------------------------------------------------------------

interface ResourcingTabContentProps {
  bookings: SchedulingBooking[];
  roleCoverage: RoleCoverage[];
  warnings: SchedulingWarning[];
  summary: SchedulingSummary;
  schedulerName: string;
  onRaiseRequest?: (roleName?: string) => void;
  resourceRequests?: ResourceRequest[];
  /** Whether a Resource Budget exists for this project */
  hasResourceBudget?: boolean;
  /** Navigate the user to the Resource Budget tab to create one */
  onNavigateToResourceBudget?: () => void;
}

function ResourcingTabContent({
  bookings,
  roleCoverage,
  warnings,
  summary,
  schedulerName,
  onRaiseRequest,
  resourceRequests = [],
  hasResourceBudget = false,
  onNavigateToResourceBudget,
}: ResourcingTabContentProps) {
  // Per-session dismissible warnings
  const [dismissedWarnings, setDismissedWarnings] = useState<Set<number>>(new Set());
  // Expandable role rows in the Role Coverage table
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
  // "Open in Scheduler" confirmation dialog
  const [openSchedulerDialogOpen, setOpenSchedulerDialogOpen] = useState(false);
  // Which sub-tab is active: bookings | coverage | requests
  const [resourcingSubTab, setResourcingSubTab] = useState<'bookings' | 'coverage' | 'requests'>('bookings');

  // Bookings table sort/pagination
  const [bookingColumnSorts, setBookingColumnSorts] = useState<ColumnSort[]>([{ columnId: 'startDate', direction: 'asc' }]);
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingPageSize, setBookingPageSize] = useState(25);

  // Role Coverage table sort/pagination
  const [coverageColumnSorts, setCoverageColumnSorts] = useState<ColumnSort[]>([]);
  const [coveragePage, setCoveragePage] = useState(1);
  const [coveragePageSize, setCoveragePageSize] = useState(25);

  // Resource Requests table sort/pagination
  const [requestsColumnSorts, setRequestsColumnSorts] = useState<ColumnSort[]>([{ columnId: 'raisedAt', direction: 'desc' }]);
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsPageSize, setRequestsPageSize] = useState(25);

  const dismissWarning = (index: number) => {
    setDismissedWarnings(prev => new Set(prev).add(index));
  };

  const toggleRoleExpand = (roleId: string) => {
    setExpandedRoles(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  };

  const visibleWarnings = warnings.filter((_, i) => !dismissedWarnings.has(i));

  // Derived KPIs from summary
  const totalUnmetHours = summary.unmetRoles.reduce((sum, r) => sum + r.gapHours, 0);

  // Bookings sorted + paginated
  const sortedBookings = useMemo(() => {
    const numCols = new Set(['totalHours', 'actualHours']);
    return [...bookings].sort((a, b) => {
      for (const { columnId, direction } of bookingColumnSorts) {
        const aVal = a[columnId as keyof SchedulingBooking];
        const bVal = b[columnId as keyof SchedulingBooking];
        const mult = direction === 'asc' ? 1 : -1;
        if (numCols.has(columnId)) {
          const diff = ((Number(aVal) || 0) - (Number(bVal) || 0)) * mult;
          if (diff !== 0) return diff;
        } else {
          const cmp = String(aVal ?? '').localeCompare(String(bVal ?? '')) * mult;
          if (cmp !== 0) return cmp;
        }
      }
      return 0;
    });
  }, [bookings, bookingColumnSorts]);
  const bookingPagination = useMemo<PaginationMetadata>(
    () => calculatePagination(sortedBookings.length, bookingPage, bookingPageSize),
    [sortedBookings.length, bookingPage, bookingPageSize],
  );
  const paginatedBookings = useMemo(
    () => paginateArray(sortedBookings, bookingPage, bookingPageSize).data,
    [sortedBookings, bookingPage, bookingPageSize],
  );

  // Role Coverage sorted + paginated
  const sortedCoverage = useMemo(() => {
    const numCols = new Set(['budgetHours', 'scheduledHours', 'actualHours', 'remainingHours', 'coveragePercent']);
    return [...roleCoverage].sort((a, b) => {
      for (const { columnId, direction } of coverageColumnSorts) {
        const aVal = a[columnId as keyof RoleCoverage];
        const bVal = b[columnId as keyof RoleCoverage];
        const mult = direction === 'asc' ? 1 : -1;
        if (numCols.has(columnId)) {
          const diff = ((Number(aVal) || 0) - (Number(bVal) || 0)) * mult;
          if (diff !== 0) return diff;
        } else {
          const cmp = String(aVal ?? '').localeCompare(String(bVal ?? '')) * mult;
          if (cmp !== 0) return cmp;
        }
      }
      return 0;
    });
  }, [roleCoverage, coverageColumnSorts]);
  const coveragePagination = useMemo<PaginationMetadata>(
    () => calculatePagination(sortedCoverage.length, coveragePage, coveragePageSize),
    [sortedCoverage.length, coveragePage, coveragePageSize],
  );
  const paginatedCoverage = useMemo(
    () => paginateArray(sortedCoverage, coveragePage, coveragePageSize).data,
    [sortedCoverage, coveragePage, coveragePageSize],
  );

  // Resource Requests sorted + paginated
  const sortedRequests = useMemo(() => {
    const numCols = new Set(['hoursRequired']);
    return [...resourceRequests].sort((a, b) => {
      for (const { columnId, direction } of requestsColumnSorts) {
        const aVal = a[columnId as keyof ResourceRequest];
        const bVal = b[columnId as keyof ResourceRequest];
        const mult = direction === 'asc' ? 1 : -1;
        if (numCols.has(columnId)) {
          const diff = ((Number(aVal) || 0) - (Number(bVal) || 0)) * mult;
          if (diff !== 0) return diff;
        } else {
          const cmp = String(aVal ?? '').localeCompare(String(bVal ?? '')) * mult;
          if (cmp !== 0) return cmp;
        }
      }
      return 0;
    });
  }, [resourceRequests, requestsColumnSorts]);
  const requestsPagination = useMemo<PaginationMetadata>(
    () => calculatePagination(sortedRequests.length, requestsPage, requestsPageSize),
    [sortedRequests.length, requestsPage, requestsPageSize],
  );
  const paginatedRequests = useMemo(
    () => paginateArray(sortedRequests, requestsPage, requestsPageSize).data,
    [sortedRequests, requestsPage, requestsPageSize],
  );

  return (
    <div className="space-y-4">

      {/* ── Header row: title + Open in Scheduler button ──────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-muted-foreground" />
            Resourcing
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Booking coverage and scheduling data from {schedulerName}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => setOpenSchedulerDialogOpen(true)}
        >
          <ExternalLink className="h-4 w-4" />
          Open in {schedulerName}
        </Button>
      </div>

      {/* ── KPI Summary Cards ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {/* Scheduled Hours */}
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Scheduled Hours</p>
            <p className="text-2xl font-bold">
              {summary.totalScheduledHours}
              <span className="text-sm font-normal text-muted-foreground">
                {' '}/ {summary.totalBudgetHours} hrs
              </span>
            </p>
            <Progress value={summary.coveragePercent} className="mt-2 h-1.5" />
          </CardContent>
        </Card>
        {/* Booking Coverage */}
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Booking Coverage</p>
            <p className="text-2xl font-bold">{summary.coveragePercent}%</p>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.confirmedBookings} confirmed · {summary.plannedBookings} planned
            </p>
          </CardContent>
        </Card>
        {/* Confirmed Bookings */}
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Confirmed Bookings</p>
            <p className="text-2xl font-bold">{summary.confirmedBookings}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.totalBookings} total bookings
            </p>
          </CardContent>
        </Card>
        {/* Unmet Demand */}
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Unmet Demand</p>
            <p className="text-2xl font-bold text-red-600">{totalUnmetHours} hrs</p>
            <p className="text-xs text-muted-foreground mt-1">
              across {summary.unmetRoles.length} role{summary.unmetRoles.length !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Sub-tab switcher ───────────────────────────────────────────────── */}
      <div className="flex gap-1 border-b">
        {(['bookings', 'coverage', 'requests'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setResourcingSubTab(tab)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors capitalize flex items-center gap-1.5',
              resourcingSubTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground',
            )}
          >
            {tab === 'bookings' ? 'Bookings' : tab === 'coverage' ? 'Role Coverage' : (
              <>
                Resource Requests
                {resourceRequests.length > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-medium h-4 px-1.5">
                    {resourceRequests.length}
                  </span>
                )}
              </>
            )}
          </button>
        ))}
      </div>

      {/* ── Bookings sub-tab ───────────────────────────────────────────────── */}
      {resourcingSubTab === 'bookings' && (
        <Card>
          <CardContent className="p-0">
            {bookings.length === 0 ? (
              /* Empty state */
              <div className="flex flex-col items-center justify-center py-16 gap-4 text-center px-6">
                <CalendarDays className="h-12 w-12 text-muted-foreground/40" />
                <div>
                  <p className="font-medium text-foreground">No bookings found for this project</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {hasResourceBudget
                      ? 'Raise a resource request to create a booking, or add bookings directly in your scheduling tool.'
                      : 'Create a Resource Budget to start raising resource requests and scheduling bookings.'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {hasResourceBudget ? (
                    <Button variant="default" size="sm" onClick={() => onRaiseRequest?.()}>
                      Raise Resource Request
                    </Button>
                  ) : (
                    <Button variant="default" size="sm" onClick={() => onNavigateToResourceBudget?.()}>
                      <Plus className="h-4 w-4 mr-1.5" />
                      Create Resource Budget
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setOpenSchedulerDialogOpen(true)}>
                    <ExternalLink className="h-4 w-4 mr-1.5" />
                    Open in {schedulerName}
                  </Button>
                </div>
              </div>
            ) : (() => {
                const bookingColDefs: ColumnDef<SchedulingBooking, unknown>[] = [
                  {
                    id: 'personName',
                    header: 'Resource',
                    accessorKey: 'personName',
                    cell: ({ row }) => (
                      <div className="flex items-center gap-2">
                        {row.original.personId ? (
                          <Avatar className="h-7 w-7">
                            <AvatarImage src={row.original.personAvatar ?? undefined} alt={row.original.personName ?? ''} />
                            <AvatarFallback className="text-xs">
                              {(row.original.personName ?? '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <div className="h-7 w-7 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                            <Users className="h-3.5 w-3.5 text-muted-foreground/50" />
                          </div>
                        )}
                        <span className="text-sm font-medium">{row.original.personName ?? 'Unassigned'}</span>
                      </div>
                    ),
                  },
                  { id: 'roleName', header: 'Role', accessorKey: 'roleName', cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.original.roleName}</span> },
                  { id: 'startDate', header: 'Start Date', accessorKey: 'startDate', cell: ({ row }) => <span className="text-sm">{formatDate(row.original.startDate)}</span> },
                  { id: 'endDate', header: 'End Date', accessorKey: 'endDate', cell: ({ row }) => <span className="text-sm">{formatDate(row.original.endDate)}</span> },
                  { id: 'totalHours', header: 'Booked Hrs', accessorKey: 'totalHours', cell: ({ row }) => <span className="text-sm font-medium">{row.original.totalHours}</span> },
                  {
                    id: 'actualHours',
                    header: 'Actual Hrs',
                    accessorKey: 'actualHours',
                    cell: ({ row }) => row.original.actualHours !== null ? (
                      <span className={cn('font-medium text-sm',
                        (row.original.actualHours ?? 0) < row.original.totalHours ? 'text-amber-600'
                          : (row.original.actualHours ?? 0) > row.original.totalHours ? 'text-red-600'
                          : 'text-green-600',
                      )}>{row.original.actualHours}</span>
                    ) : <span className="text-muted-foreground">—</span>,
                  },
                  {
                    id: 'status',
                    header: 'Status',
                    accessorKey: 'status',
                    enableSorting: false,
                    cell: ({ row }) => (
                      <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', getBookingStatusStyle(row.original.status))}>
                        {row.original.status}
                      </span>
                    ),
                  },
                  {
                    id: 'source',
                    header: 'Source',
                    accessorKey: 'source',
                    enableSorting: false,
                    cell: ({ row }) => <span className="text-xs text-muted-foreground">{row.original.source === 'Projects' ? 'Raised in Projects' : 'Created in Scheduler'}</span>,
                  },
                  {
                    id: 'actions',
                    header: '',
                    enableSorting: false,
                    cell: ({ row }) => (row.original.status === 'Confirmed' || row.original.status === 'Planned') ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpenSchedulerDialogOpen(true)}>
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="left"><p>View in {schedulerName}</p></TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : null,
                  },
                ];
                return (
                  <DataTableEnhanced
                    columns={bookingColDefs}
                    data={paginatedBookings}
                    columnVisibility={Object.fromEntries(bookingColDefs.map(c => [c.id!, true]))}
                    columnSorts={bookingColumnSorts}
                    onColumnSortChange={(s) => { setBookingColumnSorts(s); setBookingPage(1); }}
                    emptyMessage={<span className="text-muted-foreground">No bookings found</span>}
                    paginationContent={
                      <PaginationControls
                        pagination={bookingPagination}
                        onPageChange={setBookingPage}
                        onPageSizeChange={(s) => { setBookingPageSize(s); setBookingPage(1); }}
                      />
                    }
                  />
                );
              })()
            }
          </CardContent>
        </Card>
      )}

      {/* ── Role Coverage sub-tab ──────────────────────────────────────────── */}
      {resourcingSubTab === 'coverage' && (
        <Card>
          <CardContent className="p-0">
            {(() => {
              const coverageColDefs: ColumnDef<RoleCoverage, unknown>[] = [
                {
                  id: 'roleName',
                  header: 'Role',
                  accessorKey: 'roleName',
                  cell: ({ row }) => {
                    const isExpanded = expandedRoles.has(row.original.roleId);
                    return (
                      <div className="flex items-center gap-2">
                        {row.original.people.length > 0
                          ? isExpanded
                            ? <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            : <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          : <span className="w-4 flex-shrink-0" />}
                        <span className="font-medium text-sm">{row.original.roleName}</span>
                      </div>
                    );
                  },
                },
                { id: 'budgetHours', header: 'Budget Hrs', accessorKey: 'budgetHours', cell: ({ row }) => <span className="text-sm">{row.original.budgetHours}</span> },
                { id: 'scheduledHours', header: 'Scheduled Hrs', accessorKey: 'scheduledHours', cell: ({ row }) => <span className="text-sm">{row.original.scheduledHours}</span> },
                { id: 'actualHours', header: 'Actual Hrs', accessorKey: 'actualHours', cell: ({ row }) => <span className="text-sm">{row.original.actualHours}</span> },
                { id: 'remainingHours', header: 'Remaining', accessorKey: 'remainingHours', cell: ({ row }) => <span className="text-sm">{row.original.remainingHours}</span> },
                {
                  id: 'coveragePercent',
                  header: 'Coverage',
                  accessorKey: 'coveragePercent',
                  cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                      <Progress value={Math.min(row.original.coveragePercent, 100)} className="h-1.5 w-16" />
                      <span className="text-xs text-muted-foreground">{row.original.coveragePercent}%</span>
                    </div>
                  ),
                },
                {
                  id: 'status',
                  header: 'Status',
                  accessorKey: 'status',
                  enableSorting: false,
                  cell: ({ row }) => {
                    const style = getRoleCoverageStyle(row.original.status);
                    return (
                      <div className="flex items-center gap-1.5">
                        <span className={cn('h-2 w-2 rounded-full flex-shrink-0', style.dot)} />
                        <span className={cn('text-xs font-medium', style.text)}>{style.label}</span>
                      </div>
                    );
                  },
                },
                {
                  id: 'actions',
                  header: '',
                  enableSorting: false,
                  cell: ({ row }) => row.original.coveragePercent < 100 ? (
                    hasResourceBudget ? (
                      <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={() => onRaiseRequest?.(row.original.roleName)}>Raise Request</Button>
                    ) : (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0}>
                              <Button variant="outline" size="sm" className="h-7 text-xs px-2" disabled>Raise Request</Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Create a Resource Budget first</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )
                  ) : null,
                },
              ];

              return (
                <DataTableEnhanced
                  columns={coverageColDefs}
                  data={paginatedCoverage}
                  columnVisibility={Object.fromEntries(coverageColDefs.map(c => [c.id!, true]))}
                  columnSorts={coverageColumnSorts}
                  onColumnSortChange={(s) => { setCoverageColumnSorts(s); setCoveragePage(1); }}
                  emptyMessage={<span className="text-muted-foreground">No role coverage data</span>}
                  onRowClick={(row) => {
                    const role = row as unknown as RoleCoverage;
                    if ((role as RoleCoverage).people?.length > 0) toggleRoleExpand((role as RoleCoverage).roleId);
                  }}
                  customRowRenderer={(row, defaultRow) => {
                    const role = row.original as unknown as RoleCoverage;
                    const isExpanded = expandedRoles.has(role.roleId);
                    return (
                      <>
                        {defaultRow}
                        {isExpanded && role.people.map(person => (
                          <tr key={person.personId} className="bg-muted/30">
                            <td className="px-4 py-2" colSpan={1}>
                              <div className="flex items-center gap-2 pl-10">
                                <span className="text-xs text-muted-foreground">└</span>
                                <span className="text-sm text-muted-foreground">{person.personName}</span>
                              </div>
                            </td>
                            <td />
                            <td className="px-4 py-2 text-right text-sm text-muted-foreground">{person.scheduledHours}</td>
                            <td className="px-4 py-2 text-right text-sm text-muted-foreground">{person.actualHours}</td>
                            <td colSpan={4} />
                          </tr>
                        ))}
                      </>
                    );
                  }}
                  paginationContent={
                    <PaginationControls
                      pagination={coveragePagination}
                      onPageChange={setCoveragePage}
                      onPageSizeChange={(s) => { setCoveragePageSize(s); setCoveragePage(1); }}
                    />
                  }
                />
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ── Resource Requests sub-tab ─────────────────────────────────────── */}
      {resourcingSubTab === 'requests' && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Resource Requests</CardTitle>
                <CardDescription className="text-sm mt-0.5">
                  Requests raised for the resourcing team to fill via {schedulerName}
                </CardDescription>
              </div>
              {hasResourceBudget ? (
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onRaiseRequest?.()}>
                  <Plus className="h-3.5 w-3.5" />
                  New Request
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>
                        <Button size="sm" variant="outline" className="gap-1.5" disabled>
                          <Plus className="h-3.5 w-3.5" />
                          New Request
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Create a Resource Budget first to raise requests</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {resourceRequests.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-center px-6">
                <CalendarDays className="h-10 w-10 text-muted-foreground/40" />
                <div>
                  <p className="font-medium text-foreground">No resource requests yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Raise a request from the Resource Budget tab to ask the resourcing team to fill a role.
                  </p>
                </div>
              </div>
            ) : (() => {
                const priorityLabel: Record<string, string> = { 'nice-to-have': 'Nice to Have', 'important': 'Important', 'critical': 'Critical' };
                const priorityStyle: Record<string, string> = { 'nice-to-have': 'bg-gray-100 text-gray-700 border-gray-200', 'important': 'bg-blue-100 text-blue-700 border-blue-200', 'critical': 'bg-red-100 text-red-700 border-red-200' };
                const statusStyle: Record<string, string> = { 'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-200', 'InProgress': 'bg-blue-100 text-blue-800 border-blue-200', 'Filled': 'bg-green-100 text-green-800 border-green-200', 'Cancelled': 'bg-gray-100 text-gray-600 border-gray-200' };
                const statusLabel: Record<string, string> = { 'Pending': 'Pending', 'InProgress': 'In Progress', 'Filled': 'Filled', 'Cancelled': 'Cancelled' };

                const requestColDefs: ColumnDef<ResourceRequest, unknown>[] = [
                  { id: 'roleName', header: 'Role', accessorKey: 'roleName', cell: ({ row }) => <span className="font-medium text-sm">{row.original.roleName}</span> },
                  { id: 'hoursRequired', header: 'Hours', accessorKey: 'hoursRequired', cell: ({ row }) => <span className="text-sm">{row.original.hoursRequired}</span> },
                  { id: 'startDate', header: 'From', accessorKey: 'startDate', cell: ({ row }) => <span className="text-sm">{formatDate(row.original.startDate)}</span> },
                  { id: 'endDate', header: 'To', accessorKey: 'endDate', cell: ({ row }) => <span className="text-sm">{formatDate(row.original.endDate)}</span> },
                  {
                    id: 'priority',
                    header: 'Priority',
                    accessorKey: 'priority',
                    enableSorting: false,
                    cell: ({ row }) => (
                      <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', priorityStyle[row.original.priority] ?? 'bg-gray-100 text-gray-700 border-gray-200')}>
                        {priorityLabel[row.original.priority] ?? row.original.priority}
                      </span>
                    ),
                  },
                  {
                    id: 'status',
                    header: 'Status',
                    accessorKey: 'status',
                    enableSorting: false,
                    cell: ({ row }) => (
                      <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', statusStyle[row.original.status] ?? 'bg-gray-100 text-gray-700 border-gray-200')}>
                        {statusLabel[row.original.status] ?? row.original.status}
                      </span>
                    ),
                  },
                  { id: 'assignedTo', header: 'Assigned To', accessorKey: 'assignedTo', cell: ({ row }) => <span className="text-sm text-muted-foreground">{row.original.assignedTo ?? '—'}</span> },
                  {
                    id: 'raisedAt',
                    header: 'Raised',
                    accessorKey: 'raisedAt',
                    cell: ({ row }) => {
                      const daysAgo = Math.floor((Date.now() - new Date(row.original.raisedAt).getTime()) / (1000 * 60 * 60 * 24));
                      return <span className="text-xs text-muted-foreground">{daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo}d ago`}</span>;
                    },
                  },
                ];

                return (
                  <DataTableEnhanced
                    columns={requestColDefs}
                    data={paginatedRequests}
                    columnVisibility={Object.fromEntries(requestColDefs.map(c => [c.id!, true]))}
                    columnSorts={requestsColumnSorts}
                    onColumnSortChange={(s) => { setRequestsColumnSorts(s); setRequestsPage(1); }}
                    emptyMessage={<span className="text-muted-foreground">No resource requests yet</span>}
                    paginationContent={
                      <PaginationControls
                        pagination={requestsPagination}
                        onPageChange={setRequestsPage}
                        onPageSizeChange={(s) => { setRequestsPageSize(s); setRequestsPage(1); }}
                      />
                    }
                  />
                );
              })()
            }
          </CardContent>
        </Card>
      )}

      {/* ── Open in Scheduler confirmation dialog ─────────────────────────── */}
      <AlertDialog open={openSchedulerDialogOpen} onOpenChange={setOpenSchedulerDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Open in {schedulerName}</AlertDialogTitle>
            <AlertDialogDescription>
              This will open {schedulerName} and navigate to this project's bookings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Deep link configuration is managed by your administrator in{' '}
              <strong>Settings → Integrations</strong>.
            </span>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                // TODO: replace with real deep link URL from GET /api/settings/integrations/scheduler
                toast.info('Opening scheduler…', {
                  description: `Navigating to ${schedulerName}. (Deep link not yet configured.)`,
                });
              }}
            >
              Open {schedulerName}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}

// ---------------------------------------------------------------------------
// SchedulingOverviewWidget
// Displays a compact scheduling health summary card on the Overview tab.
// Driven by mock data in Phase 1; wire to API in Phase 2.
// ---------------------------------------------------------------------------

type SchedulingHealthStatus = SchedulingSummary["healthStatus"];

function getHealthBadgeStyle(status: SchedulingHealthStatus): {
  dot: string;
  label: string;
  text: string;
} {
  switch (status) {
    case "FullyResourced":
      return { dot: "bg-success-500", label: "Fully Resourced", text: "text-success-700" };
    case "PartiallyResourced":
      return { dot: "bg-warning-500", label: "Partially Resourced", text: "text-warning-700" };
    case "Unresourced":
      return { dot: "bg-error-500", label: "Unresourced", text: "text-error-700" };
    case "NoData":
    default:
      return { dot: "bg-muted-foreground", label: "No Data", text: "text-muted-foreground" };
  }
}

interface SchedulingOverviewWidgetProps {
  summary: SchedulingSummary;
  onViewDetails: () => void;
}

function SchedulingOverviewWidget({ summary, onViewDetails }: SchedulingOverviewWidgetProps) {
  // Derive health status from live thresholds so changes in Settings → Thresholds
  // are immediately reflected in the widget without a page reload.
  const thresholds = getSchedulingThresholds();
  const derivedStatus =
    summary.healthStatus === "NoData"
      ? "NoData"
      : deriveHealthStatus(summary.coveragePercent, thresholds);
  const health = getHealthBadgeStyle(derivedStatus);
  const coveragePct = Math.round(summary.coveragePercent);
  const totalUnmetHours = summary.unmetRoles.reduce((acc, r) => acc + r.gapHours, 0);

  return (
    <div>
      <Separator className="my-2" />
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base font-semibold">Resource Scheduling</CardTitle>
            </div>
            <div className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${health.dot}`} />
              <span className={`text-sm font-medium ${health.text}`}>{health.label}</span>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* KPI row */}
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs mb-0.5">Scheduled Hours</p>
              <p className="font-semibold">
                {summary.totalScheduledHours.toLocaleString()} / {summary.totalBudgetHours.toLocaleString()} hrs
              </p>
              <p className="text-xs text-muted-foreground">
                {summary.confirmedBookings} confirmed · {summary.plannedBookings} planned
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-0.5">Coverage</p>
              <p className="font-semibold">{coveragePct}%</p>
              <p className="text-xs text-muted-foreground">{summary.totalBookings} total bookings</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-0.5">Unmet Demand</p>
              <p className={`font-semibold ${totalUnmetHours > 0 ? "text-warning-700" : "text-success-700"}`}>
                {totalUnmetHours > 0 ? `${totalUnmetHours} hrs` : "None"}
              </p>
              <p className="text-xs text-muted-foreground">
                {summary.unmetRoles.length > 0
                  ? `${summary.unmetRoles.length} role${summary.unmetRoles.length > 1 ? "s" : ""} unmet`
                  : "All roles covered"}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="space-y-1">
            <Progress value={coveragePct} className="h-2" />
            <p className="text-xs text-muted-foreground">{coveragePct}% of budget hours scheduled</p>
          </div>

          {/* Warning banners */}
          {summary.warnings.length > 0 && (
            <div className="space-y-1.5">
              {summary.warnings.map((w, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-2 rounded-md px-3 py-2 text-xs ${
                    w.severity === "critical"
                      ? "bg-error-50 text-error-700 border border-error-200"
                      : w.severity === "warning"
                      ? "bg-warning-50 text-warning-700 border border-warning-200"
                      : "bg-info-50 text-info-700 border border-info-200"
                  }`}
                >
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>{w.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* View Details link */}
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-primary hover:text-primary"
              onClick={onViewDetails}
            >
              View Details
              <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

type BurnPoint = {
  month: string;
  planned: number;
  eac: number | null;
  budget: number;
  isToday: boolean;
};

type HoursBurndownPoint = {
  month: string;
  planned: number | null;   // remaining planned hours (counts down)
  eac: number | null;       // EAC remaining hours projection (dashed)
  isToday: boolean;
};

export function ProjectDetailsPage({ projectId: projectIdProp, onBack }: ProjectDetailsPageProps) {
  const { statusLabels, selectableStatuses } = useProjectStatuses();
  // Shared scheduler connection state — reads from localStorage so toggling
  // in Settings → Integrations is immediately reflected here.
  const { isConnected: schedulerConnected } = useSchedulerConnection();
  // Get projectId from URL params or props (props for backward compatibility)
  const { projectId: projectIdFromUrl } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectId = projectIdProp || projectIdFromUrl || "";
  const numericProjectId = projectId ? parseInt(projectId, 10) : 0;
  const { authenticatedUser } = useAuth();

  const { activePanelClientId, openClientPanel, closeClientPanel } = useClientPanel();

  const [loading, setLoading] = useState(true);
  const [isStatusSaving, setIsStatusSaving] = useState(false);
  const [project, setProject] = useState<Project | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [recentClientIds, setRecentClientIds] = useState<string[]>([]);

  // Client change validation state
  const [clientChangeDialogOpen, setClientChangeDialogOpen] = useState(false);
  const [pendingClientChange, setPendingClientChange] = useState<{ clientId: string; selectedClient: Client } | null>(null);
  const [previousClientForDialog, setPreviousClientForDialog] = useState<Client | null>(null);
  const [clientAuditEvents, setClientAuditEvents] = useState<AuditEvent[]>([]);
  const [projectManager, setProjectManager] = useState<Employee | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [recentProjectManagerIds, setRecentProjectManagerIds] = useState<string[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [resourceAllocations, setResourceAllocations] = useState<ResourceAllocation[]>([]);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [localBudgetData, setLocalBudgetData] = useState<BudgetData | null>(null);
  const [isStructureView, setIsStructureView] = useState(false);
  const [teamColumnSorts, setTeamColumnSorts] = useState<ColumnSort[]>([{ columnId: 'name', direction: 'asc' }]);
  const [teamPage, setTeamPage] = useState(1);
  const [teamPageSize, setTeamPageSize] = useState(25);

  const sortedTeamMembers = useMemo(() => {
    const numericCols = new Set(['allocatedHours', 'actualHours', 'remaining', 'utilisationPercent']);
    return [...teamMembers].sort((a, b) => {
      for (const { columnId, direction } of teamColumnSorts) {
        const aVal = a[columnId as keyof TeamMember];
        const bVal = b[columnId as keyof TeamMember];
        const mult = direction === 'asc' ? 1 : -1;
        if (numericCols.has(columnId)) {
          const diff = (Number(aVal) - Number(bVal)) * mult;
          if (diff !== 0) return diff;
        } else {
          const cmp = String(aVal ?? '').localeCompare(String(bVal ?? '')) * mult;
          if (cmp !== 0) return cmp;
        }
      }
      return 0;
    });
  }, [teamMembers, teamColumnSorts]);

  const teamPagination = useMemo<PaginationMetadata>(
    () => calculatePagination(sortedTeamMembers.length, teamPage, teamPageSize),
    [sortedTeamMembers.length, teamPage, teamPageSize],
  );
  const paginatedTeamMembers = useMemo(
    () => paginateArray(sortedTeamMembers, teamPage, teamPageSize).data,
    [sortedTeamMembers, teamPage, teamPageSize],
  );

  const { handleApiError } = useErrorHandler({ useToast: true });
  const { currentCompany } = useCompany();
  const { templates: invoiceTemplates, getTemplate: getInvoiceTemplate } = useInvoiceTemplates();

  // Budget permissions hook
  const budgetPermissions = useBudgetPermissions();
  const { isBudgetingEnabled } = budgetPermissions;

  // Project Information card — card-level edit mode (replaces global isEditMode)
  const [isProjectInfoEditMode, setIsProjectInfoEditMode] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Project>>({});

  // Inline header editing — name and description live here, not in the card
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [inlineNameValue, setInlineNameValue] = useState("");
  const [isDescEditing, setIsDescEditing] = useState(false);
  const [inlineDescValue, setInlineDescValue] = useState("");

  // PM confirmation dialog
  const [showPmConfirmDialog, setShowPmConfirmDialog] = useState(false);
  const [pendingPmChange, setPendingPmChange] = useState<{ id: string; employee: Employee } | null>(null);

  // Key People card — card-level edit mode
  const [isKeyPeopleEditMode, setIsKeyPeopleEditMode] = useState(false);
  // Local edit state: maps role → employee id string (or '' for cleared)
  const [keyPeopleEditState, setKeyPeopleEditState] = useState<Partial<Record<ProjectRoleType, string>>>({});
  const [hasKeyPeopleUnsavedChanges, setHasKeyPeopleUnsavedChanges] = useState(false);

  // Key People — role configs loaded from settings (display labels, enabled/required)
  const [roleConfigs, setRoleConfigs] = useState<ProjectRoleConfig[]>([]);

  useEffect(() => {
    const loadRoleConfigs = async () => {
      const mode = getDataMode();
      if (mode !== 'live') return;
      try {
        const apiConfigs = await fetchProjectRoleConfigs();
        if (apiConfigs.length > 0) {
          const merged: ProjectRoleConfig[] = PROJECT_ROLE_DISPLAY_ORDER.map((role) => {
            const fromApi = apiConfigs.find((c: { role: string }) => c.role === role);
            if (fromApi) {
              return {
                role: fromApi.role as ProjectRoleType,
                enabled: fromApi.enabled,
                required: fromApi.required,
                displayLabel: fromApi.displayLabel ?? undefined,
              };
            }
            return { role, enabled: true, required: role === 'project-manager', displayLabel: undefined };
          });
          setRoleConfigs(merged);
        }
      } catch (err) {
        console.error('Failed to load role configs:', err);
      }
    };
    loadRoleConfigs();
  }, []);

  /** Resolve the display label for a role — uses custom label from settings if configured, otherwise falls back to the default. */
  const getRoleLabel = useCallback((role: ProjectRoleType): string => {
    const config = roleConfigs.find((c) => c.role === role);
    if (config?.displayLabel) return config.displayLabel;
    return PROJECT_ROLE_LABELS[role];
  }, [roleConfigs]);

  /** Check if a role is enabled in settings. Defaults to true when configs haven't loaded yet. */
  const isRoleEnabled = useCallback((role: ProjectRoleType): boolean => {
    if (roleConfigs.length === 0) return true;
    const config = roleConfigs.find((c) => c.role === role);
    return config?.enabled ?? true;
  }, [roleConfigs]);

  /** Check if a role is required in settings. */
  const isRoleRequired = useCallback((role: ProjectRoleType): boolean => {
    const config = roleConfigs.find((c) => c.role === role);
    return config?.required ?? (role === 'project-manager');
  }, [roleConfigs]);

  // Project Content cards — Objectives, Key Deliverables, Notes (Workstream E)
  // Each card has independent edit mode so editing one never risks discarding another.
  // Read visibility settings from localStorage (set in Settings → Project Settings → Content Fields)
  // TODO [ENGINEERING — Devin]: Replace getProjectContentSettings() with a context or API-backed hook
  //   once GET /api/company/project-content-settings is live, so all users see the same settings.
  const contentFieldSettings = getProjectContentSettings();

  // Project Attributes card — custom field definitions loaded from API
  const [customFieldDefs, setCustomFieldDefs] = useState<import('@/types').ProjectCustomFieldDefinition[]>([]);

  // Project Attributes card — edit mode state
  const [isAttributesEditMode, setIsAttributesEditMode] = useState(false);
  const [attributesEditState, setAttributesEditState] = useState<Record<string, string>>({});
  const [attributesValidationErrors, setAttributesValidationErrors] = useState<Record<string, string>>({});
  const [isObjectivesEditMode, setIsObjectivesEditMode] = useState(false);
  const [objectivesEditValue, setObjectivesEditValue] = useState("");
  const [isDeliverablesEditMode, setIsDeliverablesEditMode] = useState(false);
  const [deliverablesEditValue, setDeliverablesEditValue] = useState("");
  // Note log — replaces free-text notes field (v1.4 decision: timestamped audit log)
  const [noteLog, setNoteLog] = useState<ProjectNote[]>([]);
  const [newNoteValue, setNewNoteValue] = useState("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);

  // Client Information card — card-level edit mode
  const [isClientInfoEditMode, setIsClientInfoEditMode] = useState(false);
  const [clientInfoEditData, setClientInfoEditData] = useState<{ accountManagerId?: number }>({});
  const [hasClientInfoUnsavedChanges, setHasClientInfoUnsavedChanges] = useState(false);

  // Account Manager state
  const [accountManager, setAccountManager] = useState<Employee | null>(null);
  const [recentAccountManagerIds, setRecentAccountManagerIds] = useState<string[]>([]);

  // AM confirmation dialog
  const [showAmConfirmDialog, setShowAmConfirmDialog] = useState(false);
  const [pendingAmChange, setPendingAmChange] = useState<{ id: string; employee: Employee } | null>(null);

  // Active tab state
  const [activeTab, setActiveTab] = useState("overview");
  const [timeTabPrefilterNames, setTimeTabPrefilterNames] = useState<string[]>([]);
  const [timeTabPrefilterBillable, setTimeTabPrefilterBillable] = useState(false);

  // Scheduling data state (Phase 1 — read-only booking data from EBScheduler)
  const [schedulingBookings, setSchedulingBookings] = useState<SchedulingBooking[]>([]);
  const [schedulingLoading, setSchedulingLoading] = useState(false);
  const [schedulingError, setSchedulingError] = useState<string | null>(null);
  const [schedulingConnectionStatus, setSchedulingConnectionStatus] = useState<'connected' | 'not_configured' | 'error'>('not_configured');
  const [schedulingSchedulerName, setSchedulingSchedulerName] = useState<string | null>(null);

  // Resource requests state (fetched from local DB via GET /scheduling/requests)
  const [liveResourceRequests, setLiveResourceRequests] = useState<ResourceRequest[]>([]);

  // WIP Items state (for Unbilled Work tab)
  const [selectedWipItems, setSelectedWipItems] = useState<string[]>([]);
  const [wipSearchQuery, setWipSearchQuery] = useState("");
  const [wipTypeFilter, setWipTypeFilter] = useState("all-types");
  const [wipResourceFilter, setWipResourceFilter] = useState("all-resources");
  const [wipGroupBy, setWipGroupBy] = useState("none");
  const [wipDateFrom, setWipDateFrom] = useState<Date | undefined>(undefined);
  const [wipDateTo, setWipDateTo] = useState<Date | undefined>(undefined);
  const [wipCurrentPage, setWipCurrentPage] = useState(1);
  const [wipItemsPerPage, setWipItemsPerPage] = useState(25);
  const [wipColumnSorts, setWipColumnSorts] = useState<ColumnSort[]>([{ columnId: 'date', direction: 'desc' }]);

  // Billing data state (live mode)
  const [billingWipItems, setBillingWipItems] = useState<BillingWipItem[]>([]);
  const [billingInvoices, setBillingInvoices] = useState<BillingInvoice[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingActionLoading, setBillingActionLoading] = useState(false);
  const [wipTotalCount, setWipTotalCount] = useState(0);
  const [wipSummary, setWipSummary] = useState<WipSummary>({ totalNetAmount: 0, timeNetAmount: 0, expenseNetAmount: 0, timeHours: 0 });

  // Expense type checkboxes state - driven by company integration settings
  const [includeApprovedExpenses, setIncludeApprovedExpenses] = useState(
    currentCompany?.approvedExpensesEnabled ?? true,
  );
  const [includeCreditCardExpenses, setIncludeCreditCardExpenses] = useState(
    currentCompany?.creditCardExpensesEnabled ?? false,
  );
  const [expensesLoading, setExpensesLoading] = useState(false);

  // Cost Centres state
  const [costCentres, setCostCentres] = useState<CostCentre[]>([]);
  const [hierarchicalCostCentres, setHierarchicalCostCentres] = useState<HierarchicalCostCentre[]>([]);
  const [costCentreColumns, setCostCentreColumns] = useState<CostCentreColumnDefinition[]>(() => {
    // Initialize from localStorage as immediate fallback
    try {
      const stored = localStorage.getItem('cost-centre-column-preferences');
      if (stored) {
        return JSON.parse(stored) as CostCentreColumnDefinition[];
      }
    } catch {
      // Ignore parse errors
    }
    return DEFAULT_COST_CENTRE_COLUMNS;
  });
  const [costCentreColumnConfigOpen, setCostCentreColumnConfigOpen] = useState(false);

  // Load cost centre column preferences from API in live mode
  useEffect(() => {
    async function loadCostCentreColumnPrefs() {
      const mode = getDataMode();
      if (mode !== 'live') return;
      try {
        const apiColumns = await fetchColumnPreferences('cost-centre-column-preferences');
        if (apiColumns && apiColumns.length > 0) {
          setCostCentreColumns(apiColumns as unknown as CostCentreColumnDefinition[]);
        }
      } catch (error) {
        console.error('Failed to load cost centre column preferences from API:', error);
      }
    }
    loadCostCentreColumnPrefs();
  }, []);

  // Project Settings state
  const [projectSettings, setProjectSettings] = useState({
    billingFrequency: "monthly",
    invoiceTemplateId: null as string | number | null,
    expenseRecharge: "at-cost",
    expenseMarkupPercent: "",
    purchaseRecharge: "at-cost",
    purchaseMarkupPercent: "",
    notToExceedCapEnabled: false,
    notToExceedCapAmount: "",
    // Auto-generate invoice scheduling
    autoGenerateEnabled: false,
    monthlyPattern: "specific-day",
    monthlyDay: "1",
    weeklyDay: "friday",
    // Finance system integration — required for Fixed Fee invoice posting
    defaultCostCentreCode: null as string | null,
    // Billing trigger type — fixed-price projects only
    billingTriggerType: '' as string,
  });

  // Load persisted billing config from the API on mount.
  // Restores ALL billing settings so the drawer shows the correct saved values.
  useEffect(() => {
    if (!numericProjectId) return;
    getProjectBillingConfig(numericProjectId).then((config) => {
      const storedId = config.invoiceTemplateId;

      // Resolve the template id: use the saved value, or fall back to the default template
      let resolvedTemplateId: string | number | null = storedId ?? null;
      if (resolvedTemplateId === null) {
        const defaultTpl = invoiceTemplates.find((t) => t.isDefault) ?? invoiceTemplates[0];
        if (defaultTpl) {
          resolvedTemplateId = defaultTpl.id;
          // Persist the default template so future loads find it
          updateProjectBillingConfig(numericProjectId, { invoiceTemplateId: defaultTpl.id });
        }
      }

      // Always restore ALL fields in a single state update
      setProjectSettings((prev) => ({
        ...prev,
        invoiceTemplateId: resolvedTemplateId ?? prev.invoiceTemplateId,
        billingFrequency: config.billingFrequency || prev.billingFrequency,
        autoGenerateEnabled: config.autoGenerate ?? prev.autoGenerateEnabled,
        defaultCostCentreCode: config.defaultCostCentreCode ?? prev.defaultCostCentreCode,
        billingTriggerType: config.billingTriggerType || prev.billingTriggerType,
        expenseRecharge:
          config.expenseRechargeRule === 'none' ? 'not-rechargeable'
          : config.expenseRechargeRule === 'marked-up' ? 'markup'
          : config.expenseRechargeRule || prev.expenseRecharge,
        expenseMarkupPercent:
          config.expenseMarkupPercent != null ? String(config.expenseMarkupPercent) : prev.expenseMarkupPercent,
        purchaseRecharge:
          config.rechargesPurchases === false ? 'not-rechargeable' : prev.purchaseRecharge,
        notToExceedCapEnabled: config.nteCapAmount != null && config.nteCapAmount > 0,
        notToExceedCapAmount:
          config.nteCapAmount != null ? String(config.nteCapAmount) : prev.notToExceedCapAmount,
      }));
    }).catch((error) => {
      console.error('Failed to load billing config:', error);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericProjectId]);

  const [settingsSaving, setSettingsSaving] = useState(false);
  const [configureStructureOpen, setConfigureStructureOpen] = useState(false);
  const [defaultCostCentreError, setDefaultCostCentreError] = useState(false);
  const [selectedCostCentre, setSelectedCostCentre] = useState<CostCentre | null>(null);
  const [costCentreDetailsPanelOpen, setCostCentreDetailsPanelOpen] = useState(false);
  const [costCentreFullModalOpen, setCostCentreFullModalOpen] = useState(false);
  const [createCostCentrePanelOpen, setCreateCostCentrePanelOpen] = useState(false);

  // Budget state
  const {
    data: budgetData,
    loading: budgetLoading,
    error: budgetError,
    refetch: refetchBudget,
  } = useBudgetData(numericProjectId);
  const { updateBudget, loading: updateBudgetLoading } = useUpdateBudget();
  const { settings: resourceSettings } = useResourceBudgetSettings();
  const [salesBudgetEditModalOpen, setSalesBudgetEditModalOpen] = useState(false);
  const [editSalesBudgetPageOpen, setEditSalesBudgetPageOpen] = useState(false);
  const [costBudgetEditModalOpen, setCostBudgetEditModalOpen] = useState(false);
  const [editCostBudgetPageOpen, setEditCostBudgetPageOpen] = useState(false);
  const [billingSettingsDrawerOpen, setBillingSettingsDrawerOpen] = useState(false);
  const [drawerEditMode, setDrawerEditMode] = useState(false);
  const [drawerSettings, setDrawerSettings] = useState(projectSettings);
  const [drawerSaving, setDrawerSaving] = useState(false);

  // Keep drawerSettings in sync with projectSettings when the drawer is open
  // but NOT being edited. This ensures that if the billing config API responds
  // after the drawer was opened, the drawer reflects the loaded values.
  useEffect(() => {
    if (billingSettingsDrawerOpen && !drawerEditMode) {
      setDrawerSettings(projectSettings);
    }
  }, [projectSettings, billingSettingsDrawerOpen, drawerEditMode]);
  const [resourceBudgetEditModalOpen, setResourceBudgetEditModalOpen] = useState(false);
  const [editResourceBudgetPageOpen, setEditResourceBudgetPageOpen] = useState(false);
  const [budgetHistoryModalOpen, setBudgetHistoryModalOpen] = useState(false);
  const [budgetExportModalOpen, setBudgetExportModalOpen] = useState(false);
  const [activeBudgetType, setActiveBudgetType] = useState<"sales" | "cost" | "resource">("sales");
  // Resource Request Modal (Resource Scheduling integration)
  const [raiseRequestModalOpen, setRaiseRequestModalOpen] = useState(false);
  const [raiseRequestPrefilledRole, setRaiseRequestPrefilledRole] = useState<string | undefined>(undefined);
  // Tracks roles that have had a request raised this session (disables their Raise Request button)
  const [pendingRequestRoles, setPendingRequestRoles] = useState<Set<string>>(new Set());

  const handleRequestSubmitSuccess = (roleName: string) => {
    setPendingRequestRoles((prev) => new Set(prev).add(roleName));
  };
  const [invoiceWizardOpen, setInvoiceWizardOpen] = useState(false);
  // Fixed Fee billing — tracks which milestone opened the wizard (Phase 3/4)
  const [fixedFeeMilestone, setFixedFeeMilestone] = useState<{ id: number; name: string; amount: number; dueDate: string } | null>(null);

  // Completion % override editing (Phase 13.2)
  const [completionPercentEditing, setCompletionPercentEditing] = useState(false);
  const [completionPercentInputValue, setCompletionPercentInputValue] = useState<string>('');
  const [evmExpanded, setEvmExpanded] = useState(false);
  const [financialAnalysisExpanded, setFinancialAnalysisExpanded] = useState(false);
  // Audit trail collapsible (Phase 13.4)
  const [completionAuditOpen, setCompletionAuditOpen] = useState(false);
  // Audit tab — pre-set category filter when navigating from the KPI card link
  const [auditCategoryFilter, setAuditCategoryFilter] = useState<string>("all");
  // Completion % audit data sourced from mock data keyed by project ID
  const completionAuditEntries: CompletionPercentAuditEntry[] = project ? (mockCompletionAuditData[project.id] ?? []) : [];

  // Invoice details sheet state
  const [invoiceDetailsSheetOpen, setInvoiceDetailsSheetOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);

  // Create draft dialog state
  const [createDraftDialogOpen, setCreateDraftDialogOpen] = useState(false);
  
  // Invoice column configuration
  const [invoiceColumnConfigOpen, setInvoiceColumnConfigOpen] = useState(false);
  
  // Invoice pagination state
  const [invoiceCurrentPage, setInvoiceCurrentPage] = useState(1);
  const [invoiceItemsPerPage, setInvoiceItemsPerPage] = useState(25);

  // Invoice multi-column sort state
  const [invoiceColumnSorts, setInvoiceColumnSorts] = useState<ColumnSort[]>([]);
  
  // Invoice filtering and search state
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<string>("all");
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState("");

  // Invoice columns - persisted per user via useColumnPreferences
  const { columns: invoiceColumns, updateColumns: updateInvoiceColumns } = useColumnPreferences({
    storageKey: 'billing-invoice-column-preferences',
    defaultColumns: DEFAULT_INVOICE_COLUMNS,
  });

  // Get visible invoice columns
  const visibleInvoiceColumns = useMemo(
    () => invoiceColumns.filter(col => col.visible).sort((a, b) => a.order - b.order),
    [invoiceColumns]
  );

  // WIP column configuration state
  const [wipColumnConfigOpen, setWipColumnConfigOpen] = useState(false);

  // WIP columns - persisted per user via useColumnPreferences
  const { columns: wipColumns, updateColumns: updateWipColumns } = useColumnPreferences({
    storageKey: 'billing-wip-column-preferences',
    defaultColumns: DEFAULT_WIP_COLUMNS,
  });

  const visibleWipColumns = useMemo(
    () => wipColumns.filter(col => col.visible).sort((a, b) => a.order - b.order),
    [wipColumns]
  );

  // Check if resource budgeting is enabled
  const isResourceBudgetEnabled =
    resourceSettings.budgetType !== "none" && budgetPermissions.settings.permissions.resourceBudgetEnabled;

  // Enrich budget data with allocated hours from team members
  const enrichedBudgetData = useMemo(() => {
    // Use local budget data if available (after assignments), otherwise use fetched data
    const dataToEnrich = localBudgetData || budgetData;
    if (!dataToEnrich || !dataToEnrich.resourceBudget) return dataToEnrich;

    return {
      ...dataToEnrich,
      resourceBudget: enrichBudgetWithAllocatedHours(dataToEnrich.resourceBudget, teamMembers),
    };
  }, [budgetData, localBudgetData, teamMembers]);

  // Debug logging
  console.log("[ProjectDetails] Budget permissions:", {
    isBudgetingEnabled,
    salesEnabled: budgetPermissions.settings.permissions.salesBudgetEnabled,
    costEnabled: budgetPermissions.settings.permissions.costBudgetEnabled,
    resourceEnabled: isResourceBudgetEnabled,
  });

  // Dynamic height for tabs container
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabsContainerHeight = useDynamicTableHeight(tabsContainerRef, 24); // 24px bottom padding

  // WIP Items data - uses API data in live mode, mock data in demo mode
  const dataMode = getDataMode();

  const wipItems = useMemo(() => {
    if (dataMode === "live") {
      // Live mode: transform API data to match existing table shape (may be empty)
      return billingWipItems.map((item) => ({
        id: `wip-${item.id}`,
        date: item.date ? item.date.split("T")[0] : "",
        resource: item.employeeName || (item.sourceType === 1 || item.sourceType === 2 ? "Unknown" : "-"),
        task: item.taskName || "",
        description: item.description || "",
        type: mapWipSourceType(item.sourceType),
        quantity: item.quantity,
        unit: item.unit || (item.sourceType === 1 ? "hrs" : "units"),
        rate: item.rate,
        amount: item.netAmount,
        costCentre: item.externalCostCentreCode || "",
        reference: `WIP-${item.id}`,
        costRate: item.costRate || 0,
        billingId: item.id, // preserve original ID for API calls
      }));
    }

    // Demo mode: use mock data
    return [
      {
        id: "wip-1",
        date: "2024-01-15",
        resource: "John Smith",
        task: "Backend Development",
        description: "API endpoint implementation",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 150,
        amount: 1200,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-2",
        date: "2024-01-16",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Dashboard mockups and prototypes",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 125,
        amount: 812.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-3",
        date: "2024-01-17",
        resource: "Mike Wilson",
        task: "Testing",
        description: "QA and bug fixes",
        type: "Time",
        quantity: 5.0,
        unit: "hrs",
        rate: 100,
        amount: 500,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-4",
        date: "2024-01-18",
        resource: "John Smith",
        task: "Travel",
        description: "Client meeting - London",
        type: "Expense",
        quantity: 1,
        unit: "trip",
        rate: 245.5,
        amount: 245.5,
        costCentre: "Travel",
        reference: "EXP-123",
        costRate: 245.5,
      },
      {
        id: "wip-5",
        date: "2024-01-19",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Component library setup",
        type: "Time",
        quantity: 7.5,
        unit: "hrs",
        rate: 125,
        amount: 937.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-6",
        date: "2024-01-20",
        resource: "Mike Wilson",
        task: "Software",
        description: "Development tools license",
        type: "Expense",
        quantity: 1,
        unit: "item",
        rate: 299,
        amount: 299,
        costCentre: "IT",
        reference: "EXP-124",
        costRate: 299,
      },
      {
        id: "wip-7",
        date: "2024-01-21",
        resource: "Procurement",
        task: "Hardware",
        description: "Dell Laptop - Developer workstation",
        type: "Purchase",
        quantity: 2,
        unit: "units",
        rate: 1500,
        amount: 3000,
        costCentre: "IT",
        reference: "PO-001",
        costRate: 1200,
      },
      {
        id: "wip-8",
        date: "2024-01-22",
        resource: "Procurement",
        task: "Office Supplies",
        description: "Ergonomic chairs for team",
        type: "Purchase",
        quantity: 5,
        unit: "units",
        rate: 450,
        amount: 2250,
        costCentre: "Office",
        reference: "PO-002",
        costRate: 350,
      },
      {
        id: "wip-9",
        date: "2024-01-23",
        resource: "John Smith",
        task: "Backend Development",
        description: "Database optimization",
        type: "Time",
        quantity: 6.0,
        unit: "hrs",
        rate: 150,
        amount: 900,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-10",
        date: "2024-01-24",
        resource: "Procurement",
        task: "Software Licenses",
        description: "Adobe Creative Cloud - Annual subscription",
        type: "Purchase",
        quantity: 3,
        unit: "licenses",
        rate: 599,
        amount: 1797,
        costCentre: "Design",
        reference: "PO-003",
        costRate: 599,
      },
      {
        id: "wip-11",
        date: "2024-01-25",
        resource: "John Smith",
        task: "Backend Development",
        description: "Authentication module implementation",
        type: "Time",
        quantity: 7.5,
        unit: "hrs",
        rate: 150,
        amount: 1125,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-12",
        date: "2024-01-26",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "User flow diagrams",
        type: "Time",
        quantity: 5.0,
        unit: "hrs",
        rate: 125,
        amount: 625,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-13",
        date: "2024-01-27",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Integration testing",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 100,
        amount: 800,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-14",
        date: "2024-01-28",
        resource: "John Smith",
        task: "Backend Development",
        description: "Payment gateway integration",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 150,
        amount: 975,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-15",
        date: "2024-01-29",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Responsive design implementation",
        type: "Time",
        quantity: 7.0,
        unit: "hrs",
        rate: 125,
        amount: 875,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-16",
        date: "2024-01-30",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Performance testing",
        type: "Time",
        quantity: 6.0,
        unit: "hrs",
        rate: 100,
        amount: 600,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-17",
        date: "2024-01-31",
        resource: "John Smith",
        task: "Backend Development",
        description: "Email notification system",
        type: "Time",
        quantity: 5.5,
        unit: "hrs",
        rate: 150,
        amount: 825,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-18",
        date: "2024-02-01",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Icon design and asset creation",
        type: "Time",
        quantity: 4.5,
        unit: "hrs",
        rate: 125,
        amount: 562.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-19",
        date: "2024-02-02",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Security audit and penetration testing",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 100,
        amount: 800,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-20",
        date: "2024-02-03",
        resource: "John Smith",
        task: "Backend Development",
        description: "Data migration scripts",
        type: "Time",
        quantity: 7.0,
        unit: "hrs",
        rate: 150,
        amount: 1050,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-21",
        date: "2024-02-04",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Animation and transitions",
        type: "Time",
        quantity: 6.0,
        unit: "hrs",
        rate: 125,
        amount: 750,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-22",
        date: "2024-02-05",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Cross-browser compatibility testing",
        type: "Time",
        quantity: 5.5,
        unit: "hrs",
        rate: 100,
        amount: 550,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-23",
        date: "2024-02-06",
        resource: "John Smith",
        task: "Backend Development",
        description: "Caching layer implementation",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 150,
        amount: 975,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-24",
        date: "2024-02-07",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Accessibility improvements",
        type: "Time",
        quantity: 5.0,
        unit: "hrs",
        rate: 125,
        amount: 625,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-25",
        date: "2024-02-08",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Load testing and optimization",
        type: "Time",
        quantity: 7.5,
        unit: "hrs",
        rate: 100,
        amount: 750,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-26",
        date: "2024-02-09",
        resource: "John Smith",
        task: "Travel",
        description: "Client presentation - Manchester",
        type: "Expense",
        quantity: 1,
        unit: "trip",
        rate: 185.75,
        amount: 185.75,
        costCentre: "Travel",
        reference: "EXP-125",
        costRate: 185.75,
      },
      {
        id: "wip-27",
        date: "2024-02-10",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Style guide documentation",
        type: "Time",
        quantity: 4.0,
        unit: "hrs",
        rate: 125,
        amount: 500,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-28",
        date: "2024-02-11",
        resource: "Mike Wilson",
        task: "Testing",
        description: "User acceptance testing coordination",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 100,
        amount: 650,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-29",
        date: "2024-02-12",
        resource: "John Smith",
        task: "Backend Development",
        description: "Logging and monitoring setup",
        type: "Time",
        quantity: 5.5,
        unit: "hrs",
        rate: 150,
        amount: 825,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-30",
        date: "2024-02-13",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Error state designs",
        type: "Time",
        quantity: 3.5,
        unit: "hrs",
        rate: 125,
        amount: 437.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-31",
        date: "2024-02-14",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Regression testing",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 100,
        amount: 800,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-32",
        date: "2024-02-15",
        resource: "John Smith",
        task: "Backend Development",
        description: "API documentation",
        type: "Time",
        quantity: 4.5,
        unit: "hrs",
        rate: 150,
        amount: 675,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-33",
        date: "2024-02-16",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Loading state animations",
        type: "Time",
        quantity: 5.5,
        unit: "hrs",
        rate: 125,
        amount: 687.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-34",
        date: "2024-02-17",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Mobile device testing",
        type: "Time",
        quantity: 7.0,
        unit: "hrs",
        rate: 100,
        amount: 700,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-35",
        date: "2024-02-18",
        resource: "John Smith",
        task: "Backend Development",
        description: "Deployment pipeline setup",
        type: "Time",
        quantity: 6.0,
        unit: "hrs",
        rate: 150,
        amount: 900,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-36",
        date: "2024-02-19",
        resource: "Procurement",
        task: "Cloud Services",
        description: "AWS hosting - Monthly subscription",
        type: "Purchase",
        quantity: 1,
        unit: "month",
        rate: 450,
        amount: 450,
        costCentre: "IT",
        reference: "PO-004",
        costRate: 450,
      },
      {
        id: "wip-37",
        date: "2024-02-20",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Onboarding flow design",
        type: "Time",
        quantity: 6.5,
        unit: "hrs",
        rate: 125,
        amount: 812.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
      {
        id: "wip-38",
        date: "2024-02-21",
        resource: "Mike Wilson",
        task: "Testing",
        description: "Final QA review",
        type: "Time",
        quantity: 8.0,
        unit: "hrs",
        rate: 100,
        amount: 800,
        costCentre: "QA",
        reference: "PROJ-001",
        costRate: 80,
      },
      {
        id: "wip-39",
        date: "2024-02-22",
        resource: "John Smith",
        task: "Backend Development",
        description: "Production deployment",
        type: "Time",
        quantity: 5.0,
        unit: "hrs",
        rate: 150,
        amount: 750,
        costCentre: "Engineering",
        reference: "PROJ-001",
        costRate: 120,
      },
      {
        id: "wip-40",
        date: "2024-02-23",
        resource: "Sarah Johnson",
        task: "UI Design",
        description: "Post-launch UI refinements",
        type: "Time",
        quantity: 4.5,
        unit: "hrs",
        rate: 125,
        amount: 562.5,
        costCentre: "Design",
        reference: "PROJ-001",
        costRate: 100,
      },
    ];
  }, [dataMode, billingWipItems]);

  // Filter and sort WIP items based on search, filters, and date range
  const filteredAndSortedWipItems = useMemo(() => {
    let filtered = wipItems.filter((item) => {
      const matchesSearch =
        wipSearchQuery === "" ||
        item.description.toLowerCase().includes(wipSearchQuery.toLowerCase()) ||
        item.task.toLowerCase().includes(wipSearchQuery.toLowerCase()) ||
        item.resource.toLowerCase().includes(wipSearchQuery.toLowerCase());

      const matchesType = wipTypeFilter === "all-types" || item.type.toLowerCase() === wipTypeFilter.toLowerCase();

      const matchesResource =
        wipResourceFilter === "all-resources" ||
        item.resource.toLowerCase().includes(wipResourceFilter.toLowerCase());

      // Date range filter
      const itemDate = new Date(item.date);
      const matchesDateFrom = !wipDateFrom || itemDate >= wipDateFrom;
      const matchesDateTo = !wipDateTo || itemDate <= wipDateTo;

      return matchesSearch && matchesType && matchesResource && matchesDateFrom && matchesDateTo;
    });

    // Sort items using wipColumnSorts (multi-column)
    filtered.sort((a, b) => {
      for (const sort of wipColumnSorts) {
        let aValue: any = a[sort.columnId as keyof typeof a];
        let bValue: any = b[sort.columnId as keyof typeof b];

        // Handle date sorting
        if (sort.columnId === "date") {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }

        if (typeof aValue === "number" && typeof bValue === "number") {
          const cmp = aValue - bValue;
          if (cmp !== 0) return sort.direction === "asc" ? cmp : -cmp;
        } else {
          const aStr = String(aValue ?? "").toLowerCase();
          const bStr = String(bValue ?? "").toLowerCase();
          const cmp = aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
          if (cmp !== 0) return sort.direction === "asc" ? cmp : -cmp;
        }
      }
      return 0;
    });

    return filtered;
  }, [wipItems, wipSearchQuery, wipTypeFilter, wipResourceFilter, wipDateFrom, wipDateTo, wipColumnSorts]);

  // Group WIP items if grouping is enabled
  const groupedWipItems = useMemo(() => {
    if (wipGroupBy === "none") {
      return null;
    }

    const groups: Record<string, typeof filteredAndSortedWipItems> = {};

    filteredAndSortedWipItems.forEach((item) => {
      let groupKey = "";
      if (wipGroupBy === "resource") {
        groupKey = item.resource;
      } else if (wipGroupBy === "task") {
        groupKey = item.task;
      } else if (wipGroupBy === "date") {
        groupKey = new Date(item.date).toLocaleDateString("en-GB", { 
          year: "numeric", 
          month: "long", 
          day: "numeric" 
        });
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });

    return groups;
  }, [filteredAndSortedWipItems, wipGroupBy]);

  // Paginate WIP items - in live mode, server already returns one page; in demo mode, slice client-side
  const paginatedWipItems = useMemo(() => {
    if (dataMode === "live") {
      // Server already returns only the current page of items
      return filteredAndSortedWipItems;
    }
    const startIndex = (wipCurrentPage - 1) * wipItemsPerPage;
    const endIndex = startIndex + wipItemsPerPage;
    return filteredAndSortedWipItems.slice(startIndex, endIndex);
  }, [filteredAndSortedWipItems, wipCurrentPage, wipItemsPerPage, dataMode]);

  // Calculate pagination info - use server-side total count in live mode
  const wipTotalItems = dataMode === "live" ? wipTotalCount : filteredAndSortedWipItems.length;
  const wipTotalPages = Math.ceil(wipTotalItems / wipItemsPerPage);
  const wipStartItem = wipTotalItems === 0 ? 0 : (wipCurrentPage - 1) * wipItemsPerPage + 1;
  const wipEndItem = Math.min(wipCurrentPage * wipItemsPerPage, wipTotalItems);

  // Calculate selected totals
  const selectedWipTotals = useMemo(() => {
    const selectedItems = filteredAndSortedWipItems.filter((item) => selectedWipItems.includes(item.id));

    const hours = selectedItems
      .filter((item) => item.type === "Time")
      .reduce((sum, item) => sum + item.quantity, 0);

    const expenseCount = selectedItems.filter((item) => item.type === "Expense").length;

    const amount = selectedItems.reduce((sum, item) => sum + item.amount, 0);

    return {
      count: selectedItems.length,
      hours,
      expenseCount,
      amount,
    };
  }, [filteredAndSortedWipItems, selectedWipItems]);

  // Handle select/deselect individual item
  const handleSelectWipItem = (itemId: string) => {
    setSelectedWipItems((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]));
  };

  // Handle select/deselect all filtered items
  const handleSelectAllWipItems = () => {
    // Get all filtered WIP item IDs (across all pages)
    const allWipIds = filteredAndSortedWipItems.map((item) => item.id);
    
    if (selectedWipItems.length === allWipIds.length && allWipIds.length > 0) {
      // If all items are selected, deselect all
      setSelectedWipItems([]);
    } else {
      // Select all items across all pages
      setSelectedWipItems(allWipIds);
    }
  };

  // Handle column sort
  // Handle page change - fetch new page from server
  const handleWipPageChange = async (newPage: number) => {
    setWipCurrentPage(newPage);
    if (dataMode === "live") {
      try {
        setBillingLoading(true);
        const response = await fetchBillingWipItemsPaged(numericProjectId, 0, newPage, wipItemsPerPage);
        setBillingWipItems(response.items);
        setWipTotalCount(response.totalCount);
        if (response.summary) setWipSummary(response.summary);
      } catch (error) {
        console.error("Failed to fetch WIP page:", error);
      } finally {
        setBillingLoading(false);
      }
    }
  };

  const handleInvoicePageChange = (newPage: number) => {
    setInvoiceCurrentPage(newPage);
  };

  // Helper functions to format display values to match dropdown labels
  const formatProjectType = (type: string): string => {
    const typeMap: Record<string, string> = {
      "fixed-price": "Fixed Price",
      "time-and-materials": "Time & Materials",
      retainer: "Retainer",
    };
    return typeMap[type] || type;
  };

  const formatRevenueRecognition = (method: string): string => {
    const methodMap: Record<string, string> = {
      "percentage-of-completion": "Percentage of Completion",
      "completed-contract": "Completed Contract",
      "cash-basis": "Cash Basis",
    };
    return methodMap[method] || method;
  };

  // Edit mode handlers
  const handleEnterEditMode = useCallback(() => {
    if (project) {
      setEditFormData({
        statusId: project.statusId,
        type: project.type,
        revenueRecognitionMethod: project.revenueRecognitionMethod,
        startDate: project.startDate,
        endDate: project.endDate,
        projectManagerId: project.projectManagerId,
      });
      setIsProjectInfoEditMode(true);
      setHasUnsavedChanges(false);
    }
  }, [project]);

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      setIsProjectInfoEditMode(false);
      setEditFormData({});
    }
  };

  const handleConfirmCancel = () => {
    setIsProjectInfoEditMode(false);
    setEditFormData({});
    setHasUnsavedChanges(false);
    setShowCancelDialog(false);
    toast.info("Changes discarded");
  };

  // Inline header — Project Name
  const handleNameEditStart = useCallback(() => {
    if (!project) return;
    setInlineNameValue(project.name);
    setIsNameEditing(true);
  }, [project]);

  const handleNameSave = useCallback(async () => {
    if (!project) return;
    const trimmed = inlineNameValue.trim();
    if (trimmed === project.name) {
      setIsNameEditing(false);
      return;
    }
    if (trimmed.length < 3 || trimmed.length > 100) {
      // Keep editing — validation prevents save
      return;
    }
    try {
      const dataMode = getDataMode();
      if (dataMode === 'live') {
        await updateProject(numericProjectId, {
          id: numericProjectId,
          name: trimmed,
          description: project.description,
          statusId: project.statusId,
          type: project.type,
          revenueRecognitionMethod: project.revenueRecognitionMethod,
          startDate: project.startDate,
          endDate: project.endDate,
          clientIds: project.clientIds,
          projectManagerId: project.projectManagerId,
          health: project.health,
        });
      }
      const updatedProject = { ...project, name: trimmed };
      setProject(updatedProject);
      setIsNameEditing(false);
      toast.success("Project name updated");
      window.dispatchEvent(new CustomEvent("project-updated", { detail: { projectId, project: updatedProject } }));
    } catch {
      toast.error("Failed to update project name");
    }
  }, [project, inlineNameValue, numericProjectId, projectId]);

  const handleNameKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void handleNameSave(); }
    if (e.key === 'Escape') { setIsNameEditing(false); }
  }, [handleNameSave]);

  // Inline header — Description
  const handleDescEditStart = useCallback(() => {
    if (!project) return;
    setInlineDescValue(project.description || "");
    setIsDescEditing(true);
  }, [project]);

  const handleDescSave = useCallback(async () => {
    if (!project) return;
    const trimmed = inlineDescValue.trim();
    if (trimmed === (project.description || "").trim()) {
      setIsDescEditing(false);
      return;
    }
    try {
      const dataMode = getDataMode();
      if (dataMode === 'live') {
        await updateProject(numericProjectId, {
          id: numericProjectId,
          name: project.name,
          description: trimmed,
          statusId: project.statusId,
          type: project.type,
          revenueRecognitionMethod: project.revenueRecognitionMethod,
          startDate: project.startDate,
          endDate: project.endDate,
          clientIds: project.clientIds,
          projectManagerId: project.projectManagerId,
          health: project.health,
        });
      }
      const updatedProject = { ...project, description: trimmed };
      setProject(updatedProject);
      setIsDescEditing(false);
      toast.success("Description updated");
      window.dispatchEvent(new CustomEvent("project-updated", { detail: { projectId, project: updatedProject } }));
    } catch {
      toast.error("Failed to update description");
    }
  }, [project, inlineDescValue, numericProjectId, projectId]);

  const handleDescKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') { setIsDescEditing(false); }
    // Ctrl+Enter or Cmd+Enter saves
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); void handleDescSave(); }
  }, [handleDescSave]);

  const handleSaveChanges = async () => {
    try {
      if (!project) return;

      // Validate settings if on Time & Material project
      if (project.type === "time-and-materials" && !validateSettings()) {
        return;
      }

      const dataMode = getDataMode();

      if (dataMode === "live") {
        const updateData = {
          id: numericProjectId,
          name: project.name,
          description: project.description,
          statusId: editFormData.statusId ?? project.statusId,
          type: editFormData.type ?? project.type,
          revenueRecognitionMethod: editFormData.revenueRecognitionMethod ?? project.revenueRecognitionMethod,
          startDate: editFormData.startDate ?? project.startDate,
          endDate: editFormData.endDate ?? project.endDate,
          clientIds: editFormData.clientIds ?? project.clientIds,
          projectManagerId: editFormData.projectManagerId ?? project.projectManagerId,
          health: project.health,
        };

        await updateProject(numericProjectId, updateData);

        // Save project settings for Time & Material projects
        if (project.type === "time-and-materials") {
          // TODO: Call API to save settings when backend is ready
          // await saveProjectSettings(numericProjectId, projectSettings);
        }
      }

      const updatedProject = { ...project, ...editFormData };
      setProject(updatedProject);
      setIsProjectInfoEditMode(false);
      setEditFormData({});
      setHasUnsavedChanges(false);
      toast.success("Project updated successfully");

      window.dispatchEvent(
        new CustomEvent("project-updated", {
          detail: { projectId, project: updatedProject },
        }),
      );
    } catch (error) {
      toast.error("Failed to update project");
      console.error("Error saving project:", error);
    }
  };

  // Inline status change — saves immediately, no edit mode required
  const handleInlineStatusChange = async (newStatusId: string) => {
    if (!project) return;
    const newId = Number(newStatusId) as ProjectStatusId;
    if (newId === project.statusId) return;

    const previousStatusId = project.statusId;
    const newStatusLabel = statusLabels[newId] || PROJECT_STATUS_LABELS[newId] || String(newId);

    // Optimistic update
    setProject(prev => prev ? { ...prev, statusId: newId } : prev);

    try {
      setIsStatusSaving(true);
      const dataMode = getDataMode();
      if (dataMode === 'live') {
        await updateProject(numericProjectId, {
          id: numericProjectId,
          name: project.name,
          description: project.description,
          statusId: newId,
          type: project.type,
          revenueRecognitionMethod: project.revenueRecognitionMethod,
          startDate: project.startDate,
          endDate: project.endDate,
          clientIds: project.clientIds,
          projectManagerId: project.projectManagerId,
          health: project.health,
        });
      } else {
        // Demo/tour mode — simulate save delay
        await new Promise(resolve => setTimeout(resolve, 400));
      }

      toast.success(`Status updated to "${newStatusLabel}"`);
      window.dispatchEvent(
        new CustomEvent('project-updated', {
          detail: { projectId, project: { ...project, statusId: newId } },
        }),
      );
    } catch (error) {
      // Rollback on failure
      setProject(prev => prev ? { ...prev, statusId: previousStatusId } : prev);
      toast.error('Failed to update status');
      console.error('Error updating project status:', error);
    } finally {
      setIsStatusSaving(false);
    }
  };

  // Budget handlers
  const handleSaveBudget = async (
    updatedBudget: Partial<BudgetData>,
    revisionReason: string,
    saveAsBaseline: boolean,
  ) => {
    console.log("Saving budget:", { updatedBudget, revisionReason, saveAsBaseline });

    try {
      const result = await updateBudget(numericProjectId, updatedBudget);

      if (result.success) {
        console.log("[ProjectDetails] Budget saved successfully, refetching data...");
        toast.success("Budget saved successfully!");
        refetchBudget();
      } else {
        console.error("[ProjectDetails] Failed to save budget:", result.error);
        toast.error(result.error || "Failed to save budget");
      }
    } catch (error) {
      console.error("[ProjectDetails] Error saving budget:", error);
      toast.error("Failed to save budget");
    }

    // Close the appropriate modal
    setSalesBudgetEditModalOpen(false);
    setCostBudgetEditModalOpen(false);
    setResourceBudgetEditModalOpen(false);
  };

  const handleRestoreRevision = async (revisionId: string) => {
    console.log("Restoring revision:", revisionId);
    // TODO: Implement actual restore logic when backend is ready
    toast.success("Revision restored successfully!");
    setBudgetHistoryModalOpen(false);
  };

  const handleFieldChange = (field: keyof Project, value: any) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleProjectManagerChange = (employeeId: string, selectedEmployee: Employee | undefined) => {
    if (!selectedEmployee) return;
    // If PM is actually changing, require confirmation only if there is an existing PM
    if (selectedEmployee.id !== project?.projectManagerId) {
      if (!projectManager) {
        // No existing PM — assign directly without confirmation
        setProjectManager(selectedEmployee);
        setEditFormData((prev) => ({ ...prev, projectManagerId: selectedEmployee.id }));
        const updatedRecentPMs = [employeeId, ...recentProjectManagerIds.filter((id) => id !== employeeId)].slice(0, 10);
        setRecentProjectManagerIds(updatedRecentPMs);
        localStorage.setItem("recentProjectManagerIds", JSON.stringify(updatedRecentPMs));
        setHasUnsavedChanges(true);
      } else {
        // Existing PM — show reassignment confirmation
        setPendingPmChange({ id: employeeId, employee: selectedEmployee });
        setShowPmConfirmDialog(true);
      }
    }
  };

  const handleConfirmPmChange = () => {
    if (!pendingPmChange) return;
    const { id: employeeId, employee: selectedEmployee } = pendingPmChange;
    setProjectManager(selectedEmployee);
    setEditFormData((prev) => ({ ...prev, projectManagerId: selectedEmployee.id }));
    const updatedRecentPMs = [employeeId, ...recentProjectManagerIds.filter((id) => id !== employeeId)].slice(0, 10);
    setRecentProjectManagerIds(updatedRecentPMs);
    localStorage.setItem("recentProjectManagerIds", JSON.stringify(updatedRecentPMs));
    setHasUnsavedChanges(true);
    setShowPmConfirmDialog(false);
    setPendingPmChange(null);
  };

  const handleCancelPmChange = () => {
    setShowPmConfirmDialog(false);
    setPendingPmChange(null);
  };

  // ── Key People card — edit mode ──

  const handleEnterKeyPeopleEditMode = () => {
    if (!project) return;
    // Seed edit state from existing roleAssignments
    const seed: Partial<Record<ProjectRoleType, string>> = {};
    (project.roleAssignments ?? []).forEach((a) => {
      seed[a.role] = String(a.employeeId);
    });
    // C6: If no roleAssignments, seed PM from projectManagerId fallback
    if (!project.roleAssignments && project.projectManagerId) {
      seed['project-manager'] = String(project.projectManagerId);
    }
    setKeyPeopleEditState(seed);
    setHasKeyPeopleUnsavedChanges(false);
    setIsKeyPeopleEditMode(true);
  };

  const handleCancelKeyPeopleEdit = () => {
    setIsKeyPeopleEditMode(false);
    setKeyPeopleEditState({});
    setHasKeyPeopleUnsavedChanges(false);
  };

  const handleKeyPeopleRoleChange = (role: ProjectRoleType, value: string) => {
    setKeyPeopleEditState((prev) => ({ ...prev, [role]: value }));
    setHasKeyPeopleUnsavedChanges(true);
  };

  const handleSaveKeyPeople = async () => {
    if (!project) return;
    // Build updated roleAssignments from edit state
    const updatedAssignments: ProjectRoleAssignment[] = [];
    PROJECT_ROLE_DISPLAY_ORDER.forEach((role) => {
      const empIdStr = keyPeopleEditState[role];
      if (!empIdStr) return; // cleared / not assigned
      const emp = employees.find((e) => String(e.id) === empIdStr);
      if (!emp) return;
      updatedAssignments.push({
        id: Date.now() + Math.random(), // temp id — replaced by backend on next load
        projectId: project.id,
        employeeId: emp.id,
        employeeName: emp.name,
        employeeJobTitle: emp.role ?? undefined,
        role,
      });
    });

    const dataMode = getDataMode();
    if (dataMode === 'live') {
      try {
        const assignmentItems = updatedAssignments.map((a) => ({
          role: a.role,
          employeeId: a.employeeId,
          employeeName: a.employeeName,
          employeeJobTitle: a.employeeJobTitle ?? null,
        }));
        await saveProjectRoleAssignments(project.id, assignmentItems);
        setProject({ ...project, roleAssignments: updatedAssignments });
        setIsKeyPeopleEditMode(false);
        setKeyPeopleEditState({});
        setHasKeyPeopleUnsavedChanges(false);
        toast.success('Key people updated.');
      } catch (err) {
        console.error('Failed to save key people:', err);
        toast.error('Failed to save key people.');
      }
    } else {
      setProject({ ...project, roleAssignments: updatedAssignments });
      setIsKeyPeopleEditMode(false);
      setKeyPeopleEditState({});
      setHasKeyPeopleUnsavedChanges(false);
      toast.success('Key people updated.');
    }
  };

  // ── Project Content cards — Objectives, Key Deliverables, Notes (Workstream E) ──

  const handleEnterObjectivesEdit = () => {
    setObjectivesEditValue(project?.objectives ?? "");
    setIsObjectivesEditMode(true);
  };
  const handleCancelObjectivesEdit = () => {
    setIsObjectivesEditMode(false);
    setObjectivesEditValue("");
  };
  const handleSaveObjectives = async () => {
    if (!project) return;
    const dataMode = getDataMode();
    const value = objectivesEditValue.trim() || undefined;
    if (dataMode === "live") {
      try {
        await saveProjectObjectives(numericProjectId, value ?? null);
        setProject({ ...project, objectives: value });
        setIsObjectivesEditMode(false);
        setObjectivesEditValue("");
        toast.success("Objectives saved.");
      } catch {
        toast.error("Failed to save objectives.");
      }
    } else {
      setProject({ ...project, objectives: value });
      setIsObjectivesEditMode(false);
      setObjectivesEditValue("");
      toast.success("Objectives saved.");
    }
  };

  const handleEnterDeliverablesEdit = () => {
    setDeliverablesEditValue(project?.keyDeliverables ?? "");
    setIsDeliverablesEditMode(true);
  };
  const handleCancelDeliverablesEdit = () => {
    setIsDeliverablesEditMode(false);
    setDeliverablesEditValue("");
  };
  const handleSaveDeliverables = async () => {
    if (!project) return;
    const dataMode = getDataMode();
    const value = deliverablesEditValue.trim() || undefined;
    if (dataMode === "live") {
      try {
        await saveProjectKeyDeliverables(numericProjectId, value ?? null);
        setProject({ ...project, keyDeliverables: value });
        setIsDeliverablesEditMode(false);
        setDeliverablesEditValue("");
        toast.success("Key deliverables saved.");
      } catch {
        toast.error("Failed to save key deliverables.");
      }
    } else {
      setProject({ ...project, keyDeliverables: value });
      setIsDeliverablesEditMode(false);
      setDeliverablesEditValue("");
      toast.success("Key deliverables saved.");
    }
  };

  // ── Note log — add a new entry ──
  const handleAddNote = async () => {
    if (!project || !newNoteValue.trim()) return;
    setIsSubmittingNote(true);

    // Derive initials from authenticated user name
    const name = authenticatedUser?.name ?? "Unknown User";
    const initials = name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0].toUpperCase())
      .join("");

    const dataMode = getDataMode();
    if (dataMode === "live") {
      try {
        const created = await createProjectNote(numericProjectId, newNoteValue.trim(), name, initials);
        setNoteLog((prev) => [created, ...prev]);
        setNewNoteValue("");
        toast.success("Note added.");
      } catch {
        toast.error("Failed to add note.");
      } finally {
        setIsSubmittingNote(false);
      }
    } else {
      const newEntry: ProjectNote = {
        id: crypto.randomUUID(),
        projectId: numericProjectId,
        content: newNoteValue.trim(),
        createdAt: new Date().toISOString(),
        createdByName: name,
        createdByInitials: initials,
      };
      setNoteLog((prev) => [newEntry, ...prev]);
      setNewNoteValue("");
      setIsSubmittingNote(false);
      toast.success("Note added.");
    }
  };

  // ── Project Attributes card — edit mode ──

  const handleEnterAttributesEditMode = () => {
    if (!project) return;
    setAttributesEditState({ ...(project.projectCustomFields ?? {}) });
    setIsAttributesEditMode(true);
  };

  const handleCancelAttributesEdit = () => {
    setIsAttributesEditMode(false);
    setAttributesEditState({});
    setAttributesValidationErrors({});
  };

  const handleSaveAttributes = async () => {
    if (!project) return;

    // Validate required fields
    const errors: Record<string, string> = {};
    for (const def of customFieldDefs) {
      if (def.required && def.enabled) {
        const val = attributesEditState[def.id] ?? '';
        if (val.trim() === '') {
          errors[def.id] = `${def.label} is required`;
        }
      }
    }
    if (Object.keys(errors).length > 0) {
      setAttributesValidationErrors(errors);
      toast.error('Please fill in all required fields.');
      return;
    }
    setAttributesValidationErrors({});

    try {
      const dataMode = getDataMode();
      let savedFields = { ...attributesEditState };
      if (dataMode === 'live') {
        savedFields = await saveProjectCustomFieldValues(numericProjectId, attributesEditState);
      }
      const updatedProject = { ...project, projectCustomFields: savedFields };
      setProject(updatedProject);
      setIsAttributesEditMode(false);
      setAttributesEditState({});
      toast.success("Project attributes updated.");
      window.dispatchEvent(
        new CustomEvent("project-updated", { detail: { projectId, project: updatedProject } })
      );
    } catch {
      toast.error("Failed to update project attributes.");
    }
  };

  // ── Client Information card — edit mode ──

  const handleEnterClientInfoEditMode = () => {
    if (!project) return;
    setClientInfoEditData({ accountManagerId: project.accountManagerId });
    setIsClientInfoEditMode(true);
    setHasClientInfoUnsavedChanges(false);
  };

  const handleCancelClientInfoEdit = () => {
    setIsClientInfoEditMode(false);
    setClientInfoEditData({});
    setHasClientInfoUnsavedChanges(false);
    setPendingAmChange(null);
    setShowAmConfirmDialog(false);
  };

  const handleSaveClientInfoChanges = async () => {
    try {
      if (!project) return;
      const dataMode = getDataMode();
      if (dataMode === "live") {
        await updateProject(numericProjectId, {
          id: numericProjectId,
          name: project.name,
          description: project.description,
          statusId: project.statusId,
          type: project.type,
          revenueRecognitionMethod: project.revenueRecognitionMethod,
          startDate: project.startDate,
          endDate: project.endDate,
          clientIds: project.clientIds,
          projectManagerId: project.projectManagerId,
          health: project.health,
          accountManagerId: clientInfoEditData.accountManagerId,
        });
      }
      const updatedProject = { ...project, ...clientInfoEditData };
      setProject(updatedProject);
      setIsClientInfoEditMode(false);
      setClientInfoEditData({});
      setHasClientInfoUnsavedChanges(false);
      toast.success("Client information updated");
      window.dispatchEvent(new CustomEvent("project-updated", { detail: { projectId, project: updatedProject } }));
    } catch (error) {
      toast.error("Failed to update client information");
      console.error("Error saving client information:", error);
    }
  };

  // ── Account Manager change handlers ──

  const handleAccountManagerChange = (employeeId: string, selectedEmployee: Employee | undefined) => {
    if (!selectedEmployee) return;
    const currentAmId = clientInfoEditData.accountManagerId ?? project?.accountManagerId;
    if (selectedEmployee.id !== currentAmId) {
      if (!accountManager) {
        // No existing AM — assign directly without confirmation
        setAccountManager(selectedEmployee);
        setClientInfoEditData((prev) => ({ ...prev, accountManagerId: selectedEmployee.id }));
        const updatedRecentAMs = [employeeId, ...recentAccountManagerIds.filter((id) => id !== employeeId)].slice(0, 10);
        setRecentAccountManagerIds(updatedRecentAMs);
        localStorage.setItem("recentAccountManagerIds", JSON.stringify(updatedRecentAMs));
        setHasClientInfoUnsavedChanges(true);
      } else {
        // Existing AM — show reassignment confirmation
        setPendingAmChange({ id: employeeId, employee: selectedEmployee });
        setShowAmConfirmDialog(true);
      }
    }
  };

  const handleConfirmAmChange = () => {
    if (!pendingAmChange) return;
    const { id: employeeId, employee: selectedEmployee } = pendingAmChange;
    setAccountManager(selectedEmployee);
    setClientInfoEditData((prev) => ({ ...prev, accountManagerId: selectedEmployee.id }));
    const updatedRecentAMs = [employeeId, ...recentAccountManagerIds.filter((id) => id !== employeeId)].slice(0, 10);
    setRecentAccountManagerIds(updatedRecentAMs);
    localStorage.setItem("recentAccountManagerIds", JSON.stringify(updatedRecentAMs));
    setHasClientInfoUnsavedChanges(true);
    setShowAmConfirmDialog(false);
    setPendingAmChange(null);
  };

  const handleCancelAmChange = () => {
    setShowAmConfirmDialog(false);
    setPendingAmChange(null);
  };

  // ── Client change helpers (isClientChangeLocked declared after fixedFeeInvoices useMemo below) ──

  /** Write audit entries to both the project audit tab and the system-wide report */
  function writeClientChangeAuditEntries(prevClient: Client, newClient: Client) {
    const description = `Client changed from ${prevClient.name} (${prevClient.code}) to ${newClient.name} (${newClient.code})`;

    // Project-level audit event
    const projectEvent: AuditEvent = {
      id: `client-change-${Date.now()}`,
      category: "client",
      changedAt: new Date().toISOString(),
      changedBy: "Demo User",
      description,
      metadata: {
        previousClientId: prevClient.id,
        previousClientName: prevClient.name,
        previousClientCode: prevClient.code,
        newClientId: newClient.id,
        newClientName: newClient.name,
        newClientCode: newClient.code,
        projectFinancialStateAtChange: {
          actualRevenue: project?.actualRevenue ?? 0,
          wipAmount: project?.wipAmount ?? 0,
          actualHours: project?.actualHours ?? 0,
        },
      },
    };
    setClientAuditEvents((prev) => [...prev, projectEvent]);

    // System-wide audit log entry
    appendAuditEntry({
      category: "Client",
      action: "Changed",
      entityName: `Project: ${project?.name ?? ""} (${project?.code ?? ""})`,
      oldValue: `${prevClient.name} (${prevClient.code})`,
      newValue: `${newClient.name} (${newClient.code})`,
      detail: `Client reassigned on project. Financial state at time of change — Revenue: £${project?.actualRevenue ?? 0}, WIP: £${project?.wipAmount ?? 0}, Hours: ${project?.actualHours ?? 0}h`,
      changedBy: "Demo User",
    });
  }

  /** Apply the client change to state (shared by silent + confirmed paths) */
  function applyClientChange(clientId: string, selectedClient: Client) {
    const previousClient = client;

    setClient(selectedClient);
    setEditFormData((prev) => ({ ...prev, clientIds: clientId }));

    const updatedRecentClients = [clientId, ...recentClientIds.filter((id) => id !== clientId)].slice(0, 10);
    setRecentClientIds(updatedRecentClients);
    localStorage.setItem("recentClientIds", JSON.stringify(updatedRecentClients));
    setHasUnsavedChanges(true);

    // Write audit entries for every client change
    if (previousClient && previousClient.id !== selectedClient.id) {
      writeClientChangeAuditEntries(previousClient, selectedClient);
    }
  }

  const handleClientChange = (clientId: string, selectedClient: Client | undefined) => {
    if (!selectedClient) return;

    // Hard block — field should already be disabled, but guard defensively
    if (isClientChangeLocked) return;

    // Soft warning — show confirmation dialog
    if (requiresClientChangeConfirmation) {
      setPreviousClientForDialog(client);
      setPendingClientChange({ clientId, selectedClient });
      setClientChangeDialogOpen(true);
      return;
    }

    // Silent allow — Draft with no financial activity
    applyClientChange(clientId, selectedClient);
  };

  const handleClientChangeConfirm = () => {
    if (pendingClientChange) {
      applyClientChange(pendingClientChange.clientId, pendingClientChange.selectedClient);
    }
    setClientChangeDialogOpen(false);
    setPendingClientChange(null);
    setPreviousClientForDialog(null);
  };

  const handleClientChangeCancel = () => {
    setClientChangeDialogOpen(false);
    setPendingClientChange(null);
    setPreviousClientForDialog(null);
  };

  // Project Settings handlers
  const handleSettingsChange = (field: string, value: any) => {
    setProjectSettings((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  // Drawer-specific handlers
  const handleDrawerSettingsChange = (field: string, value: any) => {
    setDrawerSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleDrawerOpen = (open: boolean) => {
    setBillingSettingsDrawerOpen(open);
    if (open) {
      // Reset drawer state when opening
      setDrawerSettings(projectSettings);
      setDrawerEditMode(false);
    }
  };

  const handleDrawerEdit = () => {
    setDrawerEditMode(true);
  };

  const handleDrawerCancel = () => {
    setDrawerSettings(projectSettings);
    setDrawerEditMode(false);
  };

  const handleDrawerSave = async () => {
    // Validate: fixed-price projects must have a default cost centre for finance posting
    if (project?.type === "fixed-price" && !drawerSettings.defaultCostCentreCode) {
      setDefaultCostCentreError(true);
      return;
    }

    setDrawerSaving(true);
    try {
      // Persist ALL billing config fields via PUT /api/projects/{id}/billing-config
      if (numericProjectId) {
        // Map drawer field names back to API field names
        const expenseRechargeRule =
          drawerSettings.expenseRecharge === 'not-rechargeable' ? 'none' as const
          : drawerSettings.expenseRecharge === 'markup' ? 'marked-up' as const
          : 'at-cost' as const;

        await updateProjectBillingConfig(numericProjectId, {
          invoiceTemplateId: drawerSettings.invoiceTemplateId ?? null,
          billingFrequency: drawerSettings.billingFrequency as "weekly" | "fortnightly" | "monthly" | "quarterly" | "on-completion" | "milestone",
          autoGenerate: drawerSettings.autoGenerateEnabled,
          defaultCostCentreCode: drawerSettings.defaultCostCentreCode ?? null,
          expenseRechargeRule,
          expenseMarkupPercent: drawerSettings.expenseMarkupPercent ? Number(drawerSettings.expenseMarkupPercent) : undefined,
          rechargesPurchases: drawerSettings.purchaseRecharge !== 'not-rechargeable',
          nteCapAmount: drawerSettings.notToExceedCapEnabled && drawerSettings.notToExceedCapAmount
            ? Number(drawerSettings.notToExceedCapAmount) : null,
          billingTriggerType: drawerSettings.billingTriggerType || undefined,
        });

        // Update local project state so UI reflects the billingTriggerType change immediately
        if (project && drawerSettings.billingTriggerType) {
          setProject((prev) =>
            prev ? { ...prev, billingTriggerType: drawerSettings.billingTriggerType as 'date' | 'percent-complete' } : prev
          );
        }
      }

      // Update the main project settings
      setProjectSettings(drawerSettings);
      setDrawerEditMode(false);
    } catch (error) {
      console.error("Error saving settings:", error);
    } finally {
      setDrawerSaving(false);
    }
  };

  // ── Completion % override handlers (Phase 13.2 / 13.3) ──────────────────

  // ── Fixed-fee invoices — in live mode use real billing data from the API;
  // in demo mode fall back to mock invoices derived from milestone data.
  const fixedFeeInvoices = useMemo(() => {
    // Live mode: map real BillingInvoice[] from API into the shape FixedFeeBillingTab expects
    if (dataMode === "live") {
      return billingInvoices.map((inv) => ({
        id: inv.id,
        invoiceNumber: inv.invoiceNumber || null,
        date: inv.invoiceDate ? inv.invoiceDate.split("T")[0] : null,
        description: inv.description || "",
        amount: inv.subtotal,
        due: inv.dueDate ? inv.dueDate.split("T")[0] : null,
        status: mapInvoiceStatus(inv.status) as "Draft" | "Sent" | "Paid" | "Overdue",
        milestoneId: inv.milestoneId ?? undefined,
      }));
    }

    // Demo mode: generate a realistic spread of invoices so the Invoice Summary card
    // always has Overdue and Draft rows to click through for testing (Task 26).
    const budget = project?.budgetRevenue ?? 0;
    const today = new Date();

    // Helper to format a date as YYYY-MM-DD
    const fmt = (d: Date) => d.toISOString().split('T')[0];

    // Helper to add days to a date
    const addDays = (d: Date, n: number) => {
      const r = new Date(d);
      r.setDate(r.getDate() + n);
      return r;
    };

    // Spread budget across 4 invoices: 30% / 25% / 25% / 20%
    const splits = [0.30, 0.25, 0.25, 0.20];
    const amounts = splits.map((s) => Math.round(budget * s));
    // Fix rounding so totals match exactly
    const diff = budget - amounts.reduce((a, b) => a + b, 0);
    amounts[0] += diff;

    // Invoice dates spread over the last ~9 months
    const invDates = [
      addDays(today, -270),
      addDays(today, -180),
      addDays(today, -75),
      addDays(today, -20),
    ];
    // Due dates = invoice date + 30 days
    const dueDates = invDates.map((d) => addDays(d, 30));

    // Statuses: Paid → Overdue → Sent → Draft
    // This ensures the card always has at least 1 Overdue and 1 Draft to click.
    const statuses: Array<"Paid" | "Overdue" | "Sent" | "Draft"> = ["Paid", "Overdue", "Sent", "Draft"];
    const invoiceNumbers = ["INV-FF-001", "INV-FF-002", null, null];

    return amounts.map((amount, i) => ({
      id: 100 + i + 1,
      invoiceNumber: invoiceNumbers[i],
      date: fmt(invDates[i]),
      description: [
        'Mobilisation Payment',
        'Progress Claim — 50% Complete',
        'Progress Claim — Practical Completion',
        'Final Retention Payment',
      ][i],
      amount,
      due: fmt(dueDates[i]),
      status: statuses[i],
      milestoneId: undefined,
    })).filter((inv) => inv.amount > 0);
  }, [dataMode, billingInvoices, project?.budgetRevenue]);

  /** Hard block — client field locked when project has financial transactions.
   *  Draft invoices are excluded — they carry no financial impact until Sent/Paid. */
  const isClientChangeLocked = Boolean(
    project && (
      (project.actualRevenue ?? 0) > 0 ||
      (project.wipAmount ?? 0) > 0 ||
      (project.unbilledAmount ?? 0) > 0 ||
      fixedFeeInvoices.some((inv) => inv.status !== 'Draft' && (inv.amount ?? 0) > 0)
    )
  );

  /** Soft warning — confirmation needed when project has approved hours */
  const requiresClientChangeConfirmation = Boolean(
    project && !isClientChangeLocked && (project.actualHours ?? 0) > 0
  );

  // ── Project Information field-level lock and warning computed values ──────

  /** Rev Rec — hard lock once any revenue has been recognised */
  const isRevRecLocked = Boolean(project && (project.actualRevenue ?? 0) > 0);

  /** Project Type — derive WIP invoice tier for three-tier lock rule */
  const wipInvoiceTier = useMemo<'none' | 'all-paid' | 'draft-exist' | 'sent-unpaid'>(() => {
    if (!fixedFeeInvoices.length) return 'none';
    const hasDraft = fixedFeeInvoices.some((i) => i.status === 'Draft');
    const hasSentUnpaid = fixedFeeInvoices.some((i) => i.status === 'Sent' || i.status === 'Overdue');
    if (hasSentUnpaid) return 'sent-unpaid';
    if (hasDraft) return 'draft-exist';
    return 'all-paid';
  }, [fixedFeeInvoices]);

  const isTypeLocked = wipInvoiceTier === 'sent-unpaid';

  /** Project Name — soft warning if sent/paid invoices exist */
  const projectNameHasIssuedInvoices = useMemo(
    () => fixedFeeInvoices.some((i) => i.status === 'Sent' || i.status === 'Paid' || i.status === 'Overdue'),
    [fixedFeeInvoices]
  );

  /** Date validation — computed against editFormData while in edit mode */
  const editStartDate = editFormData.startDate ?? project?.startDate ?? '';
  const editEndDate = editFormData.endDate ?? project?.endDate ?? '';
  const endDateBeforeStart = Boolean(
    editStartDate && editEndDate && new Date(editEndDate) < new Date(editStartDate)
  );

  /** Start Date — warn if moving start later than earliest time entry */
  const earliestTimeEntryDate = useMemo(() => {
    if (!timeEntries.length) return null;
    const sorted = [...timeEntries].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return sorted[0].date;
  }, [timeEntries]);

  const startDateMovedPastTimesheets = Boolean(
    editFormData.startDate &&
    earliestTimeEntryDate &&
    new Date(editFormData.startDate) > new Date(earliestTimeEntryDate)
  );

  /** End Date — warn if shortening past latest resource allocation */
  const latestAllocationEndDate = useMemo(() => {
    if (!resourceAllocations.length) return null;
    const withEnd = resourceAllocations.filter((r) => r.endDate);
    if (!withEnd.length) return null;
    const sorted = [...withEnd].sort((a, b) => new Date(b.endDate!).getTime() - new Date(a.endDate!).getTime());
    return sorted[0].endDate!;
  }, [resourceAllocations]);

  const endDateShortenedPastAllocations = Boolean(
    editFormData.endDate &&
    latestAllocationEndDate &&
    new Date(editFormData.endDate) < new Date(latestAllocationEndDate)
  );

  /** Overall save block — true when any hard block condition is active */
  const isSaveBlocked = endDateBeforeStart || isTypeLocked;

  /** Sum of Sent + Paid fixed-fee invoices — used by the Sales Budget Revenue Health panel. */
  const fixedFeeInvoicedToDate = useMemo(
    () => fixedFeeInvoices.filter((i) => (i.status as string) === 'Sent' || (i.status as string) === 'Paid').reduce((s, i) => s + i.amount, 0),
    [fixedFeeInvoices]
  );

  /** Fixed-fee invoices shaped as AuditInvoice[] for the Audit tab (Phase 17).
   *  The fixedFeeInvoices items don't carry createdDate/sentDate/paidDate in the
   *  current mock — we derive them from the invoice date field where possible.
   *  When the backend is live (17-BE.3) this memo is replaced by API data.
   */
  const auditInvoices = useMemo<AuditInvoice[]>(
    () =>
      fixedFeeInvoices.map((inv) => ({
        id: inv.id,
        invoiceNumber: inv.invoiceNumber,
        date: inv.date,
        dueDate: inv.due,
        description: inv.description,
        amount: inv.amount,
        status: inv.status as string,
        milestoneId: inv.milestoneId,
        // Derive event timestamps from status and the invoice date field
        createdDate: inv.date ?? undefined,
        sentDate: (inv.status === 'Paid' || inv.status === 'Sent') && inv.date ? inv.date : undefined,
        paidDate: inv.status === 'Paid' && inv.due ? inv.due : undefined,
      })),
    [fixedFeeInvoices]
  );

  /** The effective completion %, respecting the PM override if set. */
  const effectiveCompletionPercent = project
    ? (project.completionPercentOverride !== undefined
        ? project.completionPercentOverride
        : project.completionPercent)
    : 0;

  /** Auto-calculated % from approved hours ÷ budget hours (mirrors based-on-time logic). */
  const autoCalculatedCompletionPercent = project && project.budgetHours > 0
    ? Math.round((project.actualHours / project.budgetHours) * 100)
    : project?.completionPercent ?? 0;

  const handleCompletionPercentEditStart = () => {
    setCompletionPercentInputValue(String(effectiveCompletionPercent));
    setCompletionPercentEditing(true);
  };

  const handleCompletionPercentEditConfirm = () => {
    if (!project) return;
    const parsed = parseInt(completionPercentInputValue, 10);
    if (isNaN(parsed) || parsed < 0 || parsed > 100) {
      setCompletionPercentEditing(false);
      return;
    }
    const now = new Date().toISOString();
    setProject((prev) =>
      prev
        ? {
            ...prev,
            completionPercentOverride: parsed,
            completionPercentOverriddenBy: 'Sarah Chen', // current mock user
            completionPercentOverriddenAt: now,
            completionPercentMode: 'manual',
          }
        : prev
    );
    setCompletionPercentEditing(false);
  };

  const handleCompletionPercentEditCancel = () => {
    setCompletionPercentEditing(false);
  };

  /** Switch calculation mode in the Billing Settings drawer. */
  const handleCompletionModeChange = (mode: 'manual' | 'based-on-time') => {
    setProject((prev) => {
      if (!prev) return prev;
      if (mode === 'based-on-time') {
        // Recalculate from hours; clear any override
        return {
          ...prev,
          completionPercentMode: 'based-on-time',
          completionPercentOverride: undefined,
          completionPercentOverriddenBy: undefined,
          completionPercentOverriddenAt: undefined,
        };
      }
      // switching to manual — freeze current value
      return {
        ...prev,
        completionPercentMode: 'manual',
      };
    });
  };

  // Calculate next invoice generation date based on settings (for drawer)
  const getNextInvoiceDateForDrawer = () => {
    if (!drawerSettings.autoGenerateEnabled) return null;

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    if (drawerSettings.billingFrequency === "weekly") {
      const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
      const targetDay = daysOfWeek.indexOf(drawerSettings.weeklyDay);
      const currentDay = today.getDay();

      let daysUntilTarget = targetDay - currentDay;
      if (daysUntilTarget <= 0) {
        daysUntilTarget += 7;
      }

      const nextDate = new Date(today);
      nextDate.setDate(today.getDate() + daysUntilTarget);
      return nextDate;
    }

    // Monthly patterns
    let targetDate = new Date(currentYear, currentMonth, 1);

    switch (drawerSettings.monthlyPattern) {
      case "specific-day": {
        const day = parseInt(drawerSettings.monthlyDay, 10);
        targetDate.setDate(Math.min(day, new Date(currentYear, currentMonth + 1, 0).getDate()));
        break;
      }
      case "last-day":
        targetDate = new Date(currentYear, currentMonth + 1, 0);
        break;
      case "first-working-day": {
        targetDate.setDate(1);
        while (targetDate.getDay() === 0 || targetDate.getDay() === 6) {
          targetDate.setDate(targetDate.getDate() + 1);
        }
        break;
      }
      case "last-working-day": {
        targetDate = new Date(currentYear, currentMonth + 1, 0);
        while (targetDate.getDay() === 0 || targetDate.getDay() === 6) {
          targetDate.setDate(targetDate.getDate() - 1);
        }
        break;
      }
    }

    if (targetDate <= today) {
      targetDate.setMonth(targetDate.getMonth() + 1);

      if (drawerSettings.monthlyPattern === "specific-day") {
        const day = parseInt(drawerSettings.monthlyDay, 10);
        const lastDayOfMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0).getDate();
        targetDate.setDate(Math.min(day, lastDayOfMonth));
      } else if (drawerSettings.monthlyPattern === "last-day") {
        targetDate = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
      } else if (drawerSettings.monthlyPattern === "first-working-day") {
        targetDate.setDate(1);
        while (targetDate.getDay() === 0 || targetDate.getDay() === 6) {
          targetDate.setDate(targetDate.getDate() + 1);
        }
      } else if (drawerSettings.monthlyPattern === "last-working-day") {
        targetDate = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
        while (targetDate.getDay() === 0 || targetDate.getDay() === 6) {
          targetDate.setDate(targetDate.getDate() - 1);
        }
      }
    }

    return targetDate;
  };

  // Calculate next invoice generation date based on settings
  const getNextInvoiceDate = () => {
    if (!projectSettings.autoGenerateEnabled) return null;

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    if (projectSettings.billingFrequency === "weekly") {
      const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
      const targetDay = daysOfWeek.indexOf(projectSettings.weeklyDay);
      const currentDay = today.getDay();

      let daysUntilTarget = targetDay - currentDay;
      if (daysUntilTarget <= 0) {
        daysUntilTarget += 7; // Next week
      }

      const nextDate = new Date(today);
      nextDate.setDate(today.getDate() + daysUntilTarget);
      return nextDate;
    }

    // Monthly patterns
    if (projectSettings.billingFrequency === "monthly") {
      let nextDate = new Date(currentYear, currentMonth, 1);

      switch (projectSettings.monthlyPattern) {
        case "specific-day": {
          const day = parseInt(projectSettings.monthlyDay);
          const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
          const actualDay = Math.min(day, daysInMonth);
          nextDate.setDate(actualDay);

          // If the date has passed this month, move to next month
          if (nextDate <= today) {
            nextDate = new Date(currentYear, currentMonth + 1, 1);
            const nextMonthDays = new Date(currentYear, currentMonth + 2, 0).getDate();
            nextDate.setDate(Math.min(day, nextMonthDays));
          }
          break;
        }

        case "last-day": {
          nextDate = new Date(currentYear, currentMonth + 1, 0);
          if (nextDate <= today) {
            nextDate = new Date(currentYear, currentMonth + 2, 0);
          }
          break;
        }

        case "first-working-day": {
          nextDate = new Date(currentYear, currentMonth, 1);
          if (nextDate <= today) {
            nextDate = new Date(currentYear, currentMonth + 1, 1);
          }
          // Skip to Monday if weekend
          while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
            nextDate.setDate(nextDate.getDate() + 1);
          }
          break;
        }

        case "last-working-day": {
          nextDate = new Date(currentYear, currentMonth + 1, 0);
          if (nextDate <= today) {
            nextDate = new Date(currentYear, currentMonth + 2, 0);
          }
          // Skip back to Friday if weekend
          while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
            nextDate.setDate(nextDate.getDate() - 1);
          }
          break;
        }
      }

      return nextDate;
    }

    return null;
  };

  const validateSettings = () => {
    // Validate inputs
    if (projectSettings.expenseRecharge === "markup" && !projectSettings.expenseMarkupPercent) {
      toast.error("Please enter expense markup percentage");
      return false;
    }
    if (projectSettings.purchaseRecharge === "markup" && !projectSettings.purchaseMarkupPercent) {
      toast.error("Please enter purchase markup percentage");
      return false;
    }
    if (projectSettings.notToExceedCapEnabled && !projectSettings.notToExceedCapAmount) {
      toast.error("Please enter invoice cap amount");
      return false;
    }
    return true;
  };

  // Warn user about unsaved changes when navigating away (E7: covers all editable cards)
  useEffect(() => {
    const anyUnsaved =
      hasUnsavedChanges ||
      isObjectivesEditMode ||
      isDeliverablesEditMode ||
      isKeyPeopleEditMode ||
      isClientInfoEditMode;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (anyUnsaved) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, isObjectivesEditMode, isDeliverablesEditMode, isKeyPeopleEditMode, isClientInfoEditMode]);

  // Legacy event listener — kept for backwards compat but no longer triggered from UI
  useEffect(() => {
    const handleEditMode = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.projectId === projectId) {
        handleEnterEditMode();
      }
    };

    window.addEventListener("project-edit-mode", handleEditMode);
    return () => window.removeEventListener("project-edit-mode", handleEditMode);
  }, [projectId, handleEnterEditMode]);

  // Fetch scheduling bookings when the project loads or the resourcing tab is activated
  useEffect(() => {
    if (!projectId || !schedulerConnected) return;
    let cancelled = false;

    async function loadSchedulingData() {
      setSchedulingLoading(true);
      setSchedulingError(null);
      try {
        const response = await fetchSchedulingBookings(projectId);
        if (cancelled) return;
        setSchedulingBookings(response.bookings);
        setSchedulingConnectionStatus(response.connectionStatus);
        setSchedulingSchedulerName(response.schedulerName);
      } catch (err) {
        if (cancelled) return;
        console.error('[ProjectDetails] Failed to fetch scheduling bookings:', err);
        setSchedulingError('Unable to load scheduling data.');
        setSchedulingConnectionStatus('error');
        setSchedulingBookings([]);
      } finally {
        if (!cancelled) setSchedulingLoading(false);
      }
    }

    loadSchedulingData();
    return () => { cancelled = true; };
  }, [projectId, schedulerConnected]);

  // Fetch resource requests from local DB
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    async function loadResourceRequests() {
      try {
        const data = await fetchResourceRequests(projectId);
        if (cancelled) return;
        // Map API response to the ResourceRequest shape the component expects
        const mapped: ResourceRequest[] = data.map((r: ResourceRequestResponse) => ({
          requestId: r.requestId,
          roleName: r.roleName,
          hoursRequired: r.hoursRequired,
          startDate: r.desiredStartDate,
          endDate: r.desiredEndDate ?? r.desiredStartDate,
          priority: r.priority as ResourceRequest['priority'],
          status: r.status as ResourceRequest['status'],
          notes: r.notes,
          raisedAt: r.raisedAt,
          assignedTo: r.assignedTo,
        }));
        setLiveResourceRequests(mapped);
      } catch (err) {
        if (cancelled) return;
        console.error('[ProjectDetails] Failed to fetch resource requests:', err);
        setLiveResourceRequests([]);
      }
    }

    loadResourceRequests();
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    let ignore = false;
    async function loadData() {
      setLoading(true);
      try {
        const [currentProject, clientsData, employeesData, resourceAllocationsData] = await Promise.all([
          fetchProjectById(numericProjectId),
          fetchClients("projects"),
          fetchEmployees("projects"),
          fetchResourceAllocations("projects"),
        ]);

        if (ignore) return;

        if (currentProject) {
          setProject(currentProject);
          // Seed note log — use API in live mode, fall back to project data in demo/tour
          const dataMode = getDataMode();
          if (dataMode === "live") {
            try {
              const notes = await fetchProjectNotes(numericProjectId);
              setNoteLog(notes);
            } catch {
              setNoteLog([]);
            }
          } else {
            setNoteLog(currentProject.noteLog ?? []);
          }
          setClients(clientsData); // Store all clients for the dropdown
          // Get the first client ID from the comma-separated list
          const firstClientId = currentProject.clientIds?.split(",")[0]?.trim();
          setClient(clientsData.find((c) => c.id === parseInt(firstClientId || "0", 10)) || null);
          setProjectManager(employeesData.find((e) => e.id === currentProject.projectManagerId) || null);
          setAccountManager(currentProject.accountManagerId ? (employeesData.find((e) => e.id === currentProject.accountManagerId) || null) : null);

          // Load role assignments from API (live mode)
          if (dataMode === 'live') {
            try {
              const roleAssignmentsData = await fetchProjectRoleAssignments(numericProjectId);
              if (!ignore && roleAssignmentsData.length > 0) {
                const mapped: ProjectRoleAssignment[] = roleAssignmentsData.map((a) => ({
                  id: 0, // placeholder — not used for display
                  projectId: a.projectId,
                  employeeId: a.employeeId,
                  employeeName: a.employeeName,
                  employeeJobTitle: a.employeeJobTitle ?? undefined,
                  role: a.role as ProjectRoleType,
                }));
                currentProject.roleAssignments = mapped;
                setProject({ ...currentProject });
              }
            } catch (err) {
              console.error('Failed to load role assignments:', err);
            }
          }

          // Load custom field definitions and per-project values from API
          if (dataMode === 'live') {
            try {
              const [defsResponse, valuesResponse] = await Promise.all([
                fetchCustomFieldDefinitions(),
                fetchProjectCustomFieldValues(numericProjectId),
              ]);
              if (!ignore) {
                // Map backend definitions to frontend type
                const mapped = defsResponse
                  .filter((d: CustomFieldDefinitionResponse) => d.enabled)
                  .map((d: CustomFieldDefinitionResponse) => {
                    let parsedOptions: string[] | undefined;
                    if (d.options) {
                      try { const p = JSON.parse(d.options); if (Array.isArray(p)) parsedOptions = p; } catch { /* ignore */ }
                    }
                    return {
                      id: d.id,
                      label: d.label,
                      fieldType: d.fieldType as import('@/types').CustomFieldType,
                      required: d.required,
                      enabled: d.enabled,
                      displayOrder: d.displayOrder,
                      options: parsedOptions,
                      placeholder: d.placeholder ?? undefined,
                    };
                  });
                setCustomFieldDefs(mapped);
                // Merge API values into the project object
                if (Object.keys(valuesResponse).length > 0) {
                  currentProject.projectCustomFields = valuesResponse;
                  setProject({ ...currentProject });
                }
              }
            } catch (err) {
              console.error('Failed to load custom field data:', err);
            }
          } else {
            // Demo/tour mode — load from localStorage
            try {
              const { getProjectCustomFieldDefinitions } = await import('@/lib/project-custom-fields-storage');
              const defs = getProjectCustomFieldDefinitions().filter((d) => d.enabled);
              if (!ignore) setCustomFieldDefs(defs);
            } catch { /* ignore */ }
          }

          // Get resource allocations for this project
          const projectAllocations = resourceAllocationsData.filter((r) => r.projectId === numericProjectId);
          setResourceAllocations(projectAllocations);

          if (dataMode === "live") {
            // Live mode - fetch data from API
            try {
              const expenseOptions: FetchExpensesOptions = {
                includeApprovedExpenses: includeApprovedExpenses,
                includeCreditCardExpenses: includeCreditCardExpenses,
              };
              const [apiTimeEntries, apiExpenses, apiMilestones, apiChangeRequests] = await Promise.all([
                fetchTimeEntriesByProjectId(numericProjectId),
                fetchExpensesByProjectId(
                  numericProjectId,
                  currentProject.code,
                  currentCompany,
                  "expenses",
                  expenseOptions,
                ),
                fetchMilestonesByProjectId(numericProjectId),
                fetchChangeRequestsByProjectId(numericProjectId),
              ]);
              setTimeEntries(apiTimeEntries);
              setExpenses(apiExpenses);
              setMilestones(apiMilestones);
              setChangeRequests(apiChangeRequests);

              // Calculate actual hours per employee from time entries
              const timeByEmployee = new Map<number, number>();
              apiTimeEntries.forEach((entry: TimeEntry) => {
                const current = timeByEmployee.get(entry.employeeId) || 0;
                timeByEmployee.set(entry.employeeId, current + entry.hours);
              });

              // Build a map of resource allocations by employee for quick lookup
              const allocationsByEmployee = new Map<number, (typeof projectAllocations)[0]>();
              projectAllocations.forEach((alloc) => {
                allocationsByEmployee.set(alloc.employeeId, alloc);
              });

              // Generate team members from all employees fetched from the backend
              // This ensures the Team Management table shows employees even when there are no resource allocations
              const liveTeamMembers: TeamMember[] = employeesData.map((employee) => {
                const allocation = allocationsByEmployee.get(employee.id);
                const actualHours = timeByEmployee.get(employee.id) || 0;

                // Use allocation data if available, otherwise default to 0
                const allocatedHours = allocation?.allocatedHours || 0;
                const remaining = allocatedHours - actualHours;
                const utilisationPercent = allocatedHours > 0 ? (actualHours / allocatedHours) * 100 : 0;

                // Determine status based on utilisation
                let status: TeamMember["status"];
                if (allocatedHours > 0 && utilisationPercent > 100) {
                  status = "Over Allocated";
                } else if (allocatedHours > 0 && utilisationPercent > 85) {
                  status = "At Risk";
                } else {
                  status = "On Track";
                }

                return {
                  id: allocation?.id || employee.id,
                  employeeId: employee.id,
                  name: employee.name,
                  email: employee.email || "",
                  role: allocation?.role || employee.role || "Unassigned",
                  avatar: employee.photo,
                  allocatedHours,
                  actualHours,
                  remaining,
                  startDate: allocation?.startDate || "",
                  endDate: allocation?.endDate || "",
                  status,
                  utilisationPercent,
                };
              });

              // Set team members from employees
              setTeamMembers(liveTeamMembers);

              // Billing data is lazy-loaded when the Billing tab is activated
              // (see useEffect for activeTab === "billing" below)
            } catch (error) {
              console.error("Failed to fetch project details from API:", error);
              setTimeEntries([]);
              setExpenses([]);
              setMilestones([]);
              setChangeRequests([]);
              setTeamMembers([]);
              setBillingWipItems([]);
              setBillingInvoices([]);
            }
          } else {
            // Demo mode - generate mock data
            // Mock time entries - include both allocated and unallocated employees
            const mockTimeEntries: TimeEntry[] = projectAllocations.slice(0, 10).map((alloc, i) => {
              const isApproved = Math.random() > 0.3;
              const entryDate = new Date(Date.now() - i * 86400000);

              return {
                id: numericProjectId * 10000 + i,
                externalTimesheetId: `TS-2024-${1000 + i}`,
                projectId: numericProjectId,
                employeeId: alloc.employeeId,
                date: entryDate.toISOString().split("T")[0],
                hours: 6 + Math.random() * 3,
                description: "Development work",
                billable: Math.random() > 0.2,
                approved: isApproved,
                approvalHistory: [
                  {
                    date: new Date(entryDate.getTime() + 3600000).toISOString(),
                    action: "Submitted for Approval",
                    byUser: employeesData.find((e) => e.id === alloc.employeeId)?.name || "Employee",
                    status: "Pending Approval",
                  },
                  ...(isApproved
                    ? [
                        {
                          date: new Date(entryDate.getTime() + 7200000).toISOString(),
                          action: "Approved",
                          byUser: "Project Manager",
                          toUser: "Finance Team",
                          stage: "Manager Approval",
                          status: "Approved",
                        },
                      ]
                    : []),
                ],
                costRate: 50 + Math.random() * 30,
                billRate: 100 + Math.random() * 60,
              };
            });

            // Add time entries from employees NOT allocated to the project (to demonstrate unallocated tracking)
            const mockAllocatedEmployeeIds = new Set(projectAllocations.map((a) => a.employeeId));
            const unallocatedEmployees = employeesData.filter((e) => !mockAllocatedEmployeeIds.has(e.id)).slice(0, 2);

            unallocatedEmployees.forEach((employee, i) => {
              // Add 3-5 time entries for each unallocated employee
              const entryCount = 3 + Math.floor(Math.random() * 3);
              for (let j = 0; j < entryCount; j++) {
                const isApproved = Math.random() > 0.4;
                const entryDate = new Date(Date.now() - (i * 7 + j) * 86400000);

                mockTimeEntries.push({
                  id: numericProjectId * 10000 + 1000 + i * 10 + j,
                  externalTimesheetId: `TS-2024-${2000 + i * 10 + j}`,
                  projectId: numericProjectId,
                  employeeId: employee.id,
                  date: entryDate.toISOString().split("T")[0],
                  hours: 4 + Math.random() * 4,
                  description: "Ad-hoc support work",
                  billable: Math.random() > 0.3,
                  approved: isApproved,
                  approvalHistory: [
                    {
                      date: new Date(entryDate.getTime() + 3600000).toISOString(),
                      action: "Submitted for Approval",
                      byUser: employee.name,
                      status: "Pending Approval",
                    },
                    ...(isApproved
                      ? [
                          {
                            date: new Date(entryDate.getTime() + 7200000).toISOString(),
                            action: "Approved",
                            byUser: "Project Manager",
                            toUser: "Finance Team",
                            stage: "Manager Approval",
                            status: "Approved",
                          },
                        ]
                      : []),
                  ],
                  costRate: 50 + Math.random() * 30,
                  billRate: 100 + Math.random() * 60,
                });
              }
            });

            setTimeEntries(mockTimeEntries);

            // Mock expenses
            const mockExpenses: Expense[] = [
              {
                id: numericProjectId * 1000 + 1,
                projectId: numericProjectId,
                employeeId: projectAllocations[0]?.employeeId || "e1",
                date: new Date(Date.now() - 5 * 86400000).toISOString().split("T")[0],
                category: "Travel",
                amount: 450,
                description: "Client site visit",
                billable: true,
                approved: true,
                approvalHistory: [
                  {
                    date: new Date(Date.now() - 5 * 86400000 + 3600000).toISOString(),
                    action: "Submitted for Approval",
                    byUser:
                      employeesData.find((e) => e.id === (projectAllocations[0]?.employeeId || "e1"))?.name ||
                      "Employee",
                    status: "Pending Approval",
                  },
                  {
                    date: new Date(Date.now() - 5 * 86400000 + 7200000).toISOString(),
                    action: "Approved",
                    byUser: "Project Manager",
                    toUser: "Finance Team",
                    stage: "Manager Approval",
                    status: "Approved",
                  },
                ],
              },
              {
                id: numericProjectId * 1000 + 2,
                projectId: numericProjectId,
                employeeId: projectAllocations[1]?.employeeId || "e2",
                date: new Date(Date.now() - 10 * 86400000).toISOString().split("T")[0],
                category: "Software",
                amount: 299,
                description: "Development tools licence",
                billable: true,
                approved: false,
                approvalHistory: [
                  {
                    date: new Date(Date.now() - 10 * 86400000 + 3600000).toISOString(),
                    action: "Submitted for Approval",
                    byUser:
                      employeesData.find((e) => e.id === (projectAllocations[1]?.employeeId || "e2"))?.name ||
                      "Employee",
                    status: "Pending Approval",
                  },
                ],
              },
            ];
            setExpenses(mockExpenses);

            // Mock milestones
            const mockMilestones: Milestone[] = [
              {
                id: numericProjectId * 100 + 1,
                projectId: numericProjectId,
                name: "Project Kickoff",
                dueDate: currentProject.startDate,
                completedDate: currentProject.startDate,
                status: "completed",
                paymentAmount: currentProject.budgetRevenue * 0.2,
                paymentReceived: true,
              },
              {
                id: numericProjectId * 100 + 2,
                projectId: numericProjectId,
                name: "Phase 1 Delivery",
                dueDate: new Date(new Date(currentProject.startDate).getTime() + 60 * 86400000)
                  .toISOString()
                  .split("T")[0],
                completedDate: new Date(new Date(currentProject.startDate).getTime() + 58 * 86400000)
                  .toISOString()
                  .split("T")[0],
                status: "completed",
                paymentAmount: currentProject.budgetRevenue * 0.3,
                paymentReceived: true,
              },
              {
                id: numericProjectId * 100 + 3,
                projectId: numericProjectId,
                name: "Phase 2 Delivery",
                dueDate: new Date(new Date(currentProject.startDate).getTime() + 120 * 86400000)
                  .toISOString()
                  .split("T")[0],
                status: currentProject.completionPercent > 70 ? "completed" : "pending",
                completedDate:
                  currentProject.completionPercent > 70
                    ? new Date(new Date(currentProject.startDate).getTime() + 118 * 86400000)
                        .toISOString()
                        .split("T")[0]
                    : undefined,
                paymentAmount: currentProject.budgetRevenue * 0.3,
                paymentReceived: false,
              },
              {
                id: numericProjectId * 100 + 4,
                projectId: numericProjectId,
                name: "Final Delivery",
                dueDate: currentProject.endDate,
                status: currentProject.status === "completed" ? "completed" : "pending",
                completedDate: currentProject.status === "completed" ? currentProject.endDate : undefined,
                paymentAmount: currentProject.budgetRevenue * 0.2,
                paymentReceived: false,
              },
            ];
            setMilestones(mockMilestones);

            // Mock change requests
            const mockChangeRequests: ChangeRequest[] = [
              {
                id: numericProjectId * 100 + 1,
                projectId: numericProjectId,
                title: "Additional feature request",
                description: "Client requested additional reporting functionality",
                requestedBy: "Client",
                requestedDate: new Date(Date.now() - 15 * 86400000).toISOString().split("T")[0],
                status: "approved",
                impactBudget: 15000,
                impactSchedule: 10,
                approvedBy: currentProject.projectManagerId,
                approvedDate: new Date(Date.now() - 10 * 86400000).toISOString().split("T")[0],
              },
              {
                id: numericProjectId * 100 + 2,
                projectId: numericProjectId,
                title: "Scope clarification",
                description: "Clarification on integration requirements",
                requestedBy: "Project Team",
                requestedDate: new Date(Date.now() - 5 * 86400000).toISOString().split("T")[0],
                status: "pending",
                impactBudget: 0,
                impactSchedule: 0,
              },
            ];
            setChangeRequests(mockChangeRequests);

            // Generate comprehensive team member data with mock hours
            const mockTeamMembers: TeamMember[] = projectAllocations.map((alloc) => {
              const employee = employeesData.find((e) => e.id === alloc.employeeId);
              const allocatedHours = Math.floor(Math.random() * 200) + 100; // 100-300 hours
              const actualHours = Math.floor(allocatedHours * (0.3 + Math.random() * 0.8)); // 30-110% of allocated
              const remaining = allocatedHours - actualHours;
              const utilisationPercent = (actualHours / allocatedHours) * 100;

              // Determine status based on utilisation only
              let status: TeamMember["status"];

              if (utilisationPercent > 100) {
                status = "Over Allocated";
              } else if (utilisationPercent > 85) {
                status = "At Risk";
              } else {
                status = "On Track";
              }

              // Generate avatar URL using UI Avatars service
              const name = employee?.name || "Unknown";
              const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;

              return {
                id: alloc.id,
                employeeId: alloc.employeeId,
                name: name,
                email: employee?.email || "",
                role: alloc.role,
                avatar: avatarUrl,
                allocatedHours,
                actualHours,
                remaining,
                startDate: alloc.startDate,
                endDate: alloc.endDate,
                status,
                utilisationPercent,
              };
            });

            // Find employees who logged time but weren't allocated
            const demoAllocatedEmployeeIds = new Set(mockTeamMembers.map((tm) => tm.employeeId));
            const demoUnallocatedWithTime: TeamMember[] = [];

            // Group time entries by employee
            const demoTimeByEmployee = new Map<number, number>();
            mockTimeEntries.forEach((entry) => {
              if (!demoAllocatedEmployeeIds.has(entry.employeeId)) {
                const current = demoTimeByEmployee.get(entry.employeeId) || 0;
                demoTimeByEmployee.set(entry.employeeId, current + entry.hours);
              }
            });

            // Create TeamMember entries for unallocated employees who logged time
            demoTimeByEmployee.forEach((hours, employeeId) => {
              const employee = employeesData.find((e) => e.id === employeeId);
              if (employee) {
                const name = employee.name;
                const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;

                demoUnallocatedWithTime.push({
                  id: `unalloc-${employeeId}`,
                  employeeId: employee.id,
                  name: name,
                  email: employee.email || "",
                  role: employee.role || "Unassigned", // Use employee.role instead of employee.position
                  avatar: avatarUrl,
                  allocatedHours: 0, // Not allocated
                  actualHours: hours,
                  remaining: -hours, // Negative because they worked without allocation
                  startDate: "", // No allocation dates
                  endDate: "",
                  status: "Over Allocated", // Working without allocation
                  utilisationPercent: 0, // Can't calculate without allocation
                });
              }
            });

            // Combine allocated and unallocated team members
            setTeamMembers([...mockTeamMembers, ...demoUnallocatedWithTime]);
          }

          // Fetch cost centres - use API in live mode, mock data in demo mode
          // Note: dataMode is already defined above
          if (dataMode === "live") {
            try {
              const apiCostCentres = await fetchCostCentresByProjectId(numericProjectId);
              setCostCentres(apiCostCentres);
            } catch (error) {
              console.error("Failed to fetch cost centres from API:", error);
              setCostCentres([]);
            }
          } else {
            // Helper arrays for variety (used by both flat and hierarchical views)
            const types = ["Functional Area", "Administrative", "Technical Support", "Operations", "Support Services"];
            const statuses: Array<"Not Started" | "In Progress" | "On Hold" | "Completed" | "Cancelled"> = [
              "Not Started",
              "In Progress",
              "On Hold",
              "Completed",
              "Cancelled",
            ];
            const departments = [
              "Engineering",
              "Design",
              "Quality Assurance",
              "PMO",
              "IT Operations",
              "Marketing",
              "Sales",
              "Finance",
              "HR",
              "Legal",
            ];
            const locations = [
              "London Office",
              "Manchester Office",
              "Birmingham Office",
              "Head Office",
              "Data Centre",
              "Edinburgh Office",
              "Bristol Office",
              "Leeds Office",
              "Remote",
              "Client Site",
            ];
            const priorities = ["High", "Medium", "Low"];
            const owners = [
              "John Smith",
              "Sarah Johnson",
              "Mike Davis",
              "Emily Brown",
              "David Wilson",
              "Alice Chen",
              "Bob Martinez",
              "Carol White",
              "Dan Brown",
              "Emma Wilson",
            ];

            // Mock cost centres data for demo mode - 50 records for scrolling test
            const mockCostCentres: CostCentre[] = [];

            // Generate 50 cost centres
            for (let i = 1; i <= 50; i++) {
              const code = `CC-${String(i).padStart(3, "0")}`;
              const typeIndex = (i - 1) % types.length;
              const statusIndex = (i - 1) % statuses.length;
              const deptIndex = (i - 1) % departments.length;
              const locIndex = (i - 1) % locations.length;
              const priorityIndex = (i - 1) % priorities.length;
              const ownerIndex = (i - 1) % owners.length;

              // Vary the budget allocation
              const budgetFactor = 0.02 * (1 + (i % 5) * 0.5); // Between 2% and 6% of total
              const actualFactor = budgetFactor * (0.85 + Math.random() * 0.3); // 85% to 115% of budget
              const committedFactor = budgetFactor * 0.1;

              mockCostCentres.push({
                id: -Date.now() - i,
                code: code,
                name: `Cost Centre ${i}`,
                description: `Description for cost centre ${code}`,
                type: types[typeIndex],
                status: statuses[statusIndex],
                billable: i % 3 !== 0, // 2/3 are billable
                costsBudget: currentProject.budgetCost * budgetFactor,
                actualCosts: currentProject.actualCost * actualFactor,
                committedCosts: currentProject.budgetCost * committedFactor,
                revenueBudget: currentProject.budgetRevenue * budgetFactor,
                actualRevenue: currentProject.actualRevenue * actualFactor * 0.95,
                hoursBudget: Math.round(budgetFactor * 10000),
                actualHours: Math.round(actualFactor * 9500),
                owner: owners[ownerIndex],
                lastUpdated: new Date(Date.now() - (i % 10) * 86400000),
                createdAt: new Date(currentProject.startDate),
                updatedAt: new Date(Date.now() - (i % 10) * 86400000),
                customFields: {
                  Department: departments[deptIndex],
                  Location: locations[locIndex],
                  Priority: priorities[priorityIndex],
                  "Start Date": new Date(currentProject.startDate),
                  "End Date": new Date(currentProject.endDate),
                  "Requires Approval": i % 2 === 0,
                  "Approval Threshold": 1000 + i * 100,
                  "Cost Code": `${code}-${departments[deptIndex].substring(0, 3).toUpperCase()}`,
                  "GL Account": `${6100 + (i % 5) * 100}-${1000 + (i % 10) * 100}`,
                  "Project Phase": `Phase ${(i % 5) + 1}`,
                },
              });
            }
            setCostCentres(mockCostCentres);

            // Mock hierarchical cost centres data - includes all 50 cost centre codes
            const mockHierarchicalCostCentres: HierarchicalCostCentre[] = [];

            // Create 5 parent departments, each with 10 cost centres
            const parentDepartments: Array<{ code: string; name: string; range: [number, number] }> = [
              { code: "CC-DEPT-01", name: "Engineering Department", range: [1, 10] },
              { code: "CC-DEPT-02", name: "Design & Creative", range: [11, 20] },
              { code: "CC-DEPT-03", name: "Operations", range: [21, 30] },
              { code: "CC-DEPT-04", name: "Support Services", range: [31, 40] },
              { code: "CC-DEPT-05", name: "Administration", range: [41, 50] },
            ];

            parentDepartments.forEach((dept, deptIndex) => {
              // Calculate totals for parent
              const deptBudgetFactor = 0.2; // Each department gets 20% of total budget

              // Add parent department
              mockHierarchicalCostCentres.push({
                id: -Date.now() - 100 - deptIndex,
                code: dept.code,
                name: dept.name,
                billable: true,
                costsBudget: currentProject.budgetCost * deptBudgetFactor,
                actualCosts: currentProject.actualCost * deptBudgetFactor * (0.9 + Math.random() * 0.2),
                committedCosts: currentProject.budgetCost * deptBudgetFactor * 0.05,
                revenueBudget: currentProject.budgetRevenue * deptBudgetFactor,
                actualRevenue: currentProject.actualRevenue * deptBudgetFactor * (0.9 + Math.random() * 0.2),
                hoursBudget: Math.round(deptBudgetFactor * 10000),
                actualHours: Math.round(deptBudgetFactor * 9500 * (0.9 + Math.random() * 0.2)),
                parentId: null,
              });

              // Add child cost centres (CC-001 to CC-050)
              for (let i = dept.range[0]; i <= dept.range[1]; i++) {
                const code = `CC-${String(i).padStart(3, "0")}`;
                const budgetFactor = 0.02 * (1 + (i % 5) * 0.5); // Between 2% and 6% of total
                const actualFactor = budgetFactor * (0.85 + Math.random() * 0.3);
                const committedFactor = budgetFactor * 0.1;

                const statusIndex = (i - 1) % statuses.length;
                const ownerIndex = (i - 1) % owners.length;

                mockHierarchicalCostCentres.push({
                  id: -Date.now() - i,
                  code: code,
                  name: `Cost Centre ${i}`,
                  type: types[(i - 1) % types.length],
                  status: statuses[statusIndex],
                  billable: i % 3 !== 0,
                  costsBudget: currentProject.budgetCost * budgetFactor,
                  actualCosts: currentProject.actualCost * actualFactor,
                  committedCosts: currentProject.budgetCost * committedFactor,
                  revenueBudget: currentProject.budgetRevenue * budgetFactor,
                  actualRevenue: currentProject.actualRevenue * actualFactor * 0.95,
                  hoursBudget: Math.round(budgetFactor * 10000),
                  actualHours: Math.round(actualFactor * 9500),
                  owner: owners[ownerIndex],
                  lastUpdated: new Date(Date.now() - (i % 10) * 86400000),
                  parentId: -Date.now() - 100 - deptIndex,
                });
              }
            });

            setHierarchicalCostCentres(mockHierarchicalCostCentres);
          }
        }

        setEmployees(employeesData);
      } catch (error) {
        handleApiError(error, `/api/projects/${projectId}`, "GET");
      } finally {
        setLoading(false);
      }
    }
    loadData();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Sync expense checkbox defaults when company settings change
  useEffect(() => {
    if (currentCompany) {
      setIncludeApprovedExpenses(currentCompany.approvedExpensesEnabled ?? true);
      setIncludeCreditCardExpenses(currentCompany.creditCardExpensesEnabled ?? false);
    }
  }, [currentCompany?.id]);

  // Re-fetch expenses when checkbox selections change
  useEffect(() => {
    if (!project || loading) return;
    const dataMode = getDataMode();
    if (dataMode !== "live") return;

    async function refetchExpenses() {
      setExpensesLoading(true);
      try {
        const expenseOpts: FetchExpensesOptions = {
          includeApprovedExpenses: includeApprovedExpenses,
          includeCreditCardExpenses: includeCreditCardExpenses,
        };
        const refreshedExpenses = await fetchExpensesByProjectId(
          numericProjectId,
          project!.code,
          currentCompany,
          "expenses",
          expenseOpts,
        );
        setExpenses(refreshedExpenses);
      } catch (error) {
        console.error("Failed to refetch expenses:", error);
      } finally {
        setExpensesLoading(false);
      }
    }
    refetchExpenses();
  }, [includeApprovedExpenses, includeCreditCardExpenses]);

  // Lazy-load billing data when the Billing tab is activated
  // This avoids fetching 20k+ WIP items on initial project detail load (Overview tab)
  const billingDataLoadedRef = useRef(false);
  useEffect(() => {
    if (activeTab !== "billing") return;
    if (loading || !project) return;
    const dataMode = getDataMode();
    if (dataMode !== "live") return;
    // Only fetch on first activation; subsequent refreshes use refetchBillingData
    if (billingDataLoadedRef.current) return;
    billingDataLoadedRef.current = true;

    async function fetchBilling() {
      try {
        setBillingLoading(true);
        const [apiBillingWipResponse, apiBillingInvoices] = await Promise.all([
          fetchBillingWipItemsPaged(numericProjectId, 0, 1, wipItemsPerPage),
          fetchBillingInvoices(numericProjectId),
        ]);
        setBillingWipItems(apiBillingWipResponse.items);
        setWipTotalCount(apiBillingWipResponse.totalCount);
        if (apiBillingWipResponse.summary) setWipSummary(apiBillingWipResponse.summary);
        setBillingInvoices(apiBillingInvoices);
      } catch (billingError) {
        console.error("Failed to fetch billing data from API:", billingError);
        setBillingWipItems([]);
        setWipTotalCount(0);
        setWipSummary({ totalNetAmount: 0, timeNetAmount: 0, expenseNetAmount: 0, timeHours: 0 });
        setBillingInvoices([]);
      } finally {
        setBillingLoading(false);
      }
    }
    fetchBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loading, project]);

  // Billing action handlers
  const refetchBillingData = useCallback(async () => {
    if (dataMode !== "live") return;
    try {
      setBillingLoading(true);
      const [updatedWipResponse, updatedInvoices] = await Promise.all([
        fetchBillingWipItemsPaged(numericProjectId, 0, wipCurrentPage, wipItemsPerPage),
        fetchBillingInvoices(numericProjectId),
      ]);
      setBillingWipItems(updatedWipResponse.items);
      setWipTotalCount(updatedWipResponse.totalCount);
      if (updatedWipResponse.summary) setWipSummary(updatedWipResponse.summary);
      setBillingInvoices(updatedInvoices);
    } catch (error) {
      console.error("Failed to refresh billing data:", error);
    } finally {
      setBillingLoading(false);
    }
  }, [dataMode, numericProjectId, wipCurrentPage, wipItemsPerPage]);

  const handleCreateDraft = useCallback(async (description: string, notes?: string) => {
    if (dataMode !== "live") return;
    // Get selected WIP item IDs (extract numeric ID from "wip-{id}" format)
    const wipItemIds = selectedWipItems
      .map((id) => {
        const numId = parseInt(id.replace("wip-", ""), 10);
        return isNaN(numId) ? null : numId;
      })
      .filter((id): id is number => id !== null);
    
    if (wipItemIds.length === 0) {
      console.warn("No WIP items selected for draft creation");
      return;
    }

    try {
      setBillingActionLoading(true);
      await createBillingDraft(numericProjectId, wipItemIds, description, notes);
      setSelectedWipItems([]); // Clear selection
      setCreateDraftDialogOpen(false);
      await refetchBillingData();
    } catch (error) {
      console.error("Failed to create draft:", error);
      handleApiError(error, `/api/billing/${numericProjectId}/drafts`, "POST");
    } finally {
      setBillingActionLoading(false);
    }
  }, [dataMode, numericProjectId, selectedWipItems, refetchBillingData, handleApiError]);

  const handleDeleteDraft = useCallback(async (invoiceId: number) => {
    if (dataMode !== "live") return;
    try {
      setBillingActionLoading(true);
      await deleteBillingDraft(invoiceId);
      await refetchBillingData();
    } catch (error) {
      console.error("Failed to delete draft:", error);
      handleApiError(error, `/api/billing/drafts/${invoiceId}`, "DELETE");
    } finally {
      setBillingActionLoading(false);
    }
  }, [dataMode, refetchBillingData, handleApiError]);

  const handlePostToFinancials = useCallback(async (invoiceId: number) => {
    if (dataMode !== "live") return;
    try {
      setBillingActionLoading(true);
      const result = await postBillingDraftToFinancials(invoiceId);

      if (result.success) {
        toast.success("Invoice posted to Access Financials", {
          description: result.financialsIds?.length
            ? `Transaction ID${result.financialsIds.length > 1 ? "s" : ""}: ${result.financialsIds.join(", ")}`
            : undefined,
        });
        await refetchBillingData();
      } else if (result.validationErrors && result.validationErrors.length > 0) {
        const errorMessages = result.validationErrors
          .map((e) => {
            const details = e.details?.map((d) => `${d.field}: ${d.value}`).join(", ");
            return details ? `${e.message} (${details})` : e.message;
          })
          .join("\n");
        toast.error("Financials validation failed", {
          description: errorMessages,
          duration: 10000,
        });
      } else {
        toast.error("Failed to post invoice to Financials", {
          description: result.error || "An unexpected error occurred",
          duration: 8000,
        });
      }
    } catch (error) {
      console.error("Failed to post to financials:", error);
      handleApiError(error, `/api/billing/drafts/${invoiceId}/post`, "POST");
    } finally {
      setBillingActionLoading(false);
    }
  }, [dataMode, refetchBillingData, handleApiError]);

  const handleVoidInvoice = useCallback(async (invoiceId: number, reason: string) => {
    if (dataMode !== "live") return;
    try {
      setBillingActionLoading(true);
      await voidBillingInvoice(invoiceId, reason);
      await refetchBillingData();
    } catch (error) {
      console.error("Failed to void invoice:", error);
      handleApiError(error, `/api/billing/invoices/${invoiceId}/void`, "POST");
    } finally {
      setBillingActionLoading(false);
    }
  }, [dataMode, refetchBillingData, handleApiError]);

  // Legacy event listener (second instance) — no longer triggered from UI
  useEffect(() => {
    const handleEditMode = (event: Event) => {
      const customEvent = event as CustomEvent<{ projectId: string }>;
      if (customEvent.detail?.projectId === projectId) {
        handleEnterEditMode();
      }
    };

    window.addEventListener("project-edit-mode", handleEditMode);
    return () => window.removeEventListener("project-edit-mode", handleEditMode);
  }, [projectId, handleEnterEditMode]);

  // Load recent client IDs from localStorage
  useEffect(() => {
    const storedRecentClients = localStorage.getItem("recentClientIds");
    const storedRecentPMs = localStorage.getItem("recentProjectManagerIds");
    const storedRecentAMs = localStorage.getItem("recentAccountManagerIds");
    if (storedRecentPMs) {
      try {
        const parsed = JSON.parse(storedRecentPMs);
        setRecentProjectManagerIds(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        console.error("Failed to parse recent project manager IDs:", error);
        setRecentProjectManagerIds([]);
      }
    }
    if (storedRecentAMs) {
      try {
        const parsed = JSON.parse(storedRecentAMs);
        setRecentAccountManagerIds(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        console.error("Failed to parse recent account manager IDs:", error);
        setRecentAccountManagerIds([]);
      }
    }
    if (storedRecentClients) {
      try {
        const parsed = JSON.parse(storedRecentClients);
        setRecentClientIds(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        console.error("Failed to parse recent client IDs:", error);
        setRecentClientIds([]);
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Skeleton for Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4 rounded-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-3 w-full mb-1" />
                <Skeleton className="h-3 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Skeleton for Tabs */}
        <Card>
          <CardHeader>
            <div className="flex space-x-2">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
            <div className="pt-4">
              <Skeleton className="h-64 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <div className="text-muted-foreground">Project not found</div>
        <Button onClick={() => (onBack ? onBack() : navigate(ROUTES.PROJECTS))}>Back to Projects</Button>
      </div>
    );
  }

  const getEmployeeName = (employeeId: number) => {
    return employees.find((e) => e.id === employeeId)?.name || "Unknown";
  };

  const getStatusBadgeVariant = (
    status: TeamMember["status"],
    utilization?: number,
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "On Track":
        // Use different colors based on utilization
        if (utilization !== undefined) {
          if (utilization > 75) {
            return "default"; // Blue for higher utilization (75-85%)
          } else if (utilization > 50) {
            return "outline"; // Outline for medium utilization (50-75%)
          } else {
            return "secondary"; // Gray for low utilization (0-50%)
          }
        }
        return "default";
      case "At Risk":
        return "destructive";
      case "Over Allocated":
        return "destructive";
      default:
        return "default";
    }
  };

  const getStatusColor = (status: TeamMember["status"] | string): string => {
    // Normalize status to lowercase for comparison
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case "completed":
        return "text-neutral-600";
      case "on track":
      case "on-track":
        return "text-success-600";
      case "active":
      case "in-progress":
        return "text-info-600";
      case "at risk":
      case "at-risk":
        return "text-warning-600";
      case "over allocated":
      case "over-allocated":
        return "text-error-600";
      case "planning":
      case "on-hold":
        return "text-neutral-500";
      default:
        return "text-neutral-600";
    }
  };

  const handleRemoveTeamMember = async (member: TeamMember) => {
    // Only allow removal if no hours have been logged
    if (member.actualHours > 0) {
      toast.error("Cannot remove team member who has logged hours");
      return;
    }

    const dataMode = getDataMode();

    // In live mode, call the API to delete the resource allocation
    if (dataMode === "live" && member.id && member.id !== member.employeeId) {
      try {
        const memberId = typeof member.id === "number" ? member.id : parseInt(String(member.id), 10);
        await deleteResourceAllocation(memberId);
      } catch (error) {
        console.error("Failed to delete resource allocation:", error);
        toast.error("Failed to remove team member from project");
        return;
      }
    }

    // Remove the team member from local state
    setTeamMembers((prev) => prev.filter((tm) => tm.id !== member.id));

    // Update the budget data to reflect the removed allocation
    if (enrichedBudgetData?.resourceBudget?.byRole && member.allocatedHours > 0) {
      const updatedRoles = enrichedBudgetData.resourceBudget.byRole.map((r) => {
        if (r.role === member.role) {
          return {
            ...r,
            allocatedHours: Math.max(0, r.allocatedHours - member.allocatedHours),
          };
        }
        return r;
      });

      // Update local budget data
      setLocalBudgetData({
        ...enrichedBudgetData,
        resourceBudget: {
          ...enrichedBudgetData.resourceBudget,
          byRole: updatedRoles,
        },
      });
    }

    toast.success(`${member.name} removed from project`);
  };

  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const totalBillableHours = timeEntries.filter((t) => t.billable).reduce((sum, t) => sum + t.hours, 0);

  const totalNonBillableHours = timeEntries.filter((t) => !t.billable).reduce((sum, t) => sum + t.hours, 0);

  const totalExpenseAmount = expenses.reduce((sum, e) => sum + e.amount, 0);

  const completedMilestones = milestones.filter((m) => m.status === "completed").length;
  const overdueMilestones = milestones.filter((m) => m.status === "overdue").length;

  const activeResources = resourceAllocations.filter((r) => r.status === "confirmed").length;

  const pendingChangeRequests = changeRequests.filter((cr) => cr.status === "pending").length;
  const approvedChangeRequests = changeRequests.filter((cr) => cr.status === "approved").length;

  // Create default empty budget data for new projects that don't have budget data yet
  const getDefaultBudgetData = (): BudgetData => ({
    projectId: numericProjectId,
    projectName: project?.name || "New Project",
    projectType: (project?.type as "fixed-price" | "time-and-materials" | "retainer") || "fixed-price",
    summary: {
      revenue: {
        budget: 0,
        actual: 0,
        variance: 0,
        variancePercent: 0,
        progressPercent: 0,
        trend: "stable",
        status: "green",
      },
      costs: {
        budget: 0,
        actual: 0,
        variance: 0,
        variancePercent: 0,
        progressPercent: 0,
        trend: "stable",
        status: "green",
      },
      margin: { budgetedMargin: 0, actualMargin: 0, variance: 0, status: "green" },
      hours: { budget: 0, actual: 0, remaining: 0, progressPercent: 0, status: "green" },
      health: { score: "green", statusText: "On Track", factors: [] },
    },
    costBudget: {
      projectId: numericProjectId,
      categories: [],
      phases: [],
      totals: { budget: 0, actual: 0, committed: 0, available: 0, progressPercent: 0 },
    },
    salesBudget: {
      projectId: numericProjectId,
      projectType: (project?.type as "fixed-price" | "time-and-materials" | "retainer") || "fixed-price",
    },
    resourceBudget: {
      projectId: numericProjectId,
      summary: {
        totalBudget: 0,
        totalActual: 0,
        totalRemaining: 0,
        progressPercent: 0,
        billablePercent: 0,
        targetBillablePercent: 80,
        totalBudgetCost: 0,
        totalActualCost: 0,
      },
      byRole: [],
      byPerson: [],
      byPhase: [],
    },
    allBudgets: {
      projectId: numericProjectId,
      profitability: {
        revenue: { budget: 0, actual: 0, variance: 0 },
        directCosts: { budget: 0, actual: 0, variance: 0 },
        grossProfit: { budget: 0, actual: 0, variance: 0 },
        grossMargin: { budget: 0, actual: 0, variance: 0 },
        overhead: { budget: 0, actual: 0, variance: 0 },
        netProfit: { budget: 0, actual: 0, variance: 0 },
        netMargin: { budget: 0, actual: 0, variance: 0 },
      },
      burnRateData: [],
      forecast: {
        projectedFinalRevenue: 0,
        projectedFinalCost: 0,
        projectedFinalMargin: 0,
        revenueVariance: 0,
        costVariance: 0,
        marginVariance: 0,
      },
    },
    alerts: [],
    revisions: [],
    lastUpdated: new Date().toISOString(),
  });

  // Show full-screen edit page if editing cost budget
  if (editCostBudgetPageOpen) {
    const budgetDataToUse = budgetData || getDefaultBudgetData();
    // Override projectName with actual project name/code instead of the placeholder from the API
    const projectDisplayName = project?.code
      ? `${project.code} - ${project.name}`
      : project?.name || budgetDataToUse.projectName;
    const budgetDataWithCorrectName = {
      ...budgetDataToUse,
      projectName: projectDisplayName,
    };
    return (
      <EditCostBudgetPage
        budgetData={budgetDataWithCorrectName}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        onSave={async (updatedBudget) => {
          console.log("[ProjectDetails] Saving cost budget:", updatedBudget);

          try {
            // Update the budget data
            const result = await updateBudget(project.id, updatedBudget);

            if (result.success) {
              toast.success("Cost budget saved successfully");

              // Close the edit page
              setEditCostBudgetPageOpen(false);

              // Refetch the budget data to show updated values
              refetchBudget();
            } else {
              toast.error("Failed to save cost budget");
            }
          } catch (error) {
            console.error("[ProjectDetails] Error saving budget:", error);
            toast.error("Failed to save cost budget");
          }
        }}
        onCancel={() => setEditCostBudgetPageOpen(false)}
      />
    );
  }

  // Show full-screen edit page if editing sales budget (T&M projects only)
  // Use enrichedBudgetData if available, otherwise use default empty budget data for new projects
  if (editSalesBudgetPageOpen) {
    const budgetDataToUse = enrichedBudgetData || getDefaultBudgetData();
    const projectDisplayName = project?.code
      ? `${project.code} - ${project.name}`
      : project?.name || `Project ${projectId}`;
    const budgetDataWithCorrectName = {
      ...budgetDataToUse,
      projectName: projectDisplayName,
    };
    return (
      <EditSalesBudgetPage
        budgetData={budgetDataWithCorrectName}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        onSave={async (updatedBudget) => {
          console.log("[ProjectDetails] Saving sales budget:", updatedBudget);

          const result = await updateBudget(project.id, updatedBudget);

          if (result.success) {
            toast.success("Sales budget saved successfully");

            // Close the edit page
            setEditSalesBudgetPageOpen(false);

            // Refetch the budget data to show updated values
            refetchBudget();
          } else {
            throw new Error(result.error || "Failed to save sales budget");
          }
        }}
        onCancel={() => setEditSalesBudgetPageOpen(false)}
      />
    );
  }

  // Show full-screen edit page if editing resource budget
  // Use enrichedBudgetData if available, otherwise use default empty budget data for new projects
  if (editResourceBudgetPageOpen) {
    const budgetDataToUse = enrichedBudgetData || getDefaultBudgetData();
    // Override projectName with actual project name/code instead of the placeholder from the API
    const resourceProjectDisplayName = project?.code
      ? `${project.code} - ${project.name}`
      : project?.name || budgetDataToUse.projectName;
    const resourceBudgetDataWithCorrectName = {
      ...budgetDataToUse,
      projectName: resourceProjectDisplayName,
    };
    return (
      <EditResourceBudgetPage
        budgetData={resourceBudgetDataWithCorrectName}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        onSave={async (updatedBudget) => {
          console.log("[ProjectDetails] Saving resource budget:", updatedBudget);

          try {
            // Update the budget data
            const result = await updateBudget(project.id, updatedBudget);

            if (result.success) {
              toast.success("Resource budget saved successfully");

              // Close the edit page
              setEditResourceBudgetPageOpen(false);

              // Refetch the budget data to show updated values
              refetchBudget();
            } else {
              toast.error("Failed to save resource budget");
            }
          } catch (error) {
            console.error("[ProjectDetails] Error saving budget:", error);
            toast.error("Failed to save resource budget");
          }
        }}
        onCancel={() => setEditResourceBudgetPageOpen(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => (onBack ? onBack() : navigate(ROUTES.PROJECTS))}
            className="h-8 w-8 mt-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            {/* Inline-editable project name */}
            {isNameEditing ? (
              <div className="space-y-1">
                <input
                  autoFocus
                  type="text"
                  value={inlineNameValue}
                  onChange={(e) => setInlineNameValue(e.target.value)}
                  onBlur={() => void handleNameSave()}
                  onKeyDown={handleNameKeyDown}
                  maxLength={100}
                  className="w-full text-xl font-bold bg-transparent border-0 border-b-2 border-primary outline-none focus:ring-0 px-0 py-0.5 leading-tight"
                />
                {/* Validation feedback while editing */}
                {inlineNameValue.trim().length > 0 && inlineNameValue.trim().length < 3 && (
                  <p className="text-xs text-error-600">Project name must be at least 3 characters.</p>
                )}
                {projectNameHasIssuedInvoices && inlineNameValue.trim() !== project.name && (
                  <p className="flex items-center gap-1 text-xs text-amber-700">
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    This name may already appear on sent invoices. Changing it won&apos;t update existing documents.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">Press Enter to save · Esc to cancel</p>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleNameEditStart}
                className="group flex items-center gap-1.5 text-left"
                title="Click to edit project name"
              >
                <h1 className="text-xl font-bold leading-tight truncate">
                  {project.code ? `${project.code} - ${project.name}` : project.name}
                </h1>
                <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
            )}

            {/* Inline-editable description */}
            {isDescEditing ? (
              <div className="mt-1 space-y-1">
                <textarea
                  autoFocus
                  value={inlineDescValue}
                  onChange={(e) => setInlineDescValue(e.target.value)}
                  onBlur={() => void handleDescSave()}
                  onKeyDown={handleDescKeyDown}
                  maxLength={500}
                  rows={2}
                  className="w-full text-sm text-muted-foreground bg-transparent border-0 border-b border-primary outline-none focus:ring-0 px-0 py-0.5 resize-none leading-relaxed"
                  placeholder="Add a description..."
                />
                <p className="text-xs text-muted-foreground">Ctrl+Enter to save · Esc to cancel</p>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleDescEditStart}
                className="group flex items-center gap-1 mt-0.5 text-left max-w-prose"
                title="Click to edit description"
              >
                <span className="text-sm text-muted-foreground leading-relaxed">
                  {project.description || <span className="italic opacity-60">Add a description…</span>}
                </span>
                <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity shrink-0 mt-0.5" />
              </button>
            )}
          </div>
        </div>
        {/* Inline Status Select + Health Badge */}
        <div className="flex items-center gap-3">
          {/* Project Health Badge — read-only, system-derived */}
          {project.health && (() => {
            const healthLabel = project.health === 'green' ? '● Healthy' : project.health === 'amber' ? '● At Risk' : '● Critical';
            return (
              <Badge
                className={cn(
                  "h-8 rounded-full px-3 text-sm font-medium border cursor-default select-none inline-flex items-center",
                  getHealthBadgeColor(project.health)
                )}
              >
                {healthLabel}
              </Badge>
            );
          })()}
          <Select
            value={String(project.statusId)}
            onValueChange={handleInlineStatusChange}
            disabled={isStatusSaving}
          >
            <SelectTrigger
              className={cn(
                "h-8 w-auto min-w-[130px] border font-medium text-sm rounded-full px-3 gap-2 focus:ring-1",
                project.statusId === 1 && "bg-neutral-100 border-neutral-300 text-neutral-700 hover:bg-neutral-200",
                project.statusId === 2 && "bg-info-100 border-info-300 text-info-700 hover:bg-info-200",
                project.statusId === 3 && "bg-warning-100 border-warning-300 text-warning-700 hover:bg-warning-200",
                project.statusId === 5 && "bg-error-100 border-error-300 text-error-700 hover:bg-error-200",
                project.statusId === 6 && "bg-success-100 border-success-300 text-success-700 hover:bg-success-200",
              )}
            >
              {isStatusSaving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : null}
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(selectableStatuses.length > 0
                ? selectableStatuses
                : Object.entries(PROJECT_STATUS_LABELS).map(([id, label]) => ({
                    id: Number(id),
                    name: label,
                  }))
              ).map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

        </div>
      </div>

      {/* Tabs */}
      <TooltipProvider>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="financials" className="flex items-center gap-2">
              <Landmark className="h-4 w-4" />
              Financials
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Billing
            </TabsTrigger>
            {isBudgetingEnabled && budgetPermissions.settings.permissions.salesBudgetEnabled && (
              <TabsTrigger value="sales-budget" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Sales Budget
              </TabsTrigger>
            )}
            {isBudgetingEnabled && budgetPermissions.settings.permissions.costBudgetEnabled && (
              <TabsTrigger value="cost-budget" className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />
                Cost Budget
              </TabsTrigger>
            )}
            {isBudgetingEnabled && isResourceBudgetEnabled && (
              <TabsTrigger value="resource-budget" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Resource Budget
              </TabsTrigger>
            )}
            <TabsTrigger value="cost-centres" className="flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Cost Centres
            </TabsTrigger>
            <TabsTrigger value="purchases" className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Purchases
            </TabsTrigger>
            <TabsTrigger value="time" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Time
            </TabsTrigger>
            <TabsTrigger value="expenses" className="flex items-center gap-2">
              <Banknote className="h-4 w-4" />
              Expenses
            </TabsTrigger>
            <TabsTrigger value="team" className="flex items-center gap-2">
              <UsersRound className="h-4 w-4" />
              Team
            </TabsTrigger>
            {schedulerConnected && (
              <TabsTrigger value="resourcing" className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4" />
                Resourcing
              </TabsTrigger>
            )}
            <TabsTrigger value="milestones" className="flex items-center gap-2">
              <MilestoneIcon className="h-4 w-4" />
              Milestones
            </TabsTrigger>
            <TabsTrigger value="changes" className="flex items-center gap-2">
              <GitPullRequestArrow className="h-4 w-4" />
              Change Requests
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Audit
            </TabsTrigger>
          </TabsList>

          {/* Scrollable Tab Content Container */}
          <div ref={tabsContainerRef}>
            <ScrollableTabsContainer height={`${tabsContainerHeight}px`}>
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
                {/* Key Metrics - Always Visible */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Key Metrics</h2>
                  </div>
                  <div>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5 mb-4">
                      {/* Sales Variance card */}
                      {(() => {
                        const actualRevenue = project.actualRevenue ?? 0;
                        const budgetRevenue = project.budgetRevenue ?? 0;
                        const invoiced = project.type === 'fixed-price' ? fixedFeeInvoicedToDate : actualRevenue;
                        const salesVariance = project.salesVariance ?? (budgetRevenue - actualRevenue);
                        const isAboveBudget = actualRevenue >= budgetRevenue;
                        return (
                          <Card>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-sm font-medium">Sales Variance</CardTitle>
                            </CardHeader>
                            <CardContent>
                              {/* Hero */}
                              <div className="flex items-center gap-2">
                                <div className="text-2xl font-bold">
                                  <SensitiveData>{formatCurrency(actualRevenue)}</SensitiveData>
                                </div>
                                {isAboveBudget ? (
                                  <TrendingUp className="h-4 w-4 text-success-500" />
                                ) : (
                                  <TrendingDown className="h-4 w-4 text-error-500" />
                                )}
                              </div>
                              <button
                                onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=S`)}
                                className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity mt-0.5 text-left"
                              >
                                Actual Sales
                              </button>

                              {/* Secondary rows */}
                              <div className="mt-3 space-y-1.5">
                                <div className="flex justify-between text-xs">
                                  <button
                                    onClick={() => setActiveTab('sales-budget')}
                                    className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity whitespace-nowrap"
                                  >Budget Sales</button>
                                  <SensitiveData>
                                    <span className="font-medium">{formatCurrency(budgetRevenue)}</span>
                                  </SensitiveData>
                                </div>
                                <div className="flex justify-between text-xs">
                                  <span className="text-muted-foreground">Invoiced</span>
                                  <SensitiveData>
                                    <span className="font-medium">{formatCurrency(invoiced)}</span>
                                  </SensitiveData>
                                </div>
                                <div className="flex justify-between text-xs border-t pt-1.5 mt-1">
                                  <span className="text-muted-foreground">
                                    {salesVariance >= 0 ? "Remaining to Invoice" : "Over Billed"}
                                  </span>
                                  <SensitiveData>
                                    <span className={`font-medium ${salesVariance >= 0 ? "text-success-600" : "text-error-600"}`}>
                                      {formatCurrency(Math.abs(salesVariance))}
                                    </span>
                                  </SensitiveData>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })()}

                      {/* Cost Variance card */}
                      {(() => {
                        const actualCosts = project.actualCosts ?? 0;
                        const budgetCosts = (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0);
                        const costVariance = project.costVariance ?? (budgetCosts - actualCosts);
                        const costVariancePct = budgetCosts !== 0 ? ((actualCosts - budgetCosts) / budgetCosts) * 100 : 0;
                        const isOverBudget = actualCosts > budgetCosts;
                        return (
                          <Card>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-sm font-medium">Cost Variance</CardTitle>
                            </CardHeader>
                            <CardContent>
                              {/* Hero */}
                              <div className="flex items-center gap-2">
                                <div className="text-2xl font-bold">
                                  <SensitiveData>{formatCurrency(actualCosts)}</SensitiveData>
                                </div>
                                {isOverBudget ? (
                                  <TrendingUp className="h-4 w-4 text-error-500" />
                                ) : (
                                  <TrendingDown className="h-4 w-4 text-success-500" />
                                )}
                              </div>
                              <button
                                onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=C`)}
                                className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity mt-0.5"
                              >Actual Costs</button>

                              {/* Secondary rows */}
                              <div className="mt-3 space-y-1.5 text-xs">
                                <div className="flex justify-between">
                                  <button
                                    onClick={() => setActiveTab('cost-budget')}
                                    className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity whitespace-nowrap"
                                  >Budget Costs</button>
                                  <SensitiveData>
                                    <span className="font-medium">{formatCurrency(budgetCosts)}</span>
                                  </SensitiveData>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Variance (%)</span>
                                  <span className={`font-medium ${costVariancePct > 0 ? 'text-error-600' : costVariancePct < 0 ? 'text-success-600' : ''}`}>
                                    {costVariancePct >= 0 ? '+' : ''}{formatPercent(costVariancePct)}
                                  </span>
                                </div>
                                <div className="flex justify-between border-t pt-1.5 mt-1">
                                  <span className="text-muted-foreground">
                                    {costVariance >= 0 ? "Under Budget" : "Over Budget"}
                                  </span>
                                  <SensitiveData>
                                    <span className={`font-medium ${costVariance >= 0 ? "text-success-600" : "text-error-600"}`}>
                                      {formatCurrency(Math.abs(costVariance))}
                                    </span>
                                  </SensitiveData>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })()}

                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                          <CardTitle className="text-sm font-medium">Gross Margin</CardTitle>
                          <BarChart3 className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                          {(() => {
                            // Task 23 — use company GP Calculation Method
                            const _gpResult = calculateGP(
                              { revenue: project.actualSales, totalDirectCosts: project.actualCosts },
                              getFinanceSettings().gpCalculationMethod
                            );
                            return (
                          <div className="text-2xl font-bold">
                            <SensitiveData>
                              <span className={_gpResult.grossProfit >= 0 ? 'text-success-600' : 'text-error-600'}>
                                {formatPercent(_gpResult.grossMarginPct)}
                              </span>
                            </SensitiveData>
                          </div>
                          );
                          })()}
                          <p className="text-xs text-muted-foreground mt-1">
                            Gross Profit:{" "}
                            <SensitiveData>
                              {formatCurrency(project.actualSales - project.actualCosts)}
                            </SensitiveData>
                          </p>
                        </CardContent>
                      </Card>

                      {/* Hour Utilisation card */}
                      {(() => {
                        const budgetHours = project.budgetHours ?? 0;
                        const actualHours = project.actualHours ?? 0;
                        const utilisationPct = budgetHours > 0 ? (actualHours / budgetHours) * 100 : 0;
                        const utilisationColor = utilisationPct > 100 ? 'text-error-600' : utilisationPct > 85 ? 'text-warning-600' : 'text-success-600';
                        const trendIcon = utilisationPct > 100
                          ? <TrendingUp className="h-4 w-4 text-error-500" />
                          : utilisationPct > 85
                            ? <TrendingUp className="h-4 w-4 text-warning-500" />
                            : <TrendingDown className="h-4 w-4 text-success-500" />;
                        const hoursVariance = budgetHours - actualHours;
                        const hoursVariancePct = budgetHours > 0 ? ((actualHours - budgetHours) / budgetHours) * 100 : 0;
                        // Completion context: same as autoCalculatedCompletionPercent — hours as a proxy for progress
                        const impliedCompletion = Math.round(utilisationPct);
                        const completionPctForHours = project.type === 'fixed-price' && project.billingTriggerType === 'percent-complete'
                          ? effectiveCompletionPercent
                          : project.completionPercent ?? 0;
                        const impliedGap = impliedCompletion - completionPctForHours;
                        return (
                          <Card>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-sm font-medium">Hour Utilisation</CardTitle>
                            </CardHeader>
                            <CardContent>
                              {/* Hero */}
                              <div className="flex items-center gap-2">
                                <div className={`text-2xl font-bold ${utilisationColor}`}>
                                  {utilisationPct.toFixed(1)}%
                                </div>
                                {trendIcon}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">Of budget hours used</p>

                              {/* Secondary rows */}
                              <div className="mt-3 space-y-1.5 text-xs">
                                <div className="flex justify-between">
                                  <button
                                    onClick={() => setActiveTab('resource-budget')}
                                    className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity whitespace-nowrap"
                                  >
                                    Budget Hours
                                  </button>
                                  <span className="font-medium">{budgetHours.toLocaleString()} hrs</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Actual Hours</span>
                                  <span className="font-medium">{actualHours.toLocaleString()} hrs</span>
                                </div>
                                <div className="flex justify-between border-t pt-1.5 mt-1">
                                  <span className="text-muted-foreground">
                                    {hoursVariance >= 0 ? 'Remaining' : 'Over Budget'}
                                  </span>
                                  <span className={`font-medium ${hoursVariance >= 0 ? 'text-success-600' : 'text-error-600'}`}>
                                    {Math.abs(hoursVariance).toLocaleString()} hrs
                                    {hoursVariancePct !== 0 && (
                                      <span className="ml-1 text-muted-foreground font-normal">
                                        ({hoursVariancePct >= 0 ? '+' : ''}{hoursVariancePct.toFixed(1)}%)
                                      </span>
                                    )}
                                  </span>
                                </div>
                                {/* Implied completion — cross-references Completion card */}
                                <div className="flex justify-between items-center border-t pt-1.5 mt-1">
                                  <span className="text-muted-foreground">Implied completion</span>
                                  <span className={`font-medium ${impliedGap > 10 ? 'text-error-600' : impliedGap > 5 ? 'text-warning-600' : 'text-success-600'}`}>
                                    {impliedCompletion}%
                                    {Math.abs(impliedGap) > 5 && (
                                      <span className="ml-1 text-muted-foreground font-normal">
                                        ({impliedGap > 0 ? '+' : ''}{impliedGap}% vs actual)
                                      </span>
                                    )}
                                  </span>
                                </div>
                                {/* View Time Entries link — lives here as time entries are the source of hours data */}
                                <div className="flex justify-between items-center border-t pt-1.5 mt-1">
                                  <button
                                    className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity font-medium whitespace-nowrap"
                                    onClick={() => setActiveTab('time')}
                                  >
                                    View Time Entries
                                  </button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })()}

                      {(() => {
                        const completionPct = project.type === 'fixed-price' && project.billingTriggerType === 'percent-complete'
                          ? effectiveCompletionPercent
                          : project.completionPercent;
                        const timeElapsed = project.startDate && project.endDate
                          ? Math.min(100, Math.max(0, Math.round(
                              (new Date().getTime() - new Date(project.startDate).getTime()) /
                              (new Date(project.endDate).getTime() - new Date(project.startDate).getTime()) * 100
                            )))
                          : null;
                        const timeGap = timeElapsed !== null ? timeElapsed - completionPct : 0;
                        const hoursGap = autoCalculatedCompletionPercent - completionPct;
                        const isAtRisk = timeGap > 20 || hoursGap > 20;
                        const isWarning = !isAtRisk && (timeGap > 10 || hoursGap > 10);
                        const heroColor = isAtRisk ? 'text-destructive' : isWarning ? 'text-warning' : 'text-success';
                        const timeColor = timeGap > 20 ? 'text-destructive' : timeGap > 10 ? 'text-warning' : 'text-success';
                        const hoursColor = hoursGap > 20 ? 'text-destructive' : hoursGap > 10 ? 'text-warning' : 'text-success';
                        const isFixedPctComplete = project.type === 'fixed-price' && project.billingTriggerType === 'percent-complete';
                        const isManualOverride = isFixedPctComplete && project.completionPercentOverride !== undefined;
                        return (
                          <Card>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-sm font-medium">Completion</CardTitle>
                            </CardHeader>
                            <CardContent>
                              {/* Hero */}
                              <div className="flex items-center gap-2">
                                <div className={`text-2xl font-bold ${heroColor}`}>
                                  {isFixedPctComplete ? `${effectiveCompletionPercent}%` : formatPercent(project.completionPercent, 0)}
                                </div>
                                <Activity className={`h-4 w-4 ${heroColor}`} />
                                {/* Inline edit pencil for fixed-price % complete */}
                                {isFixedPctComplete && !completionPercentEditing && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          onClick={handleCompletionPercentEditStart}
                                          className="ml-1 p-1 rounded hover:bg-muted opacity-40 hover:opacity-100 transition-opacity"
                                          aria-label="Edit completion percentage"
                                        >
                                          <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent>Override completion %</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>

                              {/* Inline edit input */}
                              {isFixedPctComplete && completionPercentEditing && (
                                <div className="flex items-center gap-2 mt-1">
                                  <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={completionPercentInputValue}
                                    onChange={(e) => setCompletionPercentInputValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleCompletionPercentEditConfirm();
                                      if (e.key === 'Escape') handleCompletionPercentEditCancel();
                                    }}
                                    className="w-20 h-8 text-lg font-bold"
                                    autoFocus
                                  />
                                  <span className="text-lg font-bold">%</span>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleCompletionPercentEditConfirm}>Save</Button>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleCompletionPercentEditCancel}>Cancel</Button>
                                </div>
                              )}

                              <p className="text-xs text-muted-foreground mt-0.5">
                                {isManualOverride ? 'Manual override' : isFixedPctComplete ? 'Auto · based on approved hours' : 'Based on hours logged'}
                              </p>

                              {/* Secondary rows */}
                              <div className="mt-3 space-y-1.5">
                                {/* Hours signal — same figure as Hours Utilisation card's "Implied completion" */}
                                <div className="flex justify-between items-center text-xs">
                                  <button
                                    className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity whitespace-nowrap"
                                    onClick={() => setActiveTab('resource-budget')}
                                  >
                                    Hours utilisation
                                  </button>
                                  <span className={`font-medium ${hoursColor}`}>
                                    {autoCalculatedCompletionPercent}%
                                    {hoursGap > 5 && <span className="text-muted-foreground font-normal"> ({hoursGap}% behind)</span>}
                                    {hoursGap < -5 && <span className="text-muted-foreground font-normal"> ({Math.abs(hoursGap)}% ahead)</span>}
                                  </span>
                                </div>

                                {/* Timeline signal: how much of the project timeline has elapsed */}
                                {timeElapsed !== null && (
                                  <div className="flex justify-between items-center text-xs">
                                    <span className="text-muted-foreground">Timeline elapsed</span>
                                    <span className={`font-medium ${timeColor}`}>
                                      {timeElapsed}%
                                      {timeGap > 5 && <span className="text-muted-foreground font-normal"> ({timeGap}% gap)</span>}
                                      {timeGap < -5 && <span className="text-muted-foreground font-normal"> ({Math.abs(timeGap)}% ahead)</span>}
                                    </span>
                                  </div>
                                )}

                                {/* Separator + overall health */}
                                <div className="border-t pt-1.5 space-y-1.5">
                                  <div className="flex justify-between items-center text-xs">
                                    <span className={`font-medium ${heroColor}`}>
                                      {isAtRisk ? '⚠ At risk' : isWarning ? '△ Needs attention' : '✓ On track'}
                                    </span>
                                    <span className="text-muted-foreground">
                                      {timeElapsed !== null && timeGap > 5
                                        ? `${timeGap}% behind schedule`
                                        : timeElapsed !== null && timeGap < -5
                                        ? `${Math.abs(timeGap)}% ahead of schedule`
                                        : hoursGap > 5
                                        ? `${hoursGap}% behind on hours`
                                        : hoursGap < -5
                                        ? `${Math.abs(hoursGap)}% ahead on hours`
                                        : 'All signals green'}
                                    </span>
                                  </div>

                                  {/* Manual override suggestion */}
                                  {isFixedPctComplete && isManualOverride && autoCalculatedCompletionPercent !== effectiveCompletionPercent && (
                                    <div className="flex justify-between items-center text-xs bg-muted/50 rounded px-1.5 py-1">
                                      <span className="text-muted-foreground">Hours suggest {autoCalculatedCompletionPercent}%</span>
                                      <button
                                        className="text-primary underline underline-offset-2 hover:no-underline font-medium"
                                        onClick={() => handleCompletionModeChange('based-on-time')}
                                      >
                                        Use this instead
                                      </button>
                                    </div>
                                  )}

                                  {/* Audit History link — only relevant for fixed-price % complete manual overrides */}
                                  {project.type === 'fixed-price' && project.billingTriggerType === 'percent-complete' && completionAuditEntries.length > 0 && (
                                    <div className="flex justify-between items-center text-xs">
                                      <button
                                        className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity font-medium whitespace-nowrap"
                                        onClick={() => {
                                          setAuditCategoryFilter('completion');
                                          setActiveTab('audit');
                                        }}
                                      >
                                        View Audit History
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* ── ROW 1: Project Information | Client Information ── */}
                <div className="grid gap-4 md:grid-cols-2">

                  {/* LEFT of Row 1 — Project Information */}
                  <div className="flex flex-col gap-4">
                  {/* Project Information — card-level edit with pencil icon */}
                  <Card className={isProjectInfoEditMode ? "ring-2 ring-info-200 bg-info-50/30" : ""}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Project Information</CardTitle>
                        {!isProjectInfoEditMode && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={handleEnterEditMode}
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                  aria-label="Edit project information"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="left">Edit project information</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {isProjectInfoEditMode ? (
                        <>
                          {/* Project Type */}
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <Label htmlFor="edit-type">Project Type</Label>
                              {isTypeLocked && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-muted-foreground cursor-help">
                                        <Lock className="h-3.5 w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[260px] text-xs">
                                      There are outstanding sent invoices. Project type cannot be changed until all invoices are paid or removed.
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            {isTypeLocked ? (
                              <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                                {formatProjectType(project.type)}
                              </div>
                            ) : (
                              <Select
                                value={editFormData.type || project.type}
                                onValueChange={(value) => handleFieldChange("type", value)}
                              >
                                <SelectTrigger id="edit-type">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="fixed-price">Fixed Price</SelectItem>
                                  <SelectItem value="time-and-materials">Time & Materials</SelectItem>
                                  <SelectItem value="retainer">Retainer</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            {/* Tier-specific warnings */}
                            {!isTypeLocked && wipInvoiceTier === 'draft-exist' && (
                              <p className="flex items-start gap-1.5 text-xs text-error-700">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                Draft WIP invoices must be removed before changing project type — they&apos;ve already captured the current billing type.
                              </p>
                            )}
                            {!isTypeLocked && wipInvoiceTier === 'all-paid' && (editFormData.type !== undefined && editFormData.type !== project.type) && (
                              <p className="flex items-start gap-1.5 text-xs text-amber-700">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                All existing invoices are paid. You can proceed but this change affects future billing.
                              </p>
                            )}
                            {!isTypeLocked && wipInvoiceTier === 'none' && (editFormData.type !== undefined && editFormData.type !== project.type) && (
                              <p className="flex items-start gap-1.5 text-xs text-amber-700">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                Changing project type will affect billing setup. Please review billing configuration after saving.
                              </p>
                            )}
                          </div>
                          <Separator />

                          {/* Start Date */}
                          <div className="space-y-1.5">
                            <Label htmlFor="edit-start-date">Start Date</Label>
                            <Input
                              id="edit-start-date"
                              type="date"
                              value={formatDateForInput(editFormData.startDate || project.startDate)}
                              onChange={(e) => handleFieldChange("startDate", e.target.value)}
                            />
                            {startDateMovedPastTimesheets && (
                              <p className="flex items-start gap-1.5 text-xs text-amber-700">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                Moving the start date forward may orphan time entries submitted before this date.
                              </p>
                            )}
                          </div>
                          <Separator />

                          {/* End Date */}
                          <div className="space-y-1.5">
                            <Label htmlFor="edit-end-date">End Date</Label>
                            <Input
                              id="edit-end-date"
                              type="date"
                              value={formatDateForInput(editFormData.endDate || project.endDate)}
                              onChange={(e) => handleFieldChange("endDate", e.target.value)}
                              className={endDateBeforeStart ? "border-error-500 focus-visible:ring-error-500" : ""}
                            />
                            {endDateBeforeStart && (
                              <p className="flex items-start gap-1.5 text-xs text-error-700">
                                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                End date cannot be before the start date.
                              </p>
                            )}
                            {!endDateBeforeStart && endDateShortenedPastAllocations && (
                              <p className="flex items-start gap-1.5 text-xs text-amber-700">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                This end date is before some existing resource bookings. Review the Resourcing tab after saving.
                              </p>
                            )}
                          </div>
                          <Separator />

                          {/* Project Manager */}
                          <div className="space-y-1.5">
                            <Label htmlFor="edit-project-manager">Project Manager</Label>
                            <EmployeeCombobox
                              employees={employees}
                              value={editFormData.projectManagerId !== undefined
                                ? String(editFormData.projectManagerId)
                                : projectManager?.id !== undefined ? String(projectManager.id) : undefined}
                              onValueChange={handleProjectManagerChange}
                              placeholder="Select a project manager..."
                              recentEmployeeIds={recentProjectManagerIds}
                              maxRecentEmployees={5}
                            />
                          </div>
                          <Separator />

                          {/* Revenue Recognition */}
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <Label htmlFor="edit-rev-rec">Revenue Recognition</Label>
                              {isRevRecLocked && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-muted-foreground cursor-help">
                                        <Lock className="h-3.5 w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[260px] text-xs">
                                      Revenue recognition cannot be changed once revenue has been recognised. Contact your Finance team.
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            {isRevRecLocked ? (
                              <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                                {formatRevenueRecognition(project.revenueRecognitionMethod)}
                              </div>
                            ) : (
                              <Select
                                value={editFormData.revenueRecognitionMethod || project.revenueRecognitionMethod}
                                onValueChange={(value) => handleFieldChange("revenueRecognitionMethod", value)}
                              >
                                <SelectTrigger id="edit-rev-rec">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="percentage-of-completion">Percentage of Completion</SelectItem>
                                  <SelectItem value="completed-contract">Completed Contract</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </div>

                          {/* Card-level Save / Cancel */}
                          <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveChanges}
                              disabled={
                                !hasUnsavedChanges ||
                                isSaveBlocked ||
                                wipInvoiceTier === 'draft-exist'
                              }
                            >
                              Save Changes
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Project Type</span>
                            <span className="font-medium">{formatProjectType(project.type)}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Start Date</span>
                            <span className="font-medium">{formatDate(project.startDate)}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between items-start">
                            <span className="text-muted-foreground">End Date</span>
                            <div className="text-right">
                              <span className="font-medium">{formatDate(project.endDate)}</span>
                              {project.endDate && project.statusId !== 6 && (() => {
                                const daysRemaining = Math.ceil(
                                  (new Date(project.endDate).getTime() - new Date().setHours(0,0,0,0)) / 86_400_000
                                );
                                if (daysRemaining < 0) {
                                  return (
                                    <p className="flex items-center justify-end gap-1 text-xs text-red-600 mt-0.5">
                                      <AlertCircle className="h-3 w-3 shrink-0" />
                                      {Math.abs(daysRemaining)} days overdue
                                    </p>
                                  );
                                } else if (daysRemaining < 15) {
                                  return (
                                    <p className="flex items-center justify-end gap-1 text-xs text-red-600 mt-0.5">
                                      <AlertCircle className="h-3 w-3 shrink-0" />
                                      {daysRemaining} days remaining
                                    </p>
                                  );
                                } else if (daysRemaining <= 30) {
                                  return (
                                    <p className="flex items-center justify-end gap-1 text-xs text-amber-600 mt-0.5">
                                      <Clock className="h-3 w-3 shrink-0" />
                                      {daysRemaining} days remaining
                                    </p>
                                  );
                                } else {
                                  return (
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      {daysRemaining} days remaining
                                    </p>
                                  );
                                }
                              })()}
                            </div>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Project Manager</span>
                            <span className="font-medium">{projectManager?.name || "Unknown"}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-1.5">
                              <span className="text-muted-foreground">Revenue Recognition</span>
                              {isRevRecLocked && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-muted-foreground cursor-help">
                                        <Lock className="h-3.5 w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[260px] text-xs">
                                      Revenue recognition cannot be changed once revenue has been recognised. Contact your Finance team.
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            <span className="font-medium">{formatRevenueRecognition(project.revenueRecognitionMethod)}</span>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                  </div>{/* end LEFT of Row 1 — Project Information */}

                  {/* RIGHT of Row 1 — Client Information */}
                  <div className="flex flex-col gap-4">
                    <Card className={isClientInfoEditMode ? "ring-2 ring-info-200 bg-info-50/30" : ""}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Client Information</CardTitle>
                        <div className="flex items-center gap-2">
                          {!isClientInfoEditMode && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={handleEnterClientInfoEditMode}
                                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    aria-label="Edit client information"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="left">Edit client information</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* On Stop / Suspended warning */}
                      {client && (client.accountStatus === 'on-stop' || client.accountStatus === 'suspended') && (
                        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                          <span>
                            <span className="font-semibold">This account is ON STOP in the Finance System.</span>{' '}
                            Contact Finance before proceeding with invoicing or scope changes.
                          </span>
                        </div>
                      )}

                      {isClientInfoEditMode ? (
                        <>
                          {/* Client — editable in edit mode */}
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <Label htmlFor="client-name-edit">Client</Label>
                              {isClientChangeLocked && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-muted-foreground cursor-help">
                                        <Lock className="h-3.5 w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[260px] text-xs">
                                      Client cannot be changed — this project has financial transactions.
                                      Contact your Finance team to process a client transfer in the Finance System.
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            <ClientCombobox
                              clients={clients}
                              value={client?.id !== undefined ? String(client.id) : undefined}
                              onValueChange={handleClientChange}
                              placeholder="Select a client..."
                              recentClientIds={recentClientIds}
                              maxRecentClients={5}
                              disabled={isClientChangeLocked}
                            />
                          </div>

                          <Separator />

                          {/* Account Manager — editable in edit mode */}
                          <div className="space-y-1.5">
                            <Label htmlFor="edit-account-manager">Account Manager</Label>
                            <EmployeeCombobox
                              employees={employees}
                              value={
                                clientInfoEditData.accountManagerId !== undefined
                                  ? String(clientInfoEditData.accountManagerId)
                                  : accountManager?.id !== undefined
                                    ? String(accountManager.id)
                                    : undefined
                              }
                              onValueChange={handleAccountManagerChange}
                              placeholder="Select an account manager..."
                              recentEmployeeIds={recentAccountManagerIds}
                              maxRecentEmployees={5}
                            />
                          </div>

                          <Separator />

                          {/* Read-only fields in edit mode */}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Contact</span>
                            <span className="font-medium">{client?.contactName || 'Unknown'}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Email</span>
                            {client?.contactEmail ? (
                              <a
                                href={`mailto:${client.contactEmail}`}
                                className="font-medium text-sm text-primary hover:underline underline-offset-2 transition-colors"
                              >
                                {client.contactEmail}
                              </a>
                            ) : (
                              <span className="font-medium text-sm">Unknown</span>
                            )}
                          </div>

                          <Separator />

                          {/* Card-level Save / Cancel */}
                          <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" size="sm" onClick={handleCancelClientInfoEdit}>
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveClientInfoChanges}
                              disabled={!hasClientInfoUnsavedChanges}
                            >
                              Save Changes
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Client Name — read-only view */}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Client</span>
                            {client ? (
                              <button
                                type="button"
                                onClick={() => openClientPanel(client.id)}
                                className="font-medium text-primary hover:underline underline-offset-2 transition-colors"
                              >
                                {client.code ? `${client.code} - ${client.name}` : client.name}
                              </button>
                            ) : (
                              <span className="font-medium">Unknown</span>
                            )}
                          </div>
                          <Separator />

                          {/* Account Manager — read-only view */}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Account Manager</span>
                            <span className="font-medium">
                              {accountManager?.name || <span className="text-muted-foreground italic text-sm">Not assigned</span>}
                            </span>
                          </div>
                          <Separator />

                          {/* Contact — read-only view */}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Contact</span>
                            <span className="font-medium">{client?.contactName || 'Unknown'}</span>
                          </div>
                          <Separator />

                          {/* Email — read-only view */}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Email</span>
                            {client?.contactEmail ? (
                              <a
                                href={`mailto:${client.contactEmail}`}
                                className="font-medium text-sm text-primary hover:underline underline-offset-2 transition-colors"
                              >
                                {client.contactEmail}
                              </a>
                            ) : (
                              <span className="font-medium text-sm">Unknown</span>
                            )}
                          </div>
                        </>
                      )}
                    </CardContent>
                    </Card>

                  </div>{/* end RIGHT of Row 1 — Client Information */}
                </div>{/* end Row 1 */}

                {/* ── ROW 2: Project Attributes | Key People ── */}
                <div className="grid gap-4 md:grid-cols-2">

                  {/* LEFT of Row 2 — Project Attributes */}
                  <div className="flex flex-col gap-4">
                    <Card className={isAttributesEditMode ? "ring-2 ring-info-200 bg-info-50/30" : ""}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle>Project Attributes</CardTitle>
                          {!isAttributesEditMode && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={handleEnterAttributesEditMode}
                                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    aria-label="Edit project attributes"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="left">Edit project attributes</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {isAttributesEditMode ? (
                          /* ── Edit Mode ── */
                          <div className="space-y-4">
                            {customFieldDefs
                              .sort((a, b) => a.displayOrder - b.displayOrder)
                              .map((def, idx) => {
                                const value = attributesEditState[def.id] ?? '';
                                const fieldError = attributesValidationErrors[def.id];
                                return (
                                  <div key={def.id} className="space-y-1.5">
                                    {idx > 0 && <Separator />}
                                    <Label className="text-xs text-muted-foreground">
                                      {def.label}
                                      {def.required && <span className="text-error-500 ml-0.5">*</span>}
                                    </Label>

                                    {/* text */}
                                    {def.fieldType === 'text' && (
                                      <Input
                                        value={value}
                                        onChange={(e) => {
                                          setAttributesEditState((prev) => ({ ...prev, [def.id]: e.target.value }));
                                          if (fieldError) setAttributesValidationErrors((prev) => { const next = { ...prev }; delete next[def.id]; return next; });
                                        }}
                                        placeholder={def.placeholder ?? ''}
                                        className={cn('h-8 text-sm', fieldError && 'border-error-500 focus-visible:ring-error-500')}
                                      />
                                    )}

                                    {/* number */}
                                    {def.fieldType === 'number' && (
                                      <Input
                                        type="number"
                                        value={value}
                                        onChange={(e) => {
                                          setAttributesEditState((prev) => ({ ...prev, [def.id]: e.target.value }));
                                          if (fieldError) setAttributesValidationErrors((prev) => { const next = { ...prev }; delete next[def.id]; return next; });
                                        }}
                                        placeholder={def.placeholder ?? ''}
                                        className={cn('h-8 text-sm', fieldError && 'border-error-500 focus-visible:ring-error-500')}
                                      />
                                    )}

                                    {/* currency */}
                                    {def.fieldType === 'currency' && (
                                      <div className="relative">
                                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">£</span>
                                        <Input
                                          type="number"
                                          value={value}
                                          onChange={(e) => {
                                            setAttributesEditState((prev) => ({ ...prev, [def.id]: e.target.value }));
                                            if (fieldError) setAttributesValidationErrors((prev) => { const next = { ...prev }; delete next[def.id]; return next; });
                                          }}
                                          placeholder={def.placeholder ?? '0.00'}
                                          className={cn('h-8 text-sm pl-6', fieldError && 'border-error-500 focus-visible:ring-error-500')}
                                        />
                                      </div>
                                    )}

                                    {/* date */}
                                    {def.fieldType === 'date' && (
                                      <Input
                                        type="date"
                                        value={value}
                                        onChange={(e) => {
                                          setAttributesEditState((prev) => ({ ...prev, [def.id]: e.target.value }));
                                          if (fieldError) setAttributesValidationErrors((prev) => { const next = { ...prev }; delete next[def.id]; return next; });
                                        }}
                                        className={cn('h-8 text-sm', fieldError && 'border-error-500 focus-visible:ring-error-500')}
                                      />
                                    )}

                                    {/* boolean */}
                                    {def.fieldType === 'boolean' && (
                                      <div className="flex items-center gap-2 pt-0.5">
                                        <Switch
                                          checked={value === 'true'}
                                          onCheckedChange={(checked) =>
                                            setAttributesEditState((prev) => ({
                                              ...prev,
                                              [def.id]: checked ? 'true' : 'false',
                                            }))
                                          }
                                        />
                                        <span className="text-sm">
                                          {value === 'true' ? 'Yes' : 'No'}
                                        </span>
                                      </div>
                                    )}

                                    {/* select */}
                                    {def.fieldType === 'select' && (
                                      <Select
                                        value={value || '__none__'}
                                        onValueChange={(v) => {
                                          setAttributesEditState((prev) => ({
                                            ...prev,
                                            [def.id]: v === '__none__' ? '' : v,
                                          }));
                                          if (fieldError) setAttributesValidationErrors((prev) => { const next = { ...prev }; delete next[def.id]; return next; });
                                        }}
                                      >
                                        <SelectTrigger className={cn('h-8 text-sm', fieldError && 'border-error-500 focus-visible:ring-error-500')}>
                                          <SelectValue placeholder={def.placeholder ?? 'Select…'} />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="__none__" className="text-sm text-muted-foreground">
                                            — None —
                                          </SelectItem>
                                          {(def.options ?? []).map((opt) => (
                                            <SelectItem key={opt} value={opt} className="text-sm">
                                              {opt}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    )}

                                    {fieldError && (
                                      <p className="text-xs text-error-500">{fieldError}</p>
                                    )}
                                  </div>
                                );
                              })}

                            {/* Card-level Save / Cancel */}
                            <div className="flex justify-end gap-2 pt-2">
                              <Button variant="outline" size="sm" onClick={handleCancelAttributesEdit}>
                                Cancel
                              </Button>
                              <Button size="sm" onClick={handleSaveAttributes}>
                                Save Changes
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* ── Read Mode ── */
                          (() => {
                            const populated = customFieldDefs
                              .sort((a, b) => a.displayOrder - b.displayOrder)
                              .filter((def) => {
                                const v = project.projectCustomFields?.[def.id];
                                return v !== undefined && v !== '';
                              });

                            if (populated.length === 0) {
                              return (
                                <div className="flex flex-col items-center justify-center py-6 text-muted-foreground gap-1.5">
                                  <SlidersHorizontal className="h-6 w-6 opacity-30" />
                                  <p className="text-sm">No attributes set</p>
                                  <button
                                    type="button"
                                    onClick={handleEnterAttributesEditMode}
                                    className="text-xs text-primary hover:underline underline-offset-2"
                                  >
                                    Add project attributes
                                  </button>
                                </div>
                              );
                            }

                            return (
                              <div className="space-y-2">
                                {populated.map((def, idx) => {
                                  const rawValue = project.projectCustomFields![def.id];

                                  let displayValue: ReactNode = rawValue;

                                  if (def.fieldType === 'currency') {
                                    const num = parseFloat(rawValue);
                                    displayValue = isNaN(num)
                                      ? rawValue
                                      : `£${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                  } else if (def.fieldType === 'number') {
                                    const num = parseFloat(rawValue);
                                    displayValue = isNaN(num) ? rawValue : num.toLocaleString('en-GB');
                                  } else if (def.fieldType === 'date') {
                                    const d = new Date(rawValue);
                                    displayValue = isNaN(d.getTime())
                                      ? rawValue
                                      : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                                  } else if (def.fieldType === 'boolean') {
                                    displayValue = rawValue === 'true'
                                      ? <Badge variant="outline" className="text-xs font-normal bg-success-50 text-success-700 border-success-200">Yes</Badge>
                                      : <Badge variant="outline" className="text-xs font-normal text-muted-foreground">No</Badge>;
                                  }

                                  return (
                                    <div key={def.id}>
                                      {idx > 0 && <Separator className="my-2" />}
                                      <div className="flex justify-between items-center gap-2">
                                        <span className="text-muted-foreground text-sm">{def.label}</span>
                                        <span className="font-medium text-sm text-right">{displayValue}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()
                        )}
                      </CardContent>
                    </Card>

                  </div>{/* end LEFT of Row 2 — Project Attributes */}

                  {/* RIGHT of Row 2 — Key People */}
                  <div className="flex flex-col gap-4">
                    {/* Key People */}
                    <Card className={isKeyPeopleEditMode ? "ring-2 ring-info-200 bg-info-50/30" : ""}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle>Key People</CardTitle>
                          {!isKeyPeopleEditMode && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={handleEnterKeyPeopleEditMode}
                                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    aria-label="Edit key people"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="left">Edit key people</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {isKeyPeopleEditMode ? (
                          /* ── Edit Mode ── */
                          <div className="space-y-4">
                            {PROJECT_ROLE_DISPLAY_ORDER.filter(isRoleEnabled).map((role, idx) => {
                              const required = isRoleRequired(role);
                              const label = getRoleLabel(role);
                              const currentValue = keyPeopleEditState[role] ?? '';
                              return (
                                <div key={role} className="space-y-1.5">
                                  {idx > 0 && <Separator />}
                                  <Label>
                                    {label}
                                    {required && <span className="text-error-500 ml-0.5">*</span>}
                                  </Label>
                                  <EmployeeCombobox
                                    employees={employees}
                                    value={currentValue || undefined}
                                    onValueChange={(val) => handleKeyPeopleRoleChange(role, val)}
                                    placeholder={`Select ${label}...`}
                                    recentEmployeeIds={[]}
                                    maxRecentEmployees={5}
                                  />
                                  {required && !currentValue && hasKeyPeopleUnsavedChanges && (
                                    <p className="text-xs text-error-600 flex items-center gap-1">
                                      <AlertCircle className="h-3 w-3" />
                                      {label} is required
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                            <div className="flex justify-end gap-2 pt-2">
                              <Button variant="outline" size="sm" onClick={handleCancelKeyPeopleEdit}>
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={handleSaveKeyPeople}
                                disabled={!hasKeyPeopleUnsavedChanges || !keyPeopleEditState['project-manager']}
                              >
                                Save Changes
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* ── Read Mode ── */
                          (() => {
                            // C6: derive assignments — prefer roleAssignments, fall back to projectManagerId
                            const assignments: Array<{ role: ProjectRoleType; name: string; jobTitle?: string }> = [];
                            if (project.roleAssignments && project.roleAssignments.length > 0) {
                              const assignmentMap = new Map(project.roleAssignments.map((a) => [a.role, a]));
                              PROJECT_ROLE_DISPLAY_ORDER.forEach((role) => {
                                const a = assignmentMap.get(role);
                                if (a) assignments.push({ role, name: a.employeeName, jobTitle: a.employeeJobTitle });
                              });
                            } else if (project.projectManagerId && projectManager) {
                              assignments.push({
                                role: 'project-manager',
                                name: projectManager.name,
                                jobTitle: projectManager.role ?? undefined,
                              });
                            }

                            if (assignments.length === 0) {
                              return (
                                <div className="flex flex-col items-center justify-center py-6 text-center gap-2">
                                  <div className="rounded-full bg-muted p-3">
                                    <Users className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                  <p className="text-sm font-medium">No key people assigned</p>
                                  <p className="text-xs text-muted-foreground">
                                    Add role assignments to track accountability for this project.
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="mt-1"
                                    onClick={handleEnterKeyPeopleEditMode}
                                  >
                                    <Pencil className="h-3 w-3 mr-1.5" />
                                    Add people
                                  </Button>
                                </div>
                              );
                            }

                            return (
                              <div className="space-y-3">
                                {assignments.map(({ role, name, jobTitle }, idx) => {
                                  const initials = name
                                    .split(' ')
                                    .filter(Boolean)
                                    .slice(0, 2)
                                    .map((w) => w[0].toUpperCase())
                                    .join('');
                                  return (
                                    <div key={role}>
                                      {idx > 0 && <Separator className="mb-3" />}
                                      <div className="flex items-center gap-3">
                                        <Avatar className="h-8 w-8 shrink-0">
                                          <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                                            {initials}
                                          </AvatarFallback>
                                        </Avatar>
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-medium truncate">{name}</span>
                                            <span className="text-xs text-muted-foreground shrink-0">
                                              {getRoleLabel(role)}
                                            </span>
                                          </div>
                                          {jobTitle && (
                                            <p className="text-xs text-muted-foreground truncate">{jobTitle}</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()
                        )}
                      </CardContent>
                    </Card>
                  </div>{/* end RIGHT of Row 2 — Key People */}

                </div>{/* end Row 2 */}

                {/* ── ROW 3: Project Objectives | Key Deliverables ── */}
                <div className="grid gap-4 md:grid-cols-2">

                  {/* LEFT of Row 3 — Project Objectives */}
                  <div className="flex flex-col gap-4">
                    {/* ── Project Objectives card ── */}
                    {contentFieldSettings.showObjectives && (
                    <Card className={isObjectivesEditMode ? "ring-2 ring-info-200 bg-info-50/30" : ""}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle>Project Objectives</CardTitle>
                          {!isObjectivesEditMode && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={handleEnterObjectivesEdit}
                                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    aria-label="Edit objectives"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Edit objectives</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        {isObjectivesEditMode ? (
                          <div className="space-y-3">
                            <Textarea
                              value={objectivesEditValue}
                              onChange={(e) => setObjectivesEditValue(e.target.value)}
                              placeholder="Describe why this project exists — strategic goals, success criteria, what 'done well' looks like for this engagement."
                              className="min-h-[150px] resize-y"
                              rows={6}
                              maxLength={2000}
                            />
                            <p className="text-xs text-muted-foreground text-right">
                              {objectivesEditValue.length}/2,000 (advisory)
                            </p>
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" size="sm" onClick={handleCancelObjectivesEdit}>Cancel</Button>
                              <Button size="sm" onClick={handleSaveObjectives}>Save</Button>
                            </div>
                          </div>
                        ) : project?.objectives ? (
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{project.objectives}</p>
                        ) : (
                          <button
                            type="button"
                            onClick={handleEnterObjectivesEdit}
                            className="w-full text-left group"
                          >
                            <p className="text-sm text-muted-foreground italic group-hover:text-foreground transition-colors">
                              No objectives defined yet — add what this project is trying to achieve.
                            </p>
                          </button>
                        )}
                      </CardContent>
                    </Card>
                    )}

                  </div>{/* end LEFT of Row 3 — Project Objectives */}

                  {/* RIGHT of Row 3 — Key Deliverables */}
                  <div className="flex flex-col gap-4">
                    {/* ── Key Deliverables card ── */}
                    {contentFieldSettings.showKeyDeliverables && (
                    <Card className={isDeliverablesEditMode ? "ring-2 ring-info-200 bg-info-50/30" : ""}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle>Key Deliverables</CardTitle>
                          {!isDeliverablesEditMode && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={handleEnterDeliverablesEdit}
                                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    aria-label="Edit key deliverables"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Edit key deliverables</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        {isDeliverablesEditMode ? (
                          <div className="space-y-3">
                            <Textarea
                              value={deliverablesEditValue}
                              onChange={(e) => setDeliverablesEditValue(e.target.value)}
                              placeholder="Describe what this project will produce — tangible outputs, artefacts, systems, or services being delivered."
                              className="min-h-[150px] resize-y"
                              rows={6}
                              maxLength={2000}
                            />
                            <p className="text-xs text-muted-foreground text-right">
                              {deliverablesEditValue.length}/2,000 (advisory)
                            </p>
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" size="sm" onClick={handleCancelDeliverablesEdit}>Cancel</Button>
                              <Button size="sm" onClick={handleSaveDeliverables}>Save</Button>
                            </div>
                          </div>
                        ) : project?.keyDeliverables ? (
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{project.keyDeliverables}</p>
                        ) : (
                          <button
                            type="button"
                            onClick={handleEnterDeliverablesEdit}
                            className="w-full text-left group"
                          >
                            <p className="text-sm text-muted-foreground italic group-hover:text-foreground transition-colors">
                              No deliverables listed yet — describe what this project will produce.
                            </p>
                          </button>
                        )}
                      </CardContent>
                    </Card>
                    )}

                  </div>{/* end RIGHT of Row 3 — Key Deliverables */}

                </div>{/* end Row 3 */}

                {/* ── FULL WIDTH: Project Notes (timestamped audit log — v1.4) ── */}
                {contentFieldSettings.showNotes && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <CardTitle>Project Notes</CardTitle>
                      {noteLog.length > 0 && (
                        <Badge variant="secondary" className="text-xs font-normal">
                          {noteLog.length} {noteLog.length === 1 ? "note" : "notes"}
                        </Badge>
                      )}
                    </div>

                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Compose box — always visible at the top */}
                    <div className="flex flex-col gap-2 rounded-lg border bg-muted/30 p-3">
                      <Textarea
                        value={newNoteValue}
                        onChange={(e) => setNewNoteValue(e.target.value)}
                        placeholder="Add a note — decisions made, client context, escalations, handover information…"
                        className="min-h-[80px] resize-y bg-background border-muted"
                        rows={3}
                        onKeyDown={(e) => {
                          // Ctrl+Enter / Cmd+Enter to submit
                          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                            e.preventDefault();
                            handleAddNote();
                          }
                        }}
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          Press <kbd className="rounded border border-border bg-muted px-1 py-0.5 text-[10px] font-mono">Ctrl+Enter</kbd> to add
                        </p>
                        <Button
                          size="sm"
                          onClick={handleAddNote}
                          disabled={!newNoteValue.trim() || isSubmittingNote}
                          className="gap-1.5"
                        >
                          <Send className="h-3.5 w-3.5" />
                          Add Note
                        </Button>
                      </div>
                    </div>

                    {/* Note entries — newest first */}
                    {noteLog.length > 0 ? (
                      <div className="space-y-3">
                        {noteLog.map((note) => {
                          const date = new Date(note.createdAt);
                          const now = new Date();
                          const diffMs = now.getTime() - date.getTime();
                          const diffMins = Math.floor(diffMs / 60000);
                          const diffHours = Math.floor(diffMs / 3600000);
                          const diffDays = Math.floor(diffMs / 86400000);
                          const relativeTime =
                            diffMins < 1 ? "Just now" :
                            diffMins < 60 ? `${diffMins}m ago` :
                            diffHours < 24 ? `${diffHours}h ago` :
                            diffDays < 7 ? `${diffDays}d ago` :
                            date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
                          const fullDateTime = date.toLocaleString("en-GB", {
                            day: "numeric", month: "long", year: "numeric",
                            hour: "2-digit", minute: "2-digit"
                          });

                          return (
                            <div key={note.id} className="flex gap-3 group">
                              {/* Avatar */}
                              <div className="flex-shrink-0 mt-0.5">
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-xs font-semibold text-primary">
                                    {note.createdByInitials}
                                  </span>
                                </div>
                              </div>
                              {/* Note body */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                  <span className="text-sm font-medium">{note.createdByName}</span>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="text-xs text-muted-foreground cursor-default flex items-center gap-1">
                                          <Clock3 className="h-3 w-3" />
                                          {relativeTime}
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="top">
                                        <p>{fullDateTime}</p>
                                        {note.isEdited && note.editedAt && (
                                          <p className="text-xs text-muted-foreground mt-0.5">
                                            Edited {new Date(note.editedAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                          </p>
                                        )}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  {note.isEdited && (
                                    <span className="text-xs text-muted-foreground italic">(edited)</span>
                                  )}
                                </div>
                                <p className="text-sm whitespace-pre-wrap leading-relaxed mt-1 text-foreground/90">
                                  {note.content}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 py-6 text-center text-muted-foreground">
                        <MessageSquarePlus className="h-8 w-8 opacity-30" />
                        <p className="text-sm font-medium">No notes yet</p>
                        <p className="text-xs">Add the first note above — it will be attributed to you with a timestamp.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                )}

                {/* Quick Stats */}
                <div className="grid gap-4 md:grid-cols-4">
                  <Card>
                    {(() => {
                      const unfilledRoles = enrichedBudgetData?.resourceBudget?.byRole?.filter(
                        (r) => r.allocatedHours < r.budgetHours
                      ).length ?? 0;
                      const isError = activeResources === 0;
                      const isWarning = !isError && unfilledRoles > 0;
                      const isHealthy = !isError && !isWarning;
                      const amountColor = isError ? 'text-error-600' : isWarning ? 'text-warning-600' : 'text-success-600';
                      const Icon = isError ? AlertCircle : isWarning ? Clock : CheckCircle;
                      return (
                        <>
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-sm font-medium">Active Resources</CardTitle>
                              <Icon className={`h-4 w-4 ${amountColor}`} />
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className={`text-2xl font-bold ${amountColor}`}>{activeResources}</div>
                            <p className="text-xs text-muted-foreground">team members</p>
                            <div className="mt-2 flex items-center justify-between">
                              <p className={`text-xs ${amountColor}`}>
                                {isError ? 'no team assigned' : isWarning ? `${unfilledRoles} role${unfilledRoles > 1 ? 's' : ''} unfilled` : 'fully staffed'}
                              </p>
                              {!isHealthy && (
                                <button
                                  onClick={() => setActiveTab('team')}
                                  className="text-xs text-primary hover:underline"
                                >
                                  View Team →
                                </button>
                              )}
                            </div>
                          </CardContent>
                        </>
                      );
                    })()}
                  </Card>

                  <Card>
                    {(() => {
                      const dueSoonMilestones = milestones.filter((m) => {
                        if (m.status !== 'pending') return false;
                        const daysUntilDue = Math.ceil((new Date(m.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                        return daysUntilDue >= 0 && daysUntilDue <= 14;
                      }).length;
                      const isError = overdueMilestones > 0;
                      const isWarning = !isError && dueSoonMilestones > 0;
                      const isHealthy = !isError && !isWarning;
                      const amountColor = isError ? 'text-error-600' : isWarning ? 'text-warning-600' : 'text-success-600';
                      const Icon = isError ? AlertCircle : isWarning ? Clock : CheckCircle;
                      const iconColor = isError ? 'text-error-600' : isWarning ? 'text-warning-600' : 'text-success-600';
                      const descriptorText = isError
                        ? `${overdueMilestones} overdue`
                        : isWarning
                        ? `${dueSoonMilestones} due soon`
                        : 'all on track';
                      return (
                        <>
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-sm font-medium">Milestones</CardTitle>
                              <Icon className={`h-4 w-4 ${iconColor}`} />
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className={`text-2xl font-bold ${amountColor}`}>
                              {completedMilestones}/{milestones.length}
                            </div>
                            <p className="text-xs text-muted-foreground">completed</p>
                            <div className="mt-2 flex items-center justify-between">
                              <p className={`text-xs font-medium ${amountColor}`}>{descriptorText}</p>
                              {!isHealthy && (
                                <button
                                  onClick={() => setActiveTab('milestones')}
                                  className="text-xs text-primary hover:underline"
                                >
                                  View →
                                </button>
                              )}
                            </div>
                          </CardContent>
                        </>
                      );
                    })()}
                  </Card>

                  <Card>
                    {(() => {
                      const totalChanges = changeRequests.length;
                      const pendingCount = pendingChangeRequests;
                      const approvedBudgetImpact = changeRequests
                        .filter(cr => cr.status === 'approved')
                        .reduce((sum, cr) => sum + (cr.impactBudget || 0), 0);

                      const isError = pendingCount > 0;
                      const isWarning = !isError && approvedBudgetImpact > 0;
                      const isHealthy = !isError && !isWarning;

                      const amountColor = isError ? 'text-error-600' : isWarning ? 'text-warning-600' : 'text-success-600';
                      const iconColor = isError ? 'text-error-500' : isWarning ? 'text-warning-500' : 'text-success-500';
                      const IconComponent = isError ? AlertCircle : isWarning ? Clock : CheckCircle;

                      return (
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium">Change Requests</CardTitle>
                            <IconComponent className={`h-4 w-4 ${iconColor}`} />
                          </div>
                          <CardContent className="px-0 pb-0">
                            <div className={`text-2xl font-bold ${amountColor}`}>{totalChanges}</div>
                            <p className="text-xs text-muted-foreground">requests</p>
                            <div className="mt-2 space-y-0.5">
                              {isError && (
                                <p className={`text-xs font-medium ${amountColor}`}>
                                  {pendingCount} pending approval
                                </p>
                              )}
                              {approvedBudgetImpact > 0 && (
                                <p className="text-xs text-muted-foreground">
                                  {formatCurrency(approvedBudgetImpact)} budget impact
                                </p>
                              )}
                              {isHealthy && (
                                <p className={`text-xs font-medium ${amountColor}`}>no pending requests</p>
                              )}
                              {!isHealthy && (
                                <button
                                  onClick={() => setActiveTab("changes")}
                                  className="text-xs text-primary hover:underline mt-0.5"
                                >
                                  View →
                                </button>
                              )}
                            </div>
                          </CardContent>
                        </CardHeader>
                      );
                    })()}
                  </Card>

                  <Card>
                    {(() => {
                      const billingPercent = (project.actualRevenue / project.budgetRevenue) * 100;
                      const completionPercent = project.completionPercent || 0;
                      const billingGap = billingPercent - completionPercent;
                      const hasAgedWIP = project.wipAmount > 50000;
                      const isError = hasAgedWIP || billingGap < -10;
                      const isWarning = !isError && (billingGap < -5 || project.wipAmount > 30000);
                      const isHealthy = !isError && !isWarning;

                      const amountColor = isError ? 'text-error-600' : isWarning ? 'text-warning-600' : 'text-success-600';
                      const iconColor = isError ? 'text-error-500' : isWarning ? 'text-warning-500' : 'text-success-500';

                      const descriptor = isError && hasAgedWIP ? 'invoice now'
                        : isError ? `under-billing by ${Math.abs(billingGap).toFixed(0)}%`
                        : isWarning && billingGap < -5 ? 'review billing'
                        : isWarning ? 'review billing'
                        : 'billing on track';

                      const StatusIcon = isError ? Clock : isWarning ? AlertCircle : CheckCircle;

                      return (
                        <>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                              WIP Amount
                              <Clock className={`h-4 w-4 ${iconColor}`} />
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className={`text-2xl font-bold ${amountColor}`}>
                              <SensitiveData>{formatCurrency(project.wipAmount)}</SensitiveData>
                            </div>
                            <div className={`flex items-center gap-1 mt-1`}>
                              <StatusIcon className={`h-3 w-3 ${iconColor}`} />
                              <span className={`text-xs ${amountColor}`}>{descriptor}</span>
                            </div>
                            {!isHealthy && (
                              <button
                                onClick={() => setActiveTab("billing")}
                                className="text-xs text-primary hover:underline mt-2 block"
                              >
                                View in Billing →
                              </button>
                            )}
                          </CardContent>
                        </>
                      );
                    })()}
                  </Card>
                </div>

                {/* Resource Scheduling Widget — only visible when a scheduler is connected */}
                {schedulerConnected && (
                  <SchedulingOverviewWidget
                    summary={mockSchedulingSummary}
                    onViewDetails={() => setActiveTab("scheduling")}
                  />
                )}
              </TabsContent>

              {/* Financials Tab */}
              <TabsContent value="financials" className="space-y-6">

                {/* ── Section 1: Performance Summary ── */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-foreground">Performance Summary</h3>
                    <div className="flex-1 h-px bg-border ml-1" />
                  </div>

                {/* Row 1 — Performance metric cards (3 cards) */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {/* Card 1 — Sales Performance */}
                  {(() => {
                    const actualSales = project.actualSales ?? 0;
                    const budgetRevenue = project.budgetRevenue ?? 0;
                    const salesVariance = actualSales - budgetRevenue;
                    const salesVariancePct = budgetRevenue !== 0 ? (salesVariance / budgetRevenue) * 100 : 0;
                    const remainingToDeliver = budgetRevenue - actualSales;
                    const isOnOrAboveBudget = actualSales >= budgetRevenue;
                    return (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-medium">Sales Performance</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {/* Hero metric */}
                          <div className="flex items-center gap-2">
                            <div className="text-2xl font-bold">
                              <SensitiveData>{formatCurrency(actualSales)}</SensitiveData>
                            </div>
                            {isOnOrAboveBudget ? (
                              <TrendingUp className="h-4 w-4 text-success-500" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-error-500" />
                            )}
                          </div>
                          <button
                            onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=S`)}
                            className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity mt-0.5 text-left"
                          >
                            Actual Sales
                          </button>

                          {/* Secondary rows */}
                          <div className="mt-3 space-y-1.5">
                            <div className="flex justify-between text-xs">
                              <button
                                onClick={() => setActiveTab('sales-budget')}
                                className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                              >Budget Sales</button>
                              <SensitiveData>
                                <span className="font-medium">{formatCurrency(budgetRevenue)}</span>
                              </SensitiveData>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">Variance (£)</span>
                              <SensitiveData>
                                <span className={`font-medium ${salesVariance >= 0 ? "text-success-600" : "text-error-600"}`}>
                                  {salesVariance >= 0 ? "+" : ""}{formatCurrency(salesVariance)}
                                </span>
                              </SensitiveData>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">Variance (%)</span>
                              <SensitiveData>
                                <span className={`font-medium ${salesVariancePct >= 0 ? "text-success-600" : "text-error-600"}`}>
                                  {salesVariancePct >= 0 ? "+" : ""}{formatPercent(salesVariancePct)}
                                </span>
                              </SensitiveData>
                            </div>
                            <div className="flex justify-between text-xs border-t pt-1.5 mt-1">
                              <span className="text-muted-foreground">Remaining to Deliver</span>
                              <SensitiveData>
                                <span className={`font-medium ${remainingToDeliver <= 0 ? "text-success-600" : "text-muted-foreground"}`}>
                                  {formatCurrency(Math.max(remainingToDeliver, 0))}
                                </span>
                              </SensitiveData>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Card 2 — Cost Performance */}
                  {(() => {
                    const costTotals = budgetData?.costBudget?.totals;
                    const actualCosts = costTotals?.actual ?? project.actualCosts ?? 0;
                    const budgetCosts = costTotals?.budget ?? ((project.budgetLabour ?? 0) + (project.budgetExpense ?? 0));
                    const costVariance = actualCosts - budgetCosts;
                    const costVariancePct = budgetCosts !== 0 ? (costVariance / budgetCosts) * 100 : 0;
                    const isOverBudget = actualCosts > budgetCosts;

                    // Contingency
                    const contingencyCat = budgetData?.costBudget?.categories?.find(
                      (c: { type?: string }) => c.type === 'contingency'
                    );
                    const contingencyBudget = (contingencyCat as { budget?: number } | undefined)?.budget ?? 0;
                    const contingencyActual = (contingencyCat as { actual?: number } | undefined)?.actual ?? 0;
                    const contingencyLeft = contingencyBudget - contingencyActual;

                    return (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-medium">Cost Performance</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {/* Hero metric */}
                          <div className="flex items-center gap-2">
                            <div className="text-2xl font-bold">
                              <SensitiveData>{formatCurrency(actualCosts)}</SensitiveData>
                            </div>
                            {isOverBudget ? (
                              <TrendingUp className="h-4 w-4 text-error-500" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-success-500" />
                            )}
                          </div>
                          <button
                            onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=C`)}
                            className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity mt-1"
                          >Actual Costs</button>

                          {/* Secondary rows */}
                          <div className="mt-3 space-y-1.5 text-xs">
                            <div className="flex justify-between">
                              <button
                                onClick={() => setActiveTab('cost-budget')}
                                className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                              >Budget Costs</button>
                              <SensitiveData>
                                <span className="font-medium">{formatCurrency(budgetCosts)}</span>
                              </SensitiveData>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Variance (£)</span>
                              <SensitiveData>
                                <span className={`font-medium ${costVariance > 0 ? 'text-error-600' : costVariance < 0 ? 'text-success-600' : ''}`}>
                                  {costVariance >= 0 ? '+' : ''}{formatCurrency(costVariance)}
                                </span>
                              </SensitiveData>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Variance (%)</span>
                              <span className={`font-medium ${costVariancePct > 0 ? 'text-error-600' : costVariancePct < 0 ? 'text-success-600' : ''}`}>
                                {costVariancePct >= 0 ? '+' : ''}{formatPercent(costVariancePct)}
                              </span>
                            </div>
                            {contingencyBudget > 0 && (
                              <>
                                <div className="border-t pt-1.5 mt-1.5" />
                                <div className="flex justify-between items-center">
                                  <span className="text-muted-foreground">Contingency Left</span>
                                  <SensitiveData>
                                    <span className={`font-medium text-xs px-1.5 py-0.5 rounded-full ${
                                      contingencyLeft <= 0
                                        ? 'bg-error-100 text-error-700'
                                        : contingencyLeft < contingencyBudget * 0.25
                                        ? 'bg-warning-100 text-warning-700'
                                        : 'bg-success-100 text-success-700'
                                    }`}>
                                      {formatCurrency(contingencyLeft)}
                                    </span>
                                  </SensitiveData>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Drill-through link — Task 28 */}
                          <div className="mt-3 pt-2 border-t">
                            <button
                              onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=C`)}
                              className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                            >
                              View transactions →
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Card 3 — Margin Performance */}
                  {(() => {
                    const actualCostsForMargin = budgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0;
                    const budgetCostsForMargin = budgetData?.costBudget?.totals?.budget ?? ((project.budgetLabour ?? 0) + (project.budgetExpense ?? 0));

                    // Task 23 — use the company GP Calculation Method setting
                    const gpMethod = getFinanceSettings().gpCalculationMethod;

                    const actualResult = calculateGP(
                      {
                        revenue: project.actualSales,
                        totalDirectCosts: actualCostsForMargin,
                        // Labour and subcontractor breakdowns are not available at the project
                        // summary level yet — contribution method will fall back to 0 for those
                        // until nominal code mapping (Task 24) provides the split.
                        labourCosts: project.actualLabourCosts ?? 0,
                        subcontractorCosts: project.actualSubcontractorCosts ?? 0,
                      },
                      gpMethod
                    );

                    const budgetResult = calculateBudgetGP(
                      {
                        revenue: project.budgetRevenue,
                        totalDirectCosts: budgetCostsForMargin,
                        labourCosts: project.budgetLabour ?? 0,
                        subcontractorCosts: 0, // subcontractor budget not available at summary level
                      },
                      gpMethod
                    );

                    const actualGP = actualResult.grossProfit;
                    const budgetGP = budgetResult.grossProfit;
                    const actualGMpct = actualResult.grossMarginPct;
                    const budgetGMpct = budgetResult.grossMarginPct;
                    const variancePp = actualGMpct - budgetGMpct;

                    // Health thresholds — driven by Settings (Task 22)
                    const marginThresholds = getBudgetHealthThresholds();
                    const amberThreshold = marginThresholds.marginAmberThreshold;
                    const redThreshold = marginThresholds.marginRedThreshold;

                    return (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm font-medium">Margin Performance</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {/* Hero metric */}
                          <div className="flex items-center gap-2">
                            <div className={`text-2xl font-bold ${actualGMpct >= amberThreshold ? 'text-success-600' : actualGMpct >= redThreshold ? 'text-warning-600' : 'text-error-600'}`}>
                              <SensitiveData>{formatPercent(actualGMpct)}</SensitiveData>
                            </div>
                            {/* Health badge */}
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                              actualGMpct >= amberThreshold
                                ? 'bg-success-100 text-success-700'
                                : actualGMpct >= redThreshold
                                ? 'bg-warning-100 text-warning-700'
                                : 'bg-error-100 text-error-700'
                            }`}>
                              {actualGMpct >= amberThreshold ? 'Healthy' : actualGMpct >= redThreshold ? 'At Risk' : 'Critical'}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Actual Gross Margin %</p>

                          {/* Secondary rows */}
                          <div className="mt-3 space-y-1.5 text-xs">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Budget GM%</span>
                              <span className="font-medium">
                                <SensitiveData>{formatPercent(budgetGMpct)}</SensitiveData>
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Variance</span>
                              <span className={`font-medium ${variancePp > 0 ? 'text-success-600' : variancePp < 0 ? 'text-error-600' : ''}`}>
                                {variancePp >= 0 ? '+' : ''}{variancePp.toFixed(1)}pp
                              </span>
                            </div>
                            <div className="border-t pt-1.5 mt-1.5" />
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Actual GP (£)</span>
                              <SensitiveData>
                                <span className={`font-medium ${actualGP >= 0 ? 'text-success-600' : 'text-error-600'}`}>
                                  {formatCurrency(actualGP)}
                                </span>
                              </SensitiveData>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}
                </div>
                </div>{/* end Performance Summary section */}

                {/* ── Section 2: Billing & Cash Flow ── */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-foreground">Billing &amp; Cash Flow</h3>
                    <div className="flex-1 h-px bg-border ml-1" />
                  </div>

                {/* Row 2 — Billing metric cards (3 cards) */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">

                  {/* Card 4 — WIP & Billing */}
                  {(() => {
                    const wipThresholds = getBudgetHealthThresholds();
                    const wip = project.wipAmount ?? 0;
                    const ready = project.unbilledAmount ?? 0;
                    const completionPct = project.completionPercent ?? 0;
                    const billingPct = project.budgetRevenue > 0
                      ? ((project.type === 'fixed-price' ? fixedFeeInvoicedToDate : project.actualRevenue) / project.budgetRevenue) * 100
                      : 0;
                    const underBillingGap = completionPct - billingPct; // positive = under-billing

                    // WIP health — driven by Settings thresholds (not hardcoded)
                    const wipIsRed = wip >= wipThresholds.wipRedThreshold;
                    const wipIsAmber = !wipIsRed && wip >= wipThresholds.wipAmberThreshold;
                    const wipColor = wipIsRed
                      ? 'text-error-600'
                      : wipIsAmber
                      ? 'text-warning-600'
                      : 'text-foreground';

                    return (
                      <Card>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium">WIP &amp; Billing</CardTitle>
                            {wipIsRed ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-error-50 px-2 py-0.5 text-[10px] font-medium text-error-700">
                                <AlertCircle className="h-3 w-3" /> High WIP
                              </span>
                            ) : wipIsAmber ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-warning-50 px-2 py-0.5 text-[10px] font-medium text-warning-700">
                                <AlertCircle className="h-3 w-3" /> Review
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-success-50 px-2 py-0.5 text-[10px] font-medium text-success-700">
                                <CheckCircle className="h-3 w-3" /> On Track
                              </span>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {/* Hero — WIP */}
                          <div>
                            <div className={`text-2xl font-bold ${wipColor}`}>
                              <SensitiveData>{formatCurrency(wip)}</SensitiveData>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">WIP (Unbilled)</p>
                          </div>

                          <Separator />

                          {/* Ready to Invoice */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Ready to Invoice</span>
                            <span className="text-sm font-semibold text-success-600">
                              <SensitiveData>{formatCurrency(ready)}</SensitiveData>
                            </span>
                          </div>

                          {/* Under-billing gap */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Billing vs Completion</span>
                            {underBillingGap > 0 ? (
                              <span className="text-xs font-medium text-error-600">
                                {underBillingGap.toFixed(1)}% behind billing
                              </span>
                            ) : underBillingGap < -2 ? (
                              <span className="text-xs font-medium text-success-600">
                                {Math.abs(underBillingGap).toFixed(1)}% ahead of completion
                              </span>
                            ) : (
                              <span className="text-xs font-medium text-success-600">In line</span>
                            )}
                          </div>

                          <Separator />

                          {/* WIP threshold context */}
                          <div className="text-[10px] text-muted-foreground">
                            Amber at {formatCurrency(wipThresholds.wipAmberThreshold)} · Red at {formatCurrency(wipThresholds.wipRedThreshold)}
                          </div>

                          <Separator />

                          {/* Drill-through → Billing tab */}
                          <button
                            onClick={() => setActiveTab('billing')}
                            className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                          >
                            View in Billing
                          </button>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Card 5 — Invoice Summary */}
                  {(() => {
                    const totalInvoiced = project.type === 'fixed-price'
                      ? fixedFeeInvoicedToDate
                      : project.actualRevenue;

                    const draftInvoices = fixedFeeInvoices.filter((i) => i.status === 'Draft');
                    const overdueInvoices = fixedFeeInvoices.filter((i) => i.status === 'Overdue');
                    const draftValue = draftInvoices.reduce((s, i) => s + i.amount, 0);
                    const overdueValue = overdueInvoices.reduce((s, i) => s + i.amount, 0);

                    // Next invoice due — earliest upcoming unpaid invoice
                    const today = new Date();
                    const upcomingInvoices = fixedFeeInvoices
                      .filter((i) => i.status !== 'Paid' && i.due)
                      .sort((a, b) => new Date(a.due!).getTime() - new Date(b.due!).getTime());
                    const nextDue = upcomingInvoices[0];

                    // Overdue aging buckets
                    const aging30 = overdueInvoices
                      .filter((i) => {
                        const days = (today.getTime() - new Date(i.due!).getTime()) / 86400000;
                        return days <= 30;
                      })
                      .reduce((s, i) => s + i.amount, 0);
                    const aging60 = overdueInvoices
                      .filter((i) => {
                        const days = (today.getTime() - new Date(i.due!).getTime()) / 86400000;
                        return days > 30 && days <= 60;
                      })
                      .reduce((s, i) => s + i.amount, 0);
                    const aging90 = overdueInvoices
                      .filter((i) => {
                        const days = (today.getTime() - new Date(i.due!).getTime()) / 86400000;
                        return days > 60 && days <= 90;
                      })
                      .reduce((s, i) => s + i.amount, 0);
                    const aging90plus = overdueInvoices
                      .filter((i) => {
                        const days = (today.getTime() - new Date(i.due!).getTime()) / 86400000;
                        return days > 90;
                      })
                      .reduce((s, i) => s + i.amount, 0);

                    const hasAgingIssues = aging30 > 0 || aging60 > 0 || aging90 > 0 || aging90plus > 0;

                    return (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">Invoice Summary</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {/* Hero — Total Invoiced */}
                          <div>
                            <div className="text-2xl font-bold">
                              <SensitiveData>{formatCurrency(totalInvoiced)}</SensitiveData>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">Total Invoiced</p>
                          </div>

                          <Separator />

                          {/* Draft invoices — clickable when there are drafts (Task 26) */}
                          <div className="flex items-center justify-between">
                            {draftInvoices.length > 0 ? (
                              <button
                                className="text-xs text-primary underline underline-offset-2 hover:text-primary/80 transition-colors text-left"
                                onClick={() => {
                                  setInvoiceStatusFilter('Draft');
                                  setInvoiceCurrentPage(1);
                                  setActiveTab('billing');
                                }}
                              >
                                Draft Invoices
                              </button>
                            ) : (
                              <span className="text-xs text-muted-foreground">Draft Invoices</span>
                            )}
                            <span className={`text-xs font-medium ${draftInvoices.length > 0 ? 'text-warning-600' : 'text-muted-foreground'}`}>
                              <SensitiveData>
                                {draftInvoices.length > 0 ? formatCurrency(draftValue) : '—'}
                              </SensitiveData>
                            </span>
                          </div>

                          {/* Overdue invoices — clickable when there are overdue (Task 26) */}
                          <div className="flex items-center justify-between">
                            {overdueInvoices.length > 0 ? (
                              <button
                                className="text-xs text-primary underline underline-offset-2 hover:text-primary/80 transition-colors text-left"
                                onClick={() => {
                                  setInvoiceStatusFilter('Overdue');
                                  setInvoiceCurrentPage(1);
                                  setActiveTab('billing');
                                }}
                              >
                                Overdue
                              </button>
                            ) : (
                              <span className="text-xs text-muted-foreground">Overdue</span>
                            )}
                            <span className={`text-xs font-medium ${overdueInvoices.length > 0 ? 'text-error-600' : 'text-muted-foreground'}`}>
                              <SensitiveData>
                                {overdueInvoices.length > 0 ? formatCurrency(overdueValue) : '—'}
                              </SensitiveData>
                            </span>
                          </div>

                          {/* Next invoice due */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Next Due</span>
                            <span className="text-xs font-medium">
                              {nextDue?.due ? formatDate(nextDue.due) : '—'}
                            </span>
                          </div>

                          {/* Aging buckets — only shown if there are overdue invoices */}
                          {hasAgingIssues && (
                            <>
                              <Separator />
                              <div className="space-y-1">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Overdue Aging</p>
                                {aging30 > 0 && (
                                  <button
                                    className="w-full flex items-center justify-between hover:opacity-70 transition-opacity"
                                    onClick={() => { setInvoiceStatusFilter('Overdue'); setInvoiceCurrentPage(1); setActiveTab('billing'); }}
                                  >
                                    <span className="text-[10px] text-primary underline underline-offset-2">0–30 days</span>
                                    <span className="text-[10px] font-medium text-warning-600">
                                      <SensitiveData>{formatCurrency(aging30)}</SensitiveData>
                                    </span>
                                  </button>
                                )}
                                {aging60 > 0 && (
                                  <button
                                    className="w-full flex items-center justify-between hover:opacity-70 transition-opacity"
                                    onClick={() => { setInvoiceStatusFilter('Overdue'); setInvoiceCurrentPage(1); setActiveTab('billing'); }}
                                  >
                                    <span className="text-[10px] text-primary underline underline-offset-2">31–60 days</span>
                                    <span className="text-[10px] font-medium text-warning-700">
                                      <SensitiveData>{formatCurrency(aging60)}</SensitiveData>
                                    </span>
                                  </button>
                                )}
                                {aging90 > 0 && (
                                  <button
                                    className="w-full flex items-center justify-between hover:opacity-70 transition-opacity"
                                    onClick={() => { setInvoiceStatusFilter('Overdue'); setInvoiceCurrentPage(1); setActiveTab('billing'); }}
                                  >
                                    <span className="text-[10px] text-primary underline underline-offset-2">61–90 days</span>
                                    <span className="text-[10px] font-medium text-error-600">
                                      <SensitiveData>{formatCurrency(aging90)}</SensitiveData>
                                    </span>
                                  </button>
                                )}
                                {aging90plus > 0 && (
                                  <button
                                    className="w-full flex items-center justify-between hover:opacity-70 transition-opacity"
                                    onClick={() => { setInvoiceStatusFilter('Overdue'); setInvoiceCurrentPage(1); setActiveTab('billing'); }}
                                  >
                                    <span className="text-[10px] text-primary underline underline-offset-2">90+ days</span>
                                    <span className="text-[10px] font-medium text-error-600">
                                      <SensitiveData>{formatCurrency(aging90plus)}</SensitiveData>
                                    </span>
                                  </button>
                                )}
                              </div>
                            </>
                          )}

                          {/* All clear state */}
                          {!hasAgingIssues && draftInvoices.length === 0 && overdueInvoices.length === 0 && (
                            <div className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 text-success-500" />
                              <span className="text-xs text-success-600">No outstanding issues</span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Card 6 — Realisation Rate — Task 6 */}
                  {(() => {
                    // Potential revenue = SUM(billable approved hours × billRate per entry)
                    const billableEntries = timeEntries.filter((e) => e.billable && e.approved);
                    const potentialRevenue = billableEntries.reduce(
                      (sum, e) => sum + e.hours * e.billRate,
                      0
                    );
                    const actualBilled = project.actualSales ?? 0;
                    const hasRateData = billableEntries.length > 0 && potentialRevenue > 0;
                    const realisationRate = hasRateData
                      ? Math.round((actualBilled / potentialRevenue) * 100)
                      : null;
                    const leakage = hasRateData ? potentialRevenue - actualBilled : null;

                    // Health signal
                    // TODO Task 22: Replace hardcoded 90/75 thresholds with
                    // getBudgetHealthThresholds().realisationAmberThreshold (default 90) and
                    // getBudgetHealthThresholds().realisationRedThreshold (default 75)
                    // Also add realisationEnabled toggle — if disabled, hide the badge entirely.
                    const rateHealth =
                      realisationRate === null
                        ? null
                        : realisationRate >= 90
                        ? { label: "Healthy", colour: "text-green-600", bg: "bg-green-50 dark:bg-green-950 border-green-200" }
                        : realisationRate >= 75
                        ? { label: "At Risk", colour: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-950 border-amber-200" }
                        : { label: "Critical", colour: "text-red-600", bg: "bg-red-50 dark:bg-red-950 border-red-200" };

                    return (
                      <Card>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium">Realisation Rate</CardTitle>
                            {rateHealth && (
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${rateHealth.bg} ${rateHealth.colour}`}>
                                {rateHealth.label}
                              </span>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent>
                          {hasRateData ? (
                            <>
                              {/* Hero */}
                              <div className={`text-2xl font-bold ${rateHealth?.colour ?? ""}`}>
                                {realisationRate}%
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">Realisation Rate</p>

                              {/* Secondary rows */}
                              <div className="mt-3 space-y-1.5">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">Actual Billed</span>
                                  <span className="font-medium">{formatCurrency(actualBilled)}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">Potential Value</span>
                                  <span className="font-medium">{formatCurrency(potentialRevenue)}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">Value Leakage</span>
                                  <span className={`font-medium ${(leakage ?? 0) > 0 ? "text-red-600" : "text-green-600"}`}>
                                    {leakage !== null ? formatCurrency(leakage) : "—"}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">Billable Entries</span>
                                  <span className="font-medium">{billableEntries.length}</span>
                                </div>
                              </div>
                              <button
                                className="mt-2 text-xs text-primary hover:underline text-left"
                                onClick={() => {
                                  setTimeTabPrefilterBillable(true);
                                  setActiveTab('time');
                                }}
                              >
                                View billable entries →
                              </button>
                            </>
                          ) : (
                            <>
                              <div className="text-2xl font-bold text-muted-foreground">—</div>
                              <p className="text-xs text-muted-foreground mt-1">Realisation Rate %</p>
                              <div className="flex items-center gap-1 mt-3">
                                <span className="text-xs text-muted-foreground italic">
                                  No approved billable timesheet entries found
                                </span>
                              </div>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()}
                </div>
                </div>{/* end Billing & Cash Flow section */}

                {/* ── Section 3: Financial Analysis (collapsible) ── */}
                <div className="border rounded-lg overflow-hidden">
                  {/* Header / toggle */}
                  <button
                    type="button"
                    onClick={() => setFinancialAnalysisExpanded(e => !e)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2">
                      <BarChart2 className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-semibold">Financial Analysis</span>
                      <span className="text-xs text-muted-foreground">(Charts &amp; Budget vs Actual)</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      {financialAnalysisExpanded ? 'Hide' : 'Show'}
                      {financialAnalysisExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </button>

                  {/* Expanded body */}
                  {financialAnalysisExpanded && (
                    <div className="px-4 pb-4 pt-2 space-y-4">

                {/* Row 3 — Financial performance charts */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Profitability Grouped Bar Chart — Task 7 */}
                  {(() => {
                    const budgetSales  = project.budgetRevenue ?? 0;
                    const actualSales  = project.actualSales ?? 0;
                    const budgetCosts  = budgetData?.costBudget?.totals?.budget ?? (project.budgetLabour + project.budgetExpense);
                    const actualCosts  = budgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0;
                    const budgetGP     = budgetSales - budgetCosts;
                    const actualGP     = actualSales - actualCosts;

                    const profitabilityChartData = [
                      { metric: "Sales",        Budget: budgetSales,           Actual: actualSales  },
                      { metric: "Costs",         Budget: budgetCosts,           Actual: actualCosts  },
                      { metric: "Gross Profit",  Budget: Math.max(0, budgetGP), Actual: Math.max(0, actualGP) },
                    ];

                    const profitabilityChartConfig: ChartConfig = {
                      Budget: { label: "Budget", color: "var(--chart-3)" },
                      Actual: { label: "Actual", color: "var(--chart-1)" },
                    };

                    return (
                      <Card>
                        <CardHeader>
                          <CardTitle>Profitability Breakdown</CardTitle>
                          <CardDescription>Budget vs Actual — Sales, Costs and Gross Profit</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ChartContainer config={profitabilityChartConfig} className="h-[300px] w-full">
                            <RechartsBarChart
                              data={profitabilityChartData}
                              margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
                              barCategoryGap="20%"
                              barGap={4}
                            >
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis
                                dataKey="metric"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis
                                tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11 }}
                                width={55}
                              />
                              <ChartTooltip
                                cursor={false}
                                content={
                                  <ChartTooltipContent
                                    formatter={(value, name) => (
                                      <span className="font-medium">{name}: {formatCurrency(value as number)}</span>
                                    )}
                                  />
                                }
                              />
                              <ChartLegend content={<ChartLegendContent />} />
                              <Bar dataKey="Budget" fill="var(--color-Budget)" radius={[3, 3, 0, 0]} maxBarSize={48} />
                              <Bar dataKey="Actual"  fill="var(--color-Actual)"  radius={[3, 3, 0, 0]} maxBarSize={48} />
                            </RechartsBarChart>
                          </ChartContainer>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Margin Trend Chart — Task 8 🔒 Blocked on Backend */}
                  {/* TODO Task 8: Wire to GET /api/projects/{projectCode}/financials/monthly-summary when live */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Gross Margin Trend</CardTitle>
                      <CardDescription>
                        Gross margin % per period — project life ({project.startDate ? new Date(project.startDate).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "start"} → {project.endDate ? new Date(Math.min(new Date(project.endDate).getTime(), Date.now())).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "today"})
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px] flex flex-col items-center justify-center gap-3 text-center">
                        <div className="rounded-full bg-muted p-4">
                          <TrendingUp className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">No period data yet</p>
                          <p className="text-xs text-muted-foreground mt-1 max-w-[260px]">
                            Monthly gross margin breakdown will appear here as the project progresses.
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 px-3 py-1.5 text-xs text-amber-700 dark:text-amber-400">
                          <span className="font-medium">Overall GM%</span>
                          <span className="text-amber-500">·</span>
                          <span className="font-semibold">
                            {project.actualSales > 0
                              ? `${(((project.actualSales - (budgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0)) / project.actualSales) * 100).toFixed(1)}%`
                              : "—"}
                          </span>
                          <span className="text-muted-foreground ml-1">(project to date)</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Row 4a — Cost Breakdown by Category (full-width pair) */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Cost Breakdown by Category — Task 9 */}
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle>Cost Breakdown by Category</CardTitle>
                          <CardDescription>Budget vs Actual cost mix — all cost types</CardDescription>
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button className="text-muted-foreground hover:text-foreground transition-colors">
                                <Info className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-[240px] text-xs">
                              Actual costs by category will improve in accuracy once cost category mapping is configured in Settings.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        // Build category data from costBudget.categories — fallback to Labour + Expenses from project
                        const cats = budgetData?.costBudget?.categories ?? [];
                        const hasCostBudget = cats.length > 0;

                        // Chart palette — Evo Design System chart colours (oklch via CSS vars, no hsl() wrapper needed)
                        const chartVars = [
                          "var(--chart-1)",
                          "var(--chart-2)",
                          "var(--chart-3)",
                          "var(--chart-4)",
                          "var(--chart-5)",
                        ];

                        // Categories to render — either from cost budget or legacy two-slice fallback
                        const categories = hasCostBudget
                          ? cats.filter((c) => c.budget > 0 || c.actual > 0)
                          : [
                              { name: "Labour", budget: project.budgetLabour ?? 0, actual: project.actualLabour ?? 0 },
                              { name: "Expenses", budget: project.budgetExpense ?? 0, actual: project.actualExpense ?? 0 },
                            ];

                        if (categories.length === 0) {
                          return (
                            <div className="h-[300px] flex flex-col items-center justify-center gap-2 text-center">
                              <div className="rounded-full bg-muted p-3">
                                <BarChart2 className="h-6 w-6 text-muted-foreground" />
                              </div>
                              <p className="text-sm text-muted-foreground">No cost category data available</p>
                            </div>
                          );
                        }

                        // Build ChartConfig dynamically
                        const chartConfig: ChartConfig = Object.fromEntries(
                          categories.map((cat, i) => [
                            cat.name,
                            { label: cat.name, color: chartVars[i % chartVars.length] },
                          ])
                        );

                        // Two rows of data — Budget and Actual
                        const chartData = [
                          {
                            row: "Budget",
                            ...Object.fromEntries(categories.map((c) => [c.name, c.budget])),
                          },
                          {
                            row: "Actual",
                            ...Object.fromEntries(categories.map((c) => [c.name, c.actual])),
                          },
                        ];

                        const totalBudget = categories.reduce((s, c) => s + c.budget, 0);
                        const totalActual = categories.reduce((s, c) => s + c.actual, 0);

                        return (
                          <>
                            <ChartContainer config={chartConfig} className="h-[200px] w-full">
                              <RechartsBarChart
                                data={chartData}
                                layout="vertical"
                                margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                              >
                                <CartesianGrid horizontal={false} className="stroke-muted" />
                                <XAxis
                                  type="number"
                                  tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`}
                                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <YAxis
                                  type="category"
                                  dataKey="row"
                                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                                  axisLine={false}
                                  tickLine={false}
                                  width={48}
                                />
                                <ChartTooltip
                                  cursor={false}
                                  content={
                                    <ChartTooltipContent
                                      formatter={(value, name) =>
                                        [`${formatCurrency(value as number)}`, name as string]
                                      }
                                    />
                                  }
                                />
                                {categories.map((cat, i) => (
                                  <Bar
                                    key={cat.name}
                                    dataKey={cat.name}
                                    stackId="cost"
                                    fill={chartVars[i % chartVars.length]}
                                    radius={0}
                                    cursor="pointer"
                                    onClick={() => navigate(`/project-transactions?projectCode=${project.code}`)}
                                  />
                                ))}
                              </RechartsBarChart>
                            </ChartContainer>

                            {/* Category legend with budget / actual amounts */}
                            <div className="mt-3 space-y-1.5">
                              {categories.map((cat, i) => (
                                <div
                                  key={cat.name}
                                  className="flex items-center justify-between text-xs cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1 transition-colors"
                                  onClick={() => navigate(`/project-transactions?projectCode=${project.code}`)}
                                >
                                  <div className="flex items-center gap-1.5">
                                    <div
                                      className="h-2.5 w-2.5 rounded-sm flex-shrink-0"
                                      style={{ backgroundColor: chartVars[i % chartVars.length] }}
                                    />
                                    <span className="text-muted-foreground">{cat.name}</span>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="text-muted-foreground">
                                      <SensitiveData>{formatCurrency(cat.budget)}</SensitiveData>
                                    </span>
                                    <span className={`font-medium ${cat.actual > cat.budget ? "text-destructive" : "text-foreground"}`}>
                                      <SensitiveData>{formatCurrency(cat.actual)}</SensitiveData>
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {/* Totals row */}
                              <div className="flex items-center justify-between text-xs pt-1.5 border-t">
                                <span className="font-medium">Total</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-muted-foreground font-medium">
                                    <SensitiveData>{formatCurrency(totalBudget)}</SensitiveData>
                                  </span>
                                  <span className={`font-semibold ${totalActual > totalBudget ? "text-destructive" : "text-foreground"}`}>
                                    <SensitiveData>{formatCurrency(totalActual)}</SensitiveData>
                                  </span>
                                </div>
                              </div>
                              {/* Budget / Actual column headers */}
                              <div className="flex items-center justify-end gap-3 text-[10px] text-muted-foreground pt-0.5">
                                <span>Budget</span>
                                <span>Actual</span>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </CardContent>
                  </Card>

                </div>

                {/* Row 4b — Burn charts: Budget Burn-up + Hours Burndown */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Budget Burn-up Chart — Task 10 */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Budget Burn-up</CardTitle>
                      <CardDescription>Cumulative spend vs planned S-curve and EAC forecast</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        {(() => {
                          // --- Data preparation ---
                          // Use enrichedBudgetData (memoized, includes local edits) with multiple fallbacks for budget
                          const costTotalsBudget = enrichedBudgetData?.costBudget?.totals?.budget;
                          const catSum = enrichedBudgetData?.costBudget?.categories?.reduce((s, c) => s + (c.budget ?? 0), 0) ?? 0;
                          const legacyBudget = (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0);
                          const totalBudget = (costTotalsBudget && costTotalsBudget > 0)
                            ? costTotalsBudget
                            : catSum > 0
                              ? catSum
                              : legacyBudget;

                          // Guard: if we truly have no budget data yet, show empty state
                          if (!totalBudget || totalBudget <= 0) {
                            return (
                              <div className="h-[300px] flex flex-col items-center justify-center gap-2 text-center">
                                <div className="rounded-full bg-muted p-3">
                                  <TrendingUp className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <p className="text-sm text-muted-foreground">No budget data available to plot burn-up</p>
                              </div>
                            );
                          }

                          const startDate = project.startDate ? new Date(project.startDate) : new Date();
                          const endDate = project.endDate ? new Date(project.endDate) : new Date(startDate.getTime() + 365 * 86400000);
                          const today = new Date();

                          // Total project duration in months (at least 1)
                          const totalMonths = Math.max(
                            1,
                            (endDate.getFullYear() - startDate.getFullYear()) * 12 +
                              (endDate.getMonth() - startDate.getMonth()),
                          );

                          // Minimum 60px per month so labels never crush on long projects
                          const chartWidth = Math.max(600, totalMonths * 60);

                          // EAC: if we have completion % and actual costs, project final cost
                          const actualCosts = enrichedBudgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0;
                          const completionPct = effectiveCompletionPercent > 0 ? effectiveCompletionPercent / 100 : null;
                          const eacTotal = completionPct && completionPct > 0
                            ? actualCosts / completionPct
                            : null;

                          // Find the "today" period index (0-based month offset from start)
                          const todayMonthOffset = Math.min(
                            totalMonths,
                            Math.max(
                              0,
                              (today.getFullYear() - startDate.getFullYear()) * 12 +
                                (today.getMonth() - startDate.getMonth()),
                            ),
                          );

                          const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

                          const dataPoints: { month: string; planned: number; eac: number | null; budget: number; isToday: boolean }[] = [];

                          for (let i = 0; i <= totalMonths; i++) {
                            const date = new Date(startDate);
                            date.setMonth(date.getMonth() + i);

                            // S-curve: budget × (3t² - 2t³) where t = i / totalMonths
                            const t = i / totalMonths;
                            const planned = totalBudget * (3 * t * t - 2 * t * t * t);

                            // EAC line: only show from today onwards as a dashed projection
                            // Before today it mirrors actuals (which are pending backend); after today it projects linearly
                            let eacValue: number | null = null;
                            if (eacTotal !== null) {
                              const eacT = i / totalMonths;
                              eacValue = eacTotal * (3 * eacT * eacT - 2 * eacT * eacT * eacT);
                            }

                            dataPoints.push({
                              month: `${monthNames[date.getMonth()]} ${date.getFullYear().toString().slice(2)}`,
                              planned: Math.round(planned),
                              eac: eacValue !== null ? Math.round(eacValue) : null,
                              budget: totalBudget,
                              isToday: i === todayMonthOffset,
                            });
                          }

                          // Find the label of the "today" data point for the vertical reference line
                          const todayLabel = dataPoints[todayMonthOffset]?.month ?? "";

                          const burnUpChartConfig: ChartConfig = {
                            planned: { label: "Planned Spend",  color: "hsl(var(--chart-1))" },
                            eac:     { label: "EAC Forecast",   color: "hsl(var(--chart-3))" },
                            today:   { label: "Today",          color: "hsl(var(--chart-2))" },
                          };

                          const burnUpChartId = "burn-up-chart";

                          return (
                            <div data-chart={burnUpChartId} style={{ minWidth: chartWidth, height: 300 }}>
                              <ChartStyle id={burnUpChartId} config={burnUpChartConfig} />
                              <ResponsiveContainer width="100%" height={300}>
                              <RechartsLineChart
                                data={dataPoints}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis
                                  dataKey="month"
                                  className="text-xs"
                                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                                  interval={0}
                                />
                                <YAxis
                                  className="text-xs"
                                  tick={{ fill: "hsl(var(--muted-foreground))" }}
                                  tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                                />
                                <RechartsTooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      const d = payload[0].payload as BurnPoint;
                                      return (
                                        <div className="rounded-lg border bg-background p-2 shadow-sm text-xs">
                                          <p className="font-semibold mb-1">{d.month}{d.isToday ? " (Today)" : ""}</p>
                                          <p style={{ color: "hsl(var(--chart-1))" }}>
                                            Planned: {formatCurrency(d.planned)}
                                          </p>
                                          {d.eac !== null && (
                                            <p style={{ color: "hsl(var(--chart-3))" }}>
                                              EAC Forecast: {formatCurrency(d.eac)}
                                            </p>
                                          )}
                                          <p className="text-muted-foreground">
                                            Budget Ceiling: {formatCurrency(d.budget)}
                                          </p>
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                {/* Budget ceiling horizontal reference line */}
                                <ReferenceLine
                                  y={totalBudget}
                                  stroke="hsl(var(--muted-foreground))"
                                  strokeDasharray="4 4"
                                  label={{
                                    value: "Budget",
                                    position: "insideTopRight",
                                    fill: "hsl(var(--muted-foreground))",
                                    fontSize: 11,
                                  }}
                                />
                                {/* Today vertical marker */}
                                <ReferenceLine
                                  x={todayLabel}
                                  stroke="var(--color-today)"
                                  strokeDasharray="4 4"
                                  strokeWidth={1.5}
                                  label={{
                                    value: "Today",
                                    position: "insideTopLeft",
                                    fill: "hsl(var(--chart-2))",
                                    fontSize: 11,
                                  }}
                                />
                                {/* Planned S-curve */}
                                <Line
                                  type="monotone"
                                  dataKey="planned"
                                  stroke="var(--color-planned)"
                                  strokeWidth={2}
                                  dot={false}
                                  name="Planned Spend"
                                />
                                {/* EAC forecast — dashed */}
                                <Line
                                  type="monotone"
                                  dataKey="eac"
                                  stroke="var(--color-eac)"
                                  strokeWidth={2}
                                  strokeDasharray="6 3"
                                  dot={false}
                                  name="EAC Forecast"
                                  connectNulls={false}
                                />
                              </RechartsLineChart>
                              </ResponsiveContainer>
                            </div>
                          );
                        })()}
                      </div>

                    </CardContent>
                  </Card>

                  {/* Hours Burndown Chart — Task 11 */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Hours Burndown</CardTitle>
                      <CardDescription>Remaining hours vs plan — tracking to budget</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        {(() => {
                          const budgetHours = project.budgetHours ?? 0;
                          const actualHours = project.actualHours ?? 0;

                          if (!budgetHours || budgetHours <= 0) {
                            return (
                              <div className="h-[300px] flex flex-col items-center justify-center gap-2 text-center">
                                <div className="rounded-full bg-muted p-3">
                                  <TrendingUp className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <p className="text-sm text-muted-foreground">No hours budget set for this project</p>
                              </div>
                            );
                          }

                          const startDate = project.startDate ? new Date(project.startDate) : new Date();
                          const endDate = project.endDate
                            ? new Date(project.endDate)
                            : new Date(startDate.getTime() + 365 * 86400000);
                          const today = new Date();

                          const totalMonths = Math.max(
                            1,
                            (endDate.getFullYear() - startDate.getFullYear()) * 12 +
                              (endDate.getMonth() - startDate.getMonth()),
                          );

                          // Minimum 60px per month so labels never crush on long projects
                          const chartWidth = Math.max(600, totalMonths * 60);

                          // Build cumulative planned hours per period from monthlyAllocations in
                          // the resource budget. Fall back to a linear ramp if not available.
                          const roleBudgets = enrichedBudgetData?.resourceBudget?.byRole ?? [];
                          const hasMonthlyData = roleBudgets.some(
                            (r) => r.monthlyHours && r.monthlyHours.length > 0,
                          );

                          // Aggregate monthly planned hours across all roles (array index = month 0-based from project start)
                          let monthlyPlanned: number[] = Array(totalMonths + 1).fill(0);
                          if (hasMonthlyData) {
                            roleBudgets.forEach((role) => {
                              if (role.monthlyHours) {
                                role.monthlyHours.forEach((hrs, idx) => {
                                  if (idx <= totalMonths) {
                                    monthlyPlanned[idx] = (monthlyPlanned[idx] ?? 0) + hrs;
                                  }
                                });
                              }
                            });
                          } else {
                            // Linear distribution fallback
                            const hoursPerMonth = budgetHours / totalMonths;
                            monthlyPlanned = Array(totalMonths + 1)
                              .fill(0)
                              .map((_, i) => (i === 0 ? 0 : hoursPerMonth));
                          }

                          // EAC: forecastAtCompletionHours — use burn-rate projection from available data
                          // actualHours / completionPct gives EAC; fall back to null if no completion data
                          const completionPct =
                            effectiveCompletionPercent > 0 ? effectiveCompletionPercent / 100 : null;
                          const eacTotalHours =
                            completionPct && completionPct > 0 && actualHours > 0
                              ? actualHours / completionPct
                              : null;

                          // Today month offset
                          const todayMonthOffset = Math.min(
                            totalMonths,
                            Math.max(
                              0,
                              (today.getFullYear() - startDate.getFullYear()) * 12 +
                                (today.getMonth() - startDate.getMonth()),
                            ),
                          );

                          const monthNames = [
                            "Jan","Feb","Mar","Apr","May","Jun",
                            "Jul","Aug","Sep","Oct","Nov","Dec",
                          ];

                          // Build data points — remaining hours counts DOWN from budgetHours to 0
                          let cumulativePlanned = 0;
                          const dataPoints: HoursBurndownPoint[] = [];

                          for (let i = 0; i <= totalMonths; i++) {
                            const date = new Date(startDate);
                            date.setMonth(date.getMonth() + i);

                            cumulativePlanned += monthlyPlanned[i] ?? 0;
                            const plannedRemaining = Math.max(0, budgetHours - cumulativePlanned);

                            // EAC remaining: project from EAC total using same S-curve shape as planned
                            let eacRemaining: number | null = null;
                            if (eacTotalHours !== null) {
                              const t = totalMonths > 0 ? i / totalMonths : 1;
                              const eacConsumed = eacTotalHours * (3 * t * t - 2 * t * t * t);
                              eacRemaining = Math.max(0, eacTotalHours - eacConsumed);
                            }

                            dataPoints.push({
                              month: `${monthNames[date.getMonth()]} ${date.getFullYear().toString().slice(2)}`,
                              planned: Math.round(plannedRemaining),
                              eac: eacRemaining !== null ? Math.round(eacRemaining) : null,
                              isToday: i === todayMonthOffset,
                            });
                          }

                          const todayLabel = dataPoints[todayMonthOffset]?.month ?? "";

                          const burnDownChartConfig: ChartConfig = {
                            planned: { label: "Planned Remaining", color: "hsl(var(--chart-1))" },
                            eac:     { label: "EAC Forecast",      color: "hsl(var(--chart-3))" },
                            today:   { label: "Today",             color: "hsl(var(--chart-2))" },
                          };

                          const burnDownChartId = "burn-down-chart";

                          return (
                            <div data-chart={burnDownChartId} style={{ minWidth: chartWidth, height: 300 }}>
                              <ChartStyle id={burnDownChartId} config={burnDownChartConfig} />
                              <ResponsiveContainer width="100%" height={300}>
                              <RechartsLineChart
                                data={dataPoints}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis
                                  dataKey="month"
                                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                                  interval={0}
                                />
                                <YAxis
                                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                                  tickFormatter={(v) => `${v}h`}
                                />
                                <RechartsTooltip
                                  content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                      const d = payload[0].payload as HoursBurndownPoint;
                                      return (
                                        <div className="rounded-lg border bg-background p-2 shadow-sm text-xs">
                                          <p className="font-semibold mb-1">
                                            {d.month}
                                            {d.isToday ? " (Today)" : ""}
                                          </p>
                                          <p style={{ color: "hsl(var(--chart-1))" }}>
                                            Planned remaining: {d.planned}h
                                          </p>
                                          {d.eac !== null && (
                                            <p style={{ color: "hsl(var(--chart-3))" }}>
                                              EAC remaining: {d.eac}h
                                            </p>
                                          )}
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                {/* Zero reference line — budget exhausted */}
                                <ReferenceLine
                                  y={0}
                                  stroke="hsl(var(--muted-foreground))"
                                  strokeDasharray="4 4"
                                  label={{
                                    value: "Budget exhausted",
                                    position: "insideBottomRight",
                                    fill: "hsl(var(--muted-foreground))",
                                    fontSize: 10,
                                  }}
                                />
                                {/* Today vertical marker */}
                                <ReferenceLine
                                  x={todayLabel}
                                  stroke="var(--color-today)"
                                  strokeDasharray="4 4"
                                  strokeWidth={1.5}
                                  label={{
                                    value: "Today",
                                    position: "insideTopLeft",
                                    fill: "hsl(var(--chart-2))",
                                    fontSize: 11,
                                  }}
                                />
                                {/* Planned remaining hours — solid */}
                                <Line
                                  type="monotone"
                                  dataKey="planned"
                                  stroke="var(--color-planned)"
                                  strokeWidth={2}
                                  dot={false}
                                  name="Planned Remaining"
                                />
                                {/* EAC forecast — dashed */}
                                <Line
                                  type="monotone"
                                  dataKey="eac"
                                  stroke="var(--color-eac)"
                                  strokeWidth={2}
                                  strokeDasharray="6 3"
                                  dot={false}
                                  name="EAC Forecast"
                                  connectNulls={false}
                                />
                              </RechartsLineChart>
                              </ResponsiveContainer>
                            </div>
                          );
                        })()}
                      </div>

                    </CardContent>
                  </Card>
                </div>

                {/* Row 5 — Full width Budget vs Actual Table */}
                <div className="grid gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Budget vs Actual</CardTitle>
                      <CardDescription>Detailed variance analysis</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        {/* EAC helper — derived once, used across all rows */}
                        {(() => {
                          const completionPct = effectiveCompletionPercent > 0 ? effectiveCompletionPercent / 100 : 0;

                          // EAC: Revenue
                          const eacRevenue = project.type === 'fixed-price'
                            ? project.budgetRevenue
                            : (completionPct > 0 ? (project.actualSales / completionPct) : project.budgetRevenue);

                          // EAC: Total Costs
                          const costBudgetTotalsActual = enrichedBudgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0;
                          const costBudgetTotalsBudget = enrichedBudgetData?.costBudget?.totals?.budget ?? 0;
                          const eacTotalCosts = completionPct > 0
                            ? costBudgetTotalsActual / completionPct
                            : costBudgetTotalsBudget;

                          // EAC: Gross Profit = EAC Revenue − EAC Total Costs
                          const eacGrossProfit = eacRevenue - eacTotalCosts;

                          // EAC: Gross Margin % = (EAC GP / EAC Revenue) × 100
                          const eacGrossMarginPct = eacRevenue !== 0 ? (eacGrossProfit / eacRevenue) * 100 : 0;

                          // EAC: Hours — burn-rate projection
                          const actualHours = project.actualHours ?? 0;
                          const eacHours = completionPct > 0 && actualHours > 0
                            ? actualHours / completionPct
                            : project.budgetHours;

                          return (
                            <>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Category</TableHead>
                                  <TableHead className="text-right">
                                    <span className="inline-flex items-center gap-1.5">
                                      Budget
                                      {/* Change Control indicator stub — renders only when backend
                                          supplies project.originalBudget (🔒 blocked on backend).
                                          When the field is absent (today) this renders nothing. */}
                                      {project.originalBudget != null &&
                                        project.originalBudget !== project.budgetRevenue && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center gap-0.5 rounded-sm bg-amber-100 px-1 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 cursor-help">
                                                  Revised ↑
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent side="top" className="text-xs max-w-[220px]">
                                                Original approved budget:{' '}
                                                {formatCurrency(project.originalBudget)}. Current budget
                                                reflects approved change controls.
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                    </span>
                                  </TableHead>
                                  <TableHead className="text-right">Actual</TableHead>
                                  <TableHead className="text-right">Variance (£)</TableHead>
                                  <TableHead className="text-right">Variance %</TableHead>
                                  <TableHead className="text-right">
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="cursor-help underline decoration-dotted underline-offset-2">EAC</span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="text-xs">
                                          Forecast at Completion
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {/* Revenue */}
                                <TableRow>
                                  <TableCell>
                                    <button
                                      onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=S`)}
                                      className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                                    >
                                      Sales
                                    </button>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <SensitiveData>{formatCurrency(project.budgetRevenue)}</SensitiveData>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      const pct = project.budgetRevenue !== 0
                                        ? ((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue) * 100
                                        : 0;
                                      return (
                                        // Revenue: positive variance (over-billing) is good → invert sign for colour logic
                                        <span className={getVarianceColor(-pct)}>
                                          <SensitiveData>
                                            {formatCurrency(project.actualRevenue - project.budgetRevenue)}
                                          </SensitiveData>
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      const pct = project.budgetRevenue !== 0
                                        ? ((project.actualRevenue - project.budgetRevenue) / project.budgetRevenue) * 100
                                        : 0;
                                      return (
                                        // Revenue: positive variance (over-billing) is good → invert sign for colour logic
                                        <span className={getVarianceColor(-pct)}>
                                          {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <SensitiveData>{formatCurrency(eacRevenue)}</SensitiveData>
                                  </TableCell>
                                </TableRow>

                                {/* Total Costs */}
                                <TableRow>
                                  <TableCell>
                                    <button
                                      onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=C`)}
                                      className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
                                    >
                                      Total Costs
                                    </button>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <SensitiveData>
                                      {formatCurrency(costBudgetTotalsBudget)}
                                    </SensitiveData>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <SensitiveData>{formatCurrency(project.actualCosts)}</SensitiveData>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      const pct = costBudgetTotalsBudget !== 0
                                        ? ((project.actualCosts - costBudgetTotalsBudget) / costBudgetTotalsBudget) * 100
                                        : 0;
                                      return (
                                        <span className={getVarianceColor(pct)}>
                                          <SensitiveData>
                                            {formatCurrency(project.actualCosts - costBudgetTotalsBudget)}
                                          </SensitiveData>
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      const pct = costBudgetTotalsBudget !== 0
                                        ? ((project.actualCosts - costBudgetTotalsBudget) / costBudgetTotalsBudget) * 100
                                        : 0;
                                      return (
                                        <span className={getVarianceColor(pct)}>
                                          {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <SensitiveData>{formatCurrency(eacTotalCosts)}</SensitiveData>
                                  </TableCell>
                                </TableRow>

                                {/* Gross Profit — subtotal row, visually separated */}
                                <TableRow className="border-t bg-muted/40">
                                  <TableCell className="font-semibold">Gross Profit</TableCell>
                                  <TableCell className="text-right font-semibold">
                                    <SensitiveData>
                                      {formatCurrency(project.budgetRevenue - costBudgetTotalsBudget)}
                                    </SensitiveData>
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    <SensitiveData>
                                      {formatCurrency(project.actualSales - project.actualCosts)}
                                    </SensitiveData>
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    {(() => {
                                      const budgetGP = project.budgetRevenue - costBudgetTotalsBudget;
                                      const actualGP = project.actualSales - project.actualCosts;
                                      const pct = budgetGP !== 0 ? ((actualGP - budgetGP) / budgetGP) * 100 : 0;
                                      return (
                                        // Gross Profit: positive variance (beating plan) is good → invert sign for colour logic
                                        <span className={getVarianceColor(-pct)}>
                                          <SensitiveData>
                                            {formatCurrency(actualGP - budgetGP)}
                                          </SensitiveData>
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    {(() => {
                                      const budgetGP = project.budgetRevenue - costBudgetTotalsBudget;
                                      const actualGP = project.actualSales - project.actualCosts;
                                      const pct = budgetGP !== 0 ? ((actualGP - budgetGP) / budgetGP) * 100 : 0;
                                      return (
                                        // Gross Profit: positive variance (beating plan) is good → invert sign for colour logic
                                        <span className={getVarianceColor(-pct)}>
                                          {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    <SensitiveData>{formatCurrency(eacGrossProfit)}</SensitiveData>
                                  </TableCell>
                                </TableRow>

                                {/* Gross Margin % — sub-row beneath Gross Profit */}
                                <TableRow className="bg-muted/40">
                                  <TableCell className="font-semibold pl-6 text-muted-foreground">Gross Margin %</TableCell>
                                  <TableCell className="text-right font-semibold text-muted-foreground">
                                    {(() => {
                                      const budgetGP = project.budgetRevenue - costBudgetTotalsBudget;
                                      const budgetGMPct = project.budgetRevenue !== 0
                                        ? (budgetGP / project.budgetRevenue) * 100
                                        : 0;
                                      return `${budgetGMPct.toFixed(1)}%`;
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold text-muted-foreground">
                                    {(() => {
                                      const actualGP = project.actualSales - project.actualCosts;
                                      const actualGMPct = project.actualSales !== 0
                                        ? (actualGP / project.actualSales) * 100
                                        : 0;
                                      return `${actualGMPct.toFixed(1)}%`;
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    {(() => {
                                      const budgetGP = project.budgetRevenue - costBudgetTotalsBudget;
                                      const actualGP = project.actualSales - project.actualCosts;
                                      const budgetGMPct = project.budgetRevenue !== 0
                                        ? (budgetGP / project.budgetRevenue) * 100
                                        : 0;
                                      const actualGMPct = project.actualSales !== 0
                                        ? (actualGP / project.actualSales) * 100
                                        : 0;
                                      const ppVariance = actualGMPct - budgetGMPct;
                                      return (
                                        // Higher margin than budget is good → invert sign for colour logic
                                        <span className={getVarianceColor(-ppVariance)}>
                                          {ppVariance >= 0 ? '+' : ''}{ppVariance.toFixed(1)}pp
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  {/* Variance % — not applicable for a % row */}
                                  <TableCell className="text-right text-muted-foreground">—</TableCell>
                                  <TableCell className="text-right font-semibold text-muted-foreground">
                                    {eacRevenue !== 0
                                      ? `${eacGrossMarginPct.toFixed(1)}%`
                                      : '—'}
                                  </TableCell>
                                </TableRow>

                                {/* Hours — separate section below profitability */}
                                <TableRow className="border-t">
                                  <TableCell>Hours</TableCell>
                                  <TableCell className="text-right">
                                    {project.budgetHours != null ? `${project.budgetHours.toLocaleString()}h` : '—'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {project.actualHours != null ? `${(project.actualHours ?? 0).toLocaleString()}h` : '—'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      const budgetH = project.budgetHours ?? 0;
                                      const actualH = project.actualHours ?? 0;
                                      const variance = actualH - budgetH;
                                      return (
                                        // Over-hours is bad → positive variance = red (same as cost rows)
                                        <span className={getVarianceColor(budgetH !== 0 ? (variance / budgetH) * 100 : 0)}>
                                          {variance >= 0 ? '+' : ''}{variance.toLocaleString()}h
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {(() => {
                                      const budgetH = project.budgetHours ?? 0;
                                      const actualH = project.actualHours ?? 0;
                                      const variance = actualH - budgetH;
                                      const pct = budgetH !== 0 ? (variance / budgetH) * 100 : 0;
                                      return (
                                        // Over-hours is bad → positive variance = red
                                        <span className={getVarianceColor(pct)}>
                                          {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                                        </span>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {eacHours != null ? `${Math.round(eacHours).toLocaleString()}h` : '—'}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </>
                          );
                        })()}
                      </Table>
                    </CardContent>
                  </Card>
                </div>

                    </div>
                  )}{/* end Financial Analysis expanded body */}
                </div>{/* end Financial Analysis collapsible */}

                {/* Row 6 — EVM / Earned Value Analysis (collapsible) */}
                {(() => {
                  const completionPct = effectiveCompletionPercent > 0 ? effectiveCompletionPercent / 100 : 0;
                  const bac = enrichedBudgetData?.costBudget?.totals?.budget ?? 0;
                  const actualCost = enrichedBudgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0;

                  // Planned Value — proportional to time elapsed through the project
                  const projectStart = project.startDate ? new Date(project.startDate).getTime() : null;
                  const projectEnd = project.endDate ? new Date(project.endDate).getTime() : null;
                  const now = Date.now();
                  const timeElapsedPct = projectStart && projectEnd && projectEnd > projectStart
                    ? Math.min(1, Math.max(0, (now - projectStart) / (projectEnd - projectStart)))
                    : null;
                  const plannedValue = timeElapsedPct !== null ? bac * timeElapsedPct : null;

                  // Earned Value — how much of the budget has been "earned" by work done
                  const earnedValue = bac * completionPct;

                  // Indices
                  const cpi = actualCost > 0 ? earnedValue / actualCost : null;
                  const spi = plannedValue && plannedValue > 0 ? earnedValue / plannedValue : null;

                  // EAC & VAC
                  const eacEvm = cpi && cpi > 0 ? bac / cpi : (completionPct > 0 ? actualCost / completionPct : bac);
                  const vac = bac - eacEvm;

                  // TCPI — To-Complete Performance Index: efficiency needed on remaining work to meet BAC
                  const remainingWork = bac - earnedValue;
                  const remainingBudget = bac - actualCost;
                  const tcpi = remainingWork > 0 && remainingBudget !== 0 ? remainingWork / remainingBudget : null;

                  const noData = bac === 0 || effectiveCompletionPercent === 0;

                  const indexBadge = (value: number | null, label: string, invertGood = false) => {
                    if (value === null) return (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</span>
                        <span className="text-lg font-semibold text-muted-foreground">—</span>
                      </div>
                    );
                    // > 1.0 = good (green), 0.85–1.0 = amber, < 0.85 = red
                    // invertGood flips the logic (not used here but kept for flexibility)
                    const good = invertGood ? value < 1 : value >= 1.0;
                    const ok = !good && (invertGood ? value <= 1.15 : value >= 0.85);
                    const colour = good ? 'text-green-600 dark:text-green-400' : ok ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';
                    const bg = good ? 'bg-green-50 dark:bg-green-900/20' : ok ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-red-50 dark:bg-red-900/20';
                    return (
                      <div className={`flex flex-col items-center gap-0.5 rounded-lg px-4 py-2.5 ${bg}`}>
                        <span className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">{label}</span>
                        <span className={`text-xl font-bold ${colour}`}>{value.toFixed(2)}</span>
                        <span className={`text-[10px] font-medium ${colour}`}>
                          {good ? (label === 'TCPI' ? 'Achievable' : 'On Track') : ok ? 'At Risk' : 'Critical'}
                        </span>
                      </div>
                    );
                  };

                  return (
                    <div className="border rounded-lg overflow-hidden">
                      {/* Header / toggle */}
                      <button
                        type="button"
                        onClick={() => setEvmExpanded(e => !e)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
                      >
                        <div className="flex items-center gap-2">
                          <Activity className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-semibold">Earned Value Analysis</span>
                          <span className="text-xs text-muted-foreground">(EVM — CPI / SPI)</span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild onClick={e => e.stopPropagation()}>
                                <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 cursor-pointer text-[10px] font-bold leading-none select-none">i</span>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" align="start" className="max-w-xs text-xs space-y-1.5 p-3">
                                <p className="font-semibold text-foreground mb-1">How to read these metrics</p>
                                <p><span className="font-medium text-foreground">CPI &gt; 1.0</span> — Each £1 spent is delivering more than £1 of value. Project is cost-efficient.</p>
                                <p><span className="font-medium text-foreground">CPI &lt; 1.0</span> — Each £1 spent is delivering less than £1 of value. Project will likely overrun.</p>
                                <p><span className="font-medium text-foreground">SPI &gt; 1.0</span> — More work has been completed than was planned for this point in time.</p>
                                <p><span className="font-medium text-foreground">SPI &lt; 1.0</span> — The project is behind schedule relative to the project timeline.</p>
                                <p><span className="font-medium text-foreground">TCPI &gt; 1.0</span> — The remaining work must be delivered more efficiently than it has been to date to stay on budget.</p>
                                <p className="pt-1 italic text-muted-foreground">EVM accuracy depends on % complete being kept up to date. SPI requires project start and end dates.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {!noData && cpi !== null && (
                            <span className={`ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                              cpi >= 1.0 ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                              : cpi >= 0.85 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                            }`}>
                              CPI {cpi.toFixed(2)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          {evmExpanded ? 'Hide' : 'Show'}
                          {evmExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </button>

                      {/* Expanded body */}
                      {evmExpanded && (
                        <div className="px-4 py-4 space-y-5">
                          {noData ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              EVM metrics require a cost budget and a % complete value greater than 0.
                            </p>
                          ) : (
                            <>
                              {/* Index row */}
                              <div className="flex flex-wrap gap-3 justify-center">
                                {indexBadge(cpi, 'CPI')}
                                {indexBadge(spi, 'SPI')}
                                {indexBadge(tcpi, 'TCPI')}
                              </div>

                              {/* EVM figures grid */}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                {/* BAC */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">BAC — Budget at Completion</p>
                                  <p className="text-base font-semibold">{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(bac)}</p>
                                  <p className="text-[11px] text-muted-foreground">Authorised total budget</p>
                                </div>
                                {/* EV */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">EV — Earned Value</p>
                                  <p className="text-base font-semibold">{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(earnedValue)}</p>
                                  <p className="text-[11px] text-muted-foreground">Value of work completed ({effectiveCompletionPercent}%)</p>
                                </div>
                                {/* PV */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">PV — Planned Value</p>
                                  <p className="text-base font-semibold">
                                    {plannedValue !== null
                                      ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(plannedValue)
                                      : '—'}
                                  </p>
                                  <p className="text-[11px] text-muted-foreground">
                                    {timeElapsedPct !== null ? `${Math.round(timeElapsedPct * 100)}% of project time elapsed` : 'No project dates set'}
                                  </p>
                                </div>
                                {/* AC */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">AC — Actual Cost</p>
                                  <p className="text-base font-semibold">{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(actualCost)}</p>
                                  <p className="text-[11px] text-muted-foreground">Total cost incurred to date</p>
                                </div>
                                {/* EAC */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">EAC — Estimate at Completion</p>
                                  <p className={`text-base font-semibold ${eacEvm > bac ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                    {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(eacEvm)}
                                  </p>
                                  <p className="text-[11px] text-muted-foreground">Projected final cost (BAC ÷ CPI)</p>
                                </div>
                                {/* VAC */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">VAC — Variance at Completion</p>
                                  <p className={`text-base font-semibold ${vac >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {vac >= 0 ? '+' : ''}{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(vac)}
                                  </p>
                                  <p className="text-[11px] text-muted-foreground">Projected over / under-run (BAC − EAC)</p>
                                </div>
                                {/* CV */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">CV — Cost Variance</p>
                                  {(() => {
                                    const cv = earnedValue - actualCost;
                                    return (
                                      <>
                                        <p className={`text-base font-semibold ${cv >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                          {cv >= 0 ? '+' : ''}{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(cv)}
                                        </p>
                                        <p className="text-[11px] text-muted-foreground">EV − AC (positive = under budget)</p>
                                      </>
                                    );
                                  })()}
                                </div>
                                {/* SV */}
                                <div className="rounded-lg border p-3 space-y-0.5">
                                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">SV — Schedule Variance</p>
                                  {(() => {
                                    const sv = plannedValue !== null ? earnedValue - plannedValue : null;
                                    return sv !== null ? (
                                      <>
                                        <p className={`text-base font-semibold ${sv >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                          {sv >= 0 ? '+' : ''}{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(sv)}
                                        </p>
                                        <p className="text-[11px] text-muted-foreground">EV − PV (positive = ahead of plan)</p>
                                      </>
                                    ) : (
                                      <>
                                        <p className="text-base font-semibold text-muted-foreground">—</p>
                                        <p className="text-[11px] text-muted-foreground">Requires project start/end dates</p>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>


                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

              </TabsContent>

              {/* Billing Tab */}
              <TabsContent value="billing" className="space-y-4">
                {project?.type === "fixed-price" ? (
                  (() => {
                    // Build BillingMilestone[] — prefer the Sales Budget milestones from
                    // enrichedBudgetData because they carry triggerPercent values (needed for
                    // % complete milestone status derivation in Phase 9). Fall back to the raw
                    // project milestones state only when no Sales Budget data is available.
                    const salesBudgetMilestones = enrichedBudgetData?.salesBudget?.milestones;
                    const billingMilestones: import("@/types/budget.types").BillingMilestone[] =
                      salesBudgetMilestones && salesBudgetMilestones.length > 0
                        ? salesBudgetMilestones
                        : milestones.map((m) => ({
                            id: m.id,
                            name: m.name,
                            amount: m.paymentAmount ?? 0,
                            dueDate: m.dueDate,
                            status: (
                              m.status === "completed"
                                ? "paid"
                                : m.status === "overdue"
                                ? "overdue"
                                : new Date(m.dueDate) < new Date()
                                ? "due"
                                : "pending"
                            ) as import("@/types/budget.types").BillingMilestoneStatus,
                          }));

                    // Use the shared fixedFeeInvoices computed at component level (Phase 16).
                    // This ensures the Sales Budget Revenue Health panel and the Billing Tab
                    // both reference the same invoice data without diverging.

                    const contractValue =
                      enrichedBudgetData?.salesBudget?.contractValue ??
                      project.budgetRevenue ??
                      0;

                    return (
                      <FixedFeeBillingTab
                        contractValue={contractValue}
                        milestones={billingMilestones}
                        invoices={fixedFeeInvoices}
                        projectName={project.name}
                        completionPercent={effectiveCompletionPercent}
                        billingTriggerType={project.billingTriggerType}
                        formatCurrency={formatCurrency}
                        onSettingsClick={() => setBillingSettingsDrawerOpen(true)}
                        costCentreConfigured={!!projectSettings.defaultCostCentreCode}
                        initialStatusFilter={invoiceStatusFilter}
                        onGenerateInvoice={(milestone) => {
                          setFixedFeeMilestone({
                            id: milestone.id,
                            name: milestone.name,
                            amount: milestone.amount,
                            dueDate: milestone.dueDate,
                          });
                          setInvoiceWizardOpen(true);
                        }}
                      />
                    );
                  })()
                ) : project?.type !== "time-and-materials" ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <div className="rounded-full bg-muted p-3 mb-4">
                        <Receipt className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">Coming Soon</h3>
                      <p className="text-sm text-muted-foreground text-center max-w-md">
                        Billing features for {formatProjectType(project?.type || "")} projects are currently under
                        development.
                        <br />
                        Time & Materials project billing is available now.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {/* Billing Sub-tabs */}
                    <Tabs defaultValue="invoices" className="w-full">
                      <TabsList className="grid w-full max-w-md grid-cols-2">
                        <TabsTrigger value="invoices">Invoices</TabsTrigger>
                        <TabsTrigger value="unbilled">Unbilled Work</TabsTrigger>
                      </TabsList>

                      {/* Invoices Tab Content */}
                      <TabsContent value="invoices" className="space-y-4 mt-4">
                        {/* Billing Summary - One Line */}
                        {(() => {
                          // Compute Invoiced from posted/paid invoices (exclude Draft=0 and Void=5)
                          const invoicedTotal = billingInvoices
                            .filter((inv) => inv.status !== 0 && inv.status !== 5)
                            .reduce((sum, inv) => sum + inv.subtotal, 0);
                          const wipTotal = wipSummary.totalNetAmount;
                          const readyToBill = wipTotal;
                          const budgetRevenue = project.budgetRevenue;
                          const billingPercent = budgetRevenue > 0 ? (invoicedTotal / budgetRevenue) * 100 : 0;
                          const completionPercent = project.completionPercent || 0;
                          const billingGap = billingPercent - completionPercent;
                          return (
                        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                          <div className="flex items-center gap-8 flex-1">
                            <div>
                              <span className="text-sm text-neutral-600">Budget:</span>
                              <span className="ml-2 text-base font-semibold text-neutral-900">
                                <SensitiveData>{formatCurrency(budgetRevenue)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Invoiced:</span>
                              <span className="ml-2 text-base font-semibold text-neutral-900">
                                <SensitiveData>{formatCurrency(invoicedTotal)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">WIP:</span>
                              <span className="ml-2 text-base font-semibold text-warning-600">
                                <SensitiveData>{formatCurrency(wipTotal)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Ready to Bill:</span>
                              <span className="ml-2 text-base font-semibold text-success-600">
                                <SensitiveData>{formatCurrency(readyToBill)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Status:</span>
                              <span className="ml-2">
                                {billingLoading ? (
                                  <Badge variant="outline">Loading...</Badge>
                                ) : billingGap < -10 ? (
                                  <Badge variant="destructive">Under-billing</Badge>
                                ) : billingGap < -5 ? (
                                  <Badge variant="warning">Review</Badge>
                                ) : (
                                  <Badge variant="success">On track</Badge>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                          );
                        })()}

                        {/* Invoice History Table */}
                        <Card>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle>Invoice History</CardTitle>
                                <CardDescription>All invoices raised for this project</CardDescription>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => setBillingSettingsDrawerOpen(true)}>
                                  <Settings2 className="h-4 w-4 mr-2" />
                                  Billing Settings
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setInvoiceColumnConfigOpen(true)}>
                                  <Settings2 className="h-4 w-4 mr-2" />
                                  Configure
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {/* Filters and Search */}
                            <div className="flex items-center gap-4 mb-4">
                              <div className="relative flex-1 max-w-sm">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                  placeholder="Search invoices..."
                                  value={invoiceSearchQuery}
                                  onChange={(e) => {
                                    setInvoiceSearchQuery(e.target.value);
                                    setInvoiceCurrentPage(1); // Reset to first page when searching
                                  }}
                                  className="pl-9 pr-9"
                                />
                                {invoiceSearchQuery && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                                    onClick={() => {
                                      setInvoiceSearchQuery("");
                                      setInvoiceCurrentPage(1);
                                    }}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                              
                              <Select
                                value={invoiceStatusFilter}
                                onValueChange={(value) => {
                                  setInvoiceStatusFilter(value);
                                  setInvoiceCurrentPage(1); // Reset to first page when filtering
                                }}
                              >
                                <SelectTrigger id="status-filter" className="w-[180px]">
                                  <SelectValue placeholder="All Statuses" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All Statuses</SelectItem>
                                  <SelectItem value="Draft">Draft</SelectItem>
                                  <SelectItem value="Sent">Sent</SelectItem>
                                  <SelectItem value="Paid">Paid</SelectItem>
                                  <SelectItem value="Overdue">Overdue</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {(() => {
                              // Invoice data - uses API data in live mode, mock data in demo mode
                              const allInvoices = (dataMode === "live")
                                ? billingInvoices.map((inv) => ({
                                    id: inv.id,
                                    invoiceNumber: inv.invoiceNumber || null,
                                    date: inv.invoiceDate ? inv.invoiceDate.split("T")[0] : null,
                                    description: inv.description || "",
                                    amount: inv.grossTotal,
                                    created: inv.createdBy ? inv.invoiceDate?.split("T")[0] || null : null,
                                    due: inv.dueDate ? inv.dueDate.split("T")[0] : null,
                                    status: mapInvoiceStatus(inv.status) as "Draft" | "Sent" | "Paid" | "Overdue" | "PartiallyPaid" | "Void" | "WriteOff",
                                  }))
                                : [
                                {
                                  id: 1,
                                  invoiceNumber: "INV-2024-001" as string | null,
                                  date: "2024-01-15" as string | null,
                                  description: "Monthly Development Services - January",
                                  amount: 12500,
                                  created: "2024-01-10" as string | null,
                                  due: "2024-02-15" as string | null,
                                  status: "Paid" as const,
                                },
                                {
                                  id: 2,
                                  invoiceNumber: "INV-2024-002" as string | null,
                                  date: "2024-02-15" as string | null,
                                  description: "Monthly Development Services - February",
                                  amount: 15000,
                                  created: "2024-02-10" as string | null,
                                  due: "2024-03-15" as string | null,
                                  status: "Paid" as const,
                                },
                                {
                                  id: 3,
                                  invoiceNumber: "INV-2024-003" as string | null,
                                  date: "2024-03-15" as string | null,
                                  description: "Monthly Development Services - March",
                                  amount: 13750,
                                  created: "2024-03-10" as string | null,
                                  due: "2024-04-15" as string | null,
                                  status: "Overdue" as const,
                                },
                                {
                                  id: 4,
                                  invoiceNumber: "INV-2024-004" as string | null,
                                  date: "2024-04-15" as string | null,
                                  description: "Monthly Development Services - April",
                                  amount: 14200,
                                  created: "2024-04-10" as string | null,
                                  due: "2024-05-15" as string | null,
                                  status: "Sent" as const,
                                },
                                {
                                  id: 5,
                                  invoiceNumber: "DRAFT-005" as string | null,
                                  date: null as string | null,
                                  description: "Monthly Development Services - May",
                                  amount: 8500,
                                  created: "2024-05-01" as string | null,
                                  due: null as string | null,
                                  status: "Draft" as const,
                                },
                                {
                                  id: 6,
                                  invoiceNumber: null as string | null,
                                  date: null as string | null,
                                  description: "Additional Features Development",
                                  amount: 5200,
                                  created: null as string | null,
                                  due: null as string | null,
                                  status: "Draft" as const,
                                },
                              ];

                              // Apply filters and search
                              const filteredInvoices = allInvoices.filter((invoice) => {
                                // Status filter
                                const matchesStatus = invoiceStatusFilter === "all" || invoice.status === invoiceStatusFilter;
                                
                                // Search filter (searches invoice number, description)
                                const searchLower = invoiceSearchQuery.toLowerCase();
                                const matchesSearch = 
                                  invoiceSearchQuery === "" ||
                                  (invoice.invoiceNumber?.toLowerCase().includes(searchLower)) ||
                                  invoice.description.toLowerCase().includes(searchLower);
                                
                                return matchesStatus && matchesSearch;
                              });

                              const invoices = filteredInvoices;

                              const getStatusBadge = (status: string) => {
                                switch (status) {
                                  case "Paid":
                                    return <Badge variant="success">Paid</Badge>;
                                  case "Sent":
                                    return <Badge variant="default">Sent</Badge>;
                                  case "Overdue":
                                    return <Badge variant="destructive">Overdue</Badge>;
                                  case "Draft":
                                    return <Badge variant="secondary">Draft</Badge>;
                                  default:
                                    return <Badge variant="outline">{status}</Badge>;
                                }
                              };

                              // Calculate totals
                              const totalInvoiced = invoices.reduce((sum, inv) => sum + inv.amount, 0);
                              const outstanding = invoices
                                .filter((inv) => ["Sent", "Overdue"].includes(inv.status))
                                .reduce((sum, inv) => sum + inv.amount, 0);
                              const paid = invoices
                                .filter((inv) => inv.status === "Paid")
                                .reduce((sum, inv) => sum + inv.amount, 0);

                              // Build TanStack ColumnDef array from visibleInvoiceColumns
                              const invoiceColumnDefs: ColumnDef<typeof invoices[number], unknown>[] = [
                                ...visibleInvoiceColumns.map((column) => ({
                                  id: column.id,
                                  header: column.name,
                                  accessorFn: (row: typeof invoices[number]) => {
                                    switch (column.id) {
                                      case 'invoice-number': return row.invoiceNumber || `#${row.id}`;
                                      case 'date': return row.date;
                                      case 'description': return row.description;
                                      case 'amount': return row.amount;
                                      case 'days-overdue': {
                                        if (row.status !== 'Overdue' || !row.due) return -1;
                                        return Math.floor((Date.now() - new Date(row.due).getTime()) / 86400000);
                                      }
                                      case 'status': return row.status;
                                      default: return null;
                                    }
                                  },
                                  cell: ({ row }: { row: { original: typeof invoices[number] } }) => {
                                    const invoice = row.original;
                                    switch (column.id) {
                                      case 'invoice-number':
                                        return <span className="font-medium">{invoice.invoiceNumber || `#${invoice.id}`}</span>;
                                      case 'date':
                                        return invoice.date
                                          ? new Date(invoice.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                                          : "-";
                                      case 'description':
                                        return invoice.description;
                                      case 'amount':
                                        return (
                                          <span className="font-medium">
                                            <SensitiveData>{formatCurrency(invoice.amount)}</SensitiveData>
                                          </span>
                                        );
                                      case 'days-overdue': {
                                        if (invoice.status !== 'Overdue' || !invoice.due) return <span className="text-muted-foreground">—</span>;
                                        const days = Math.floor((Date.now() - new Date(invoice.due).getTime()) / 86400000);
                                        const bucket = days > 90 ? '90+ days' : days > 60 ? '61–90 days' : days > 30 ? '31–60 days' : '1–30 days';
                                        const colour = days > 60 ? 'text-red-600' : days > 30 ? 'text-orange-500' : 'text-amber-500';
                                        return <span className={`font-medium ${colour}`} title={`${days} days overdue`}>{bucket}</span>;
                                      }
                                      case 'status':
                                        return getStatusBadge(invoice.status);
                                      default:
                                        return null;
                                    }
                                  },
                                  ...(column.align === 'right' ? { meta: { align: 'right' } } : {}),
                                })),
                                {
                                  id: 'actions',
                                  enableSorting: false,
                                  header: () => <span className="flex justify-end">Actions</span>,
                                  cell: ({ row }: { row: { original: typeof invoices[number] } }) => {
                                    const invoice = row.original;
                                    return (
                                      <div className="flex items-center justify-end gap-2">
                                        {invoice.status === "Draft" ? (
                                          <>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => {
                                                setSelectedInvoiceId(invoice.id);
                                                setInvoiceDetailsSheetOpen(true);
                                              }}
                                            >
                                              <FileText className="h-4 w-4 mr-1" />
                                              View
                                            </Button>
                                            <AlertDialog>
                                              <AlertDialogTrigger asChild>
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  disabled={billingActionLoading}
                                                >
                                                  <Trash2 className="h-4 w-4 mr-1" />
                                                  Delete
                                                </Button>
                                              </AlertDialogTrigger>
                                              <AlertDialogContent>
                                                <AlertDialogHeader>
                                                  <AlertDialogTitle>Delete Draft Invoice</AlertDialogTitle>
                                                  <AlertDialogDescription>
                                                    This will delete the draft invoice and return all associated WIP items back to Unbilled Work. The line items on this draft will be removed.
                                                  </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                  <AlertDialogAction onClick={() => handleDeleteDraft(invoice.id)}>
                                                    Delete &amp; Return to WIP
                                                  </AlertDialogAction>
                                                </AlertDialogFooter>
                                              </AlertDialogContent>
                                            </AlertDialog>
                                          </>
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                              setSelectedInvoiceId(invoice.id);
                                              setInvoiceDetailsSheetOpen(true);
                                            }}
                                          >
                                            <FileText className="h-4 w-4 mr-1" />
                                            View
                                          </Button>
                                        )}
                                      </div>
                                    );
                                  },
                                },
                              ];

                              // Build column visibility state
                              const invoiceColumnVisibility = columnDefinitionsToVisibility(invoiceColumns);

                              // Apply multi-column sorting
                              const sortedInvoices = [...filteredInvoices].sort((a, b) => {
                                for (const sort of invoiceColumnSorts) {
                                  let aVal: string | number | null = null;
                                  let bVal: string | number | null = null;
                                  switch (sort.columnId) {
                                    case 'invoice-number': aVal = a.invoiceNumber || ''; bVal = b.invoiceNumber || ''; break;
                                    case 'date': aVal = a.date || ''; bVal = b.date || ''; break;
                                    case 'description': aVal = a.description; bVal = b.description; break;
                                    case 'amount': aVal = a.amount; bVal = b.amount; break;
                                    case 'days-overdue': {
                                      aVal = (a.status === 'Overdue' && a.due) ? Math.floor((Date.now() - new Date(a.due).getTime()) / 86400000) : -1;
                                      bVal = (b.status === 'Overdue' && b.due) ? Math.floor((Date.now() - new Date(b.due).getTime()) / 86400000) : -1;
                                      break;
                                    }
                                    case 'status': aVal = a.status; bVal = b.status; break;
                                  }
                                  if (aVal === null || bVal === null) continue;
                                  const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
                                  if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
                                }
                                return 0;
                              });

                              // Paginate
                              const invoicePagination = calculatePagination(sortedInvoices.length, invoiceCurrentPage, invoiceItemsPerPage);
                              const paginatedInvoices = sortedInvoices.slice(invoicePagination.startIndex, invoicePagination.endIndex);

                              return (
                                <>
                                  <DataTableEnhanced<typeof sortedInvoices[number], unknown>
                                    columns={invoiceColumnDefs}
                                    data={paginatedInvoices}
                                    columnVisibility={invoiceColumnVisibility}
                                    columnSorts={invoiceColumnSorts}
                                    onColumnSortChange={(newSorts) => {
                                      setInvoiceColumnSorts(newSorts);
                                      setInvoiceCurrentPage(1);
                                    }}
                                    getRowId={(row) => String(row.id)}
                                    emptyMessage={
                                      <div className="space-y-2">
                                        <Receipt className="h-12 w-12 mx-auto opacity-50" />
                                        <p className="text-lg font-medium">No invoice data available</p>
                                        <p className="text-sm">Invoice history will appear here once integrated with your billing system</p>
                                      </div>
                                    }
                                    paginationContent={
                                      sortedInvoices.length > 0 ? (
                                        <PaginationControls
                                          pagination={invoicePagination}
                                          onPageChange={(page) => setInvoiceCurrentPage(page)}
                                          onPageSizeChange={(size) => { setInvoiceItemsPerPage(size); setInvoiceCurrentPage(1); }}
                                          showPageSizeSelector={true}
                                          className="pt-4 border-t"
                                        />
                                      ) : null
                                    }
                                  />

                                  {/* Footer with totals */}
                                  {sortedInvoices.length > 0 && (
                                    <div className="mt-6 pt-4 border-t">
                                      <div className="grid gap-4 md:grid-cols-3">
                                        <div className="space-y-1">
                                          <div className="text-sm text-muted-foreground">Total Invoiced</div>
                                          <div className="text-xl font-bold">
                                            <SensitiveData>{formatCurrency(totalInvoiced)}</SensitiveData>
                                          </div>
                                        </div>
                                        <div className="space-y-1">
                                          <div className="text-sm text-muted-foreground">Outstanding</div>
                                          <div className="text-xl font-bold text-warning-600">
                                            <SensitiveData>{formatCurrency(outstanding)}</SensitiveData>
                                          </div>
                                        </div>
                                        <div className="space-y-1">
                                          <div className="text-sm text-muted-foreground">Paid</div>
                                          <div className="text-xl font-bold text-success-600">
                                            <SensitiveData>{formatCurrency(paid)}</SensitiveData>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </CardContent>
                        </Card>

                        {/* Payment Tracking */}
                        <Card>
                          <CardHeader>
                            <CardTitle>Payment Tracking</CardTitle>
                            <CardDescription>Outstanding and received payments</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-2 p-4 border rounded-lg">
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Clock className="h-4 w-4" />
                                    Outstanding
                                  </div>
                                  <div className="text-2xl font-bold">
                                    <SensitiveData>{formatCurrency(0)}</SensitiveData>
                                  </div>
                                  <div className="text-xs text-muted-foreground">0 invoices pending payment</div>
                                </div>

                                <div className="space-y-2 p-4 border rounded-lg">
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <CheckCircle className="h-4 w-4" />
                                    Received
                                  </div>
                                  <div className="text-2xl font-bold text-success-600">
                                    <SensitiveData>{formatCurrency(project.actualRevenue)}</SensitiveData>
                                  </div>
                                  <div className="text-xs text-muted-foreground">All invoices paid</div>
                                </div>
                              </div>

                              <Separator />

                              <div className="text-center py-8 text-muted-foreground">
                                <p className="text-sm">Payment tracking details will appear here</p>
                                <p className="text-xs mt-2">Connect your accounting system to track payment status</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </TabsContent>

                      {/* Unbilled Work Tab Content */}
                      <TabsContent value="unbilled" className="space-y-4 mt-4">
                        {/* WIP Summary - One Line (uses server-side summary for accurate totals across all pages) */}
                        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                          <div className="flex items-center gap-8 flex-1">
                            <div>
                              <span className="text-sm text-neutral-600">Time WIP:</span>
                              <span className="ml-2 text-base font-semibold text-neutral-900">
                                <SensitiveData>{formatCurrency(wipSummary.timeNetAmount)}</SensitiveData>
                              </span>
                              <span className="ml-1 text-xs text-neutral-500">({wipSummary.timeHours.toFixed(1)} hrs)</span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Expense WIP:</span>
                              <span className="ml-2 text-base font-semibold text-neutral-900">
                                <SensitiveData>{formatCurrency(wipSummary.expenseNetAmount)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Total WIP:</span>
                              <span className="ml-2 text-base font-semibold text-success-600">
                                <SensitiveData>{formatCurrency(wipSummary.totalNetAmount)}</SensitiveData>
                              </span>
                            </div>
                            <div>
                              <span className="text-sm text-neutral-600">Status:</span>
                              <span className="ml-2">
                                {billingLoading ? (
                                  <Badge variant="outline">Loading...</Badge>
                                ) : wipSummary.totalNetAmount > 0 ? (
                                  <Badge variant="success">Ready to Bill</Badge>
                                ) : (
                                  <Badge variant="secondary">No Items</Badge>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* WIP Items Table */}
                        <Card>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle>Unbilled Work Items</CardTitle>
                                <CardDescription>Select items to include in the next invoice</CardDescription>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setWipColumnConfigOpen(true)}
                                >
                                  <Settings2 className="h-4 w-4 mr-2" />
                                  Configure
                                </Button>
                                {dataMode === "live" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-2"
                                    disabled={selectedWipItems.length === 0 || billingActionLoading}
                                    onClick={() => setCreateDraftDialogOpen(true)}
                                  >
                                    <FileText className="h-4 w-4" />
                                    Create Draft
                                  </Button>
                                )}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span tabIndex={!client ? 0 : undefined}>
                                      <Button 
                                        className="gap-2" 
                                        disabled={!client || selectedWipItems.length === 0 || billingActionLoading}
                                        onClick={() => setInvoiceWizardOpen(true)}
                                        style={!client ? { pointerEvents: "none" } : undefined}
                                      >
                                        <FileText className="h-4 w-4" />
                                        Generate Invoice
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  {!client && (
                                    <TooltipContent>
                                      <p>Project has no Client set, Please set Client before you can Generate Invoice</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {/* Filters */}
                            <div className="flex flex-wrap gap-4 mb-4">
                              <div className="flex-1 min-w-[200px]">
                                <Input
                                  placeholder="Search by description..."
                                  className="w-full"
                                  value={wipSearchQuery}
                                  onChange={(e) => setWipSearchQuery(e.target.value)}
                                />
                              </div>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className="w-[150px] justify-start text-left font-normal">
                                    <Calendar className="mr-2 h-4 w-4" />
                                    {wipDateFrom ? wipDateFrom.toLocaleDateString() : "From Date"}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <CalendarComponent
                                    mode="single"
                                    selected={wipDateFrom}
                                    onSelect={setWipDateFrom}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className="w-[150px] justify-start text-left font-normal">
                                    <Calendar className="mr-2 h-4 w-4" />
                                    {wipDateTo ? wipDateTo.toLocaleDateString() : "To Date"}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <CalendarComponent
                                    mode="single"
                                    selected={wipDateTo}
                                    onSelect={setWipDateTo}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                              {(wipDateFrom || wipDateTo) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setWipDateFrom(undefined);
                                    setWipDateTo(undefined);
                                  }}
                                >
                                  Clear Dates
                                </Button>
                              )}
                              <Select value={wipTypeFilter} onValueChange={setWipTypeFilter}>
                                <SelectTrigger className="w-[150px]">
                                  <SelectValue placeholder="Type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all-types">All Types</SelectItem>
                                  <SelectItem value="time">Time</SelectItem>
                                  <SelectItem value="expense">Expense</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select value={wipResourceFilter} onValueChange={setWipResourceFilter}>
                                <SelectTrigger className="w-[180px]">
                                  <SelectValue placeholder="Resource" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all-resources">All Resources</SelectItem>
                                  {Array.from(new Set(wipItems.map((item) => item.resource)))
                                    .sort()
                                    .map((resource) => (
                                      <SelectItem key={resource} value={resource.toLowerCase()}>
                                        {resource}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <Select value={wipGroupBy} onValueChange={setWipGroupBy}>
                                <SelectTrigger className="w-[150px]">
                                  <SelectValue placeholder="Group By" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  <SelectItem value="resource">Resource</SelectItem>
                                  <SelectItem value="task">Task</SelectItem>
                                  <SelectItem value="date">Date</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* WIP Table — DataTableEnhanced */}
                            {(() => {
                              type WipRow = typeof filteredAndSortedWipItems[number];

                              const wipColumnDefs: ColumnDef<WipRow, unknown>[] = [
                                // Checkbox select column
                                {
                                  id: 'select',
                                  enableSorting: false,
                                  header: () => (
                                    <input
                                      type="checkbox"
                                      className="rounded"
                                      checked={
                                        filteredAndSortedWipItems.length > 0 &&
                                        selectedWipItems.length === filteredAndSortedWipItems.length
                                      }
                                      onChange={handleSelectAllWipItems}
                                    />
                                  ),
                                  cell: ({ row }: { row: { original: WipRow } }) => (
                                    <input
                                      type="checkbox"
                                      className="rounded"
                                      checked={selectedWipItems.includes(row.original.id)}
                                      onChange={() => handleSelectWipItem(row.original.id)}
                                    />
                                  ),
                                },
                                // Data columns from visible WIP column preferences
                                ...visibleWipColumns.map((column) => ({
                                  id: column.id,
                                  header: column.name,
                                  accessorFn: (row: WipRow) => {
                                    switch (column.id) {
                                      case 'date': return row.date;
                                      case 'resource': return row.resource;
                                      case 'task': return row.task;
                                      case 'description': return row.description;
                                      case 'type': return row.type;
                                      case 'quantity': return row.quantity;
                                      case 'rate': return row.rate;
                                      case 'amount': return row.amount;
                                      case 'costCentre': return row.costCentre;
                                      default: return null;
                                    }
                                  },
                                  cell: ({ row }: { row: { original: WipRow } }) => {
                                    const item = row.original;
                                    const align = column.align === 'right' ? 'text-right' : '';
                                    switch (column.id) {
                                      case 'date':
                                        return <span>{item.date}</span>;
                                      case 'resource':
                                        return <span>{item.resource}</span>;
                                      case 'task':
                                        return <span>{item.task}</span>;
                                      case 'description':
                                        return <span>{item.description}</span>;
                                      case 'type':
                                        return (
                                          <Badge variant={item.type === 'Time' ? 'outline' : 'secondary'}>
                                            {item.type}
                                          </Badge>
                                        );
                                      case 'quantity':
                                        return <span className={align}>{item.quantity.toFixed(2)}</span>;
                                      case 'rate':
                                        return (
                                          <span className={align}>
                                            <SensitiveData>{formatCurrency(item.rate)}</SensitiveData>
                                          </span>
                                        );
                                      case 'amount':
                                        return (
                                          <span className={align}>
                                            <SensitiveData>{formatCurrency(item.amount)}</SensitiveData>
                                          </span>
                                        );
                                      case 'costCentre':
                                        return <span>{item.costCentre}</span>;
                                      default:
                                        return <span>-</span>;
                                    }
                                  },
                                  ...(column.align === 'right' ? { meta: { align: 'right' } } : {}),
                                })),
                              ];

                              const wipColumnVisibility = columnDefinitionsToVisibility(wipColumns);

                              // Decide which rows to render: grouped rows or paginated flat rows
                              const wipPagination = calculatePagination(wipTotalItems, wipCurrentPage, wipItemsPerPage);

                              // For grouped view build a flat list with group-header pseudo-rows inserted
                              const tableData: (WipRow & { _isGroupHeader?: boolean; _groupKey?: string; _groupItems?: WipRow[] })[] =
                                wipGroupBy !== 'none' && groupedWipItems
                                  ? Object.entries(groupedWipItems).flatMap(([groupKey, groupItems]) => [
                                      // Pseudo-row for the group header (rendered via custom row)
                                      { id: `__group__${groupKey}`, _isGroupHeader: true, _groupKey: groupKey, _groupItems: groupItems } as any,
                                      ...groupItems,
                                    ])
                                  : paginatedWipItems;

                              return (
                                <DataTableEnhanced<WipRow, unknown>
                                  columns={wipColumnDefs}
                                  data={tableData}
                                  columnVisibility={{ ...wipColumnVisibility, select: true }}
                                  columnSorts={wipColumnSorts}
                                  onColumnSortChange={(newSorts) => {
                                    setWipColumnSorts(newSorts);
                                    setWipCurrentPage(1);
                                  }}
                                  getRowId={(row) => String(row.id)}
                                  customRowRenderer={(row) => {
                                    const r = row.original as any;
                                    if (!r._isGroupHeader) return null; // use default rendering

                                    const groupItems: WipRow[] = r._groupItems ?? [];
                                    const groupTotal = groupItems.reduce((sum, item) => sum + item.amount, 0);
                                    const groupHours = groupItems
                                      .filter((item) => item.type === 'Time')
                                      .reduce((sum, item) => sum + item.quantity, 0);
                                    const colCount = visibleWipColumns.length + 1; // +1 for checkbox col

                                    return (
                                      <tr key={`group-${r._groupKey}`} className="bg-muted/30 border-b">
                                        <td
                                          colSpan={visibleWipColumns.findIndex((c) => c.id === 'quantity') + 2}
                                          className="px-4 py-2 font-semibold text-sm"
                                        >
                                          {r._groupKey} ({groupItems.length} items)
                                        </td>
                                        {visibleWipColumns.find((c) => c.id === 'quantity') && (
                                          <td className="px-4 py-2 text-right font-semibold text-sm">
                                            {groupHours > 0 ? `${groupHours.toFixed(1)} hrs` : '-'}
                                          </td>
                                        )}
                                        {visibleWipColumns.find((c) => c.id === 'rate') && (
                                          <td className="px-4 py-2" />
                                        )}
                                        {visibleWipColumns.find((c) => c.id === 'amount') && (
                                          <td className="px-4 py-2 text-right font-semibold text-sm">
                                            <SensitiveData>{formatCurrency(groupTotal)}</SensitiveData>
                                          </td>
                                        )}
                                        {visibleWipColumns.find((c) => c.id === 'costCentre') && (
                                          <td className="px-4 py-2" />
                                        )}
                                        {/* fill remaining cols to colCount */}
                                        {Array.from({ length: Math.max(0, colCount - visibleWipColumns.length - 1) }).map((_, i) => (
                                          <td key={i} />
                                        ))}
                                      </tr>
                                    );
                                  }}
                                  emptyMessage={
                                    <div className="space-y-2">
                                      <FileText className="h-12 w-12 mx-auto opacity-50" />
                                      <p className="text-lg font-medium">No unbilled work items</p>
                                      <p className="text-sm">Time and expense entries will appear here once recorded</p>
                                    </div>
                                  }
                                  footerContent={
                                    filteredAndSortedWipItems.length > 0 ? (
                                      <tr className="bg-muted/50 font-semibold border-t-2">
                                        <td
                                          colSpan={visibleWipColumns.findIndex((c) => c.id === 'quantity') + 2}
                                          className="px-4 py-2 text-right text-sm"
                                        >
                                          Selected Total ({selectedWipTotals.count} items):
                                        </td>
                                        {visibleWipColumns.find((c) => c.id === 'quantity') && (
                                          <td className="px-4 py-2 text-right text-sm">
                                            {selectedWipTotals.hours > 0 ? `${selectedWipTotals.hours.toFixed(1)} hrs` : '-'}
                                          </td>
                                        )}
                                        {visibleWipColumns.find((c) => c.id === 'rate') && <td className="px-4 py-2" />}
                                        {visibleWipColumns.find((c) => c.id === 'amount') && (
                                          <td className="px-4 py-2 text-right text-sm">
                                            <SensitiveData>{formatCurrency(selectedWipTotals.amount)}</SensitiveData>
                                          </td>
                                        )}
                                        {visibleWipColumns.find((c) => c.id === 'costCentre') && <td className="px-4 py-2" />}
                                      </tr>
                                    ) : null
                                  }
                                  paginationContent={
                                    wipGroupBy === 'none' && wipTotalItems > 0 ? (
                                      <PaginationControls
                                        pagination={wipPagination}
                                        onPageChange={(page) => handleWipPageChange(page)}
                                        onPageSizeChange={(size) => { setWipItemsPerPage(size); setWipCurrentPage(1); }}
                                        showPageSizeSelector={true}
                                        className="pt-4 border-t"
                                      />
                                    ) : null
                                  }
                                />
                              );
                            })()}
                          </CardContent>
                        </Card>
                      </TabsContent>
                    </Tabs>
                  </div>
                )}
              </TabsContent>

              {/* T&M Sales Budget Tab */}
              {isBudgetingEnabled && budgetPermissions.settings.permissions.salesBudgetEnabled && (
                <TabsContent value="sales-budget" className="space-y-4">
                  {budgetLoading ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading budget data...</p>
                      </div>
                    </div>
                  ) : budgetError ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center space-y-4">
                        <div className="text-destructive mb-2">Error loading budget data</div>
                        <p className="text-sm text-muted-foreground">{budgetError}</p>
                      </div>
                    </div>
                  ) : (
                    <SalesBudgetView
                      projectId={numericProjectId}
                      data={budgetData?.salesBudget}
                      costBudget={budgetData?.costBudget}
                      projectType={project.type}
                      billingTriggerType={project.billingTriggerType}
                      completionPercent={project.type === 'fixed-price' ? effectiveCompletionPercent : undefined}
                      completionPercentMode={project.type === 'fixed-price' ? (project.completionPercentMode ?? 'based-on-time') : undefined}
                      completionPercentOverriddenBy={project.type === 'fixed-price' ? project.completionPercentOverriddenBy : undefined}
                      invoicedToDate={project.type === 'fixed-price' ? fixedFeeInvoicedToDate : undefined}
                      milestoneInvoices={project.type === 'fixed-price' ? fixedFeeInvoices : undefined}
                      onCreateBudget={() => setSalesBudgetEditModalOpen(true)}
                      onEdit={() => {
                        // Use page view for T&M projects, modal for Fixed Price
                        if (project.type === "time-and-materials") {
                          setEditSalesBudgetPageOpen(true);
                        } else {
                          setSalesBudgetEditModalOpen(true);
                        }
                      }}
                      onExport={() => {
                        setActiveBudgetType("sales");
                        setBudgetExportModalOpen(true);
                      }}
                      onHistory={() => {
                        setActiveBudgetType("sales");
                        setBudgetHistoryModalOpen(true);
                      }}
                    />
                  )}
                </TabsContent>
              )}

              {/* Cost Budget Tab */}
              {isBudgetingEnabled && budgetPermissions.settings.permissions.costBudgetEnabled && (
                <TabsContent value="cost-budget" className="space-y-4">
                  {budgetLoading ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading budget data...</p>
                      </div>
                    </div>
                  ) : budgetError ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center space-y-4">
                        <div className="text-destructive mb-2">Error loading budget data</div>
                        <p className="text-sm text-muted-foreground">{budgetError}</p>
                      </div>
                    </div>
                  ) : budgetData ? (
                    <>
                      <CostBudgetView
                        projectId={numericProjectId}
                        resourceBudget={budgetData.resourceBudget}
                        data={budgetData.costBudget}
                        onEdit={() => setEditCostBudgetPageOpen(true)}
                        onExport={() => {
                          setActiveBudgetType("cost");
                          setBudgetExportModalOpen(true);
                        }}
                        onHistory={() => {
                          setActiveBudgetType("cost");
                          setBudgetHistoryModalOpen(true);
                        }}
                      />

                      {budgetData.alerts && budgetData.alerts.length > 0 && (
                        <BudgetAlertsSection alerts={budgetData.alerts} />
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center min-h-[500px]">
                      <Card className="w-full max-w-md border-dashed">
                        <CardContent className="flex flex-col items-center justify-center text-center p-12">
                          <div className="rounded-full bg-muted p-4 mb-4">
                            <Receipt className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <h3 className="text-lg font-semibold mb-2">No Cost Budget</h3>
                          <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                            Track expenses, commitments, and cost categories by creating a cost budget for this project.
                          </p>
                          <Button onClick={() => setEditCostBudgetPageOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Cost Budget
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* Resource Budget Tab */}
              {isBudgetingEnabled && isResourceBudgetEnabled && (
                <TabsContent value="resource-budget" className="space-y-4">
                  {budgetLoading ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading budget data...</p>
                      </div>
                    </div>
                  ) : budgetError ? (
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center space-y-4">
                        <div className="text-destructive mb-2">Error loading budget data</div>
                        <p className="text-sm text-muted-foreground">{budgetError}</p>
                      </div>
                    </div>
                  ) : budgetData && enrichedBudgetData?.resourceBudget ? (
                    <>
                      <ResourceBudgetWrapper
                        projectId={numericProjectId}
                        data={enrichedBudgetData.resourceBudget}
                        onEdit={() => setEditResourceBudgetPageOpen(true)}
                        onExport={() => {
                          setActiveBudgetType("resource");
                          setBudgetExportModalOpen(true);
                        }}
                        onHistory={() => {
                          setActiveBudgetType("resource");
                          setBudgetHistoryModalOpen(true);
                        }}
                        availablePeople={employees}
                        existingAllocations={resourceAllocations}
                        projectStartDate={project?.startDate}
                        projectEndDate={project?.endDate}
                        onAssign={async (role, selections) => {
                          const dataMode = getDataMode();
                          const totalAssignedHours = selections.reduce((sum, s) => sum + s.allocatedHours, 0);

                          if (dataMode === "live") {
                            try {
                              const now = new Date();
                              const startDate = project?.startDate || now.toISOString().split("T")[0];
                              const endDate =
                                project?.endDate ||
                                new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

                              for (const selection of selections) {
                                const allocationData = {
                                  projectId: numericProjectId,
                                  employeeId: selection.personId,
                                  role: role,
                                  allocatedHours: selection.allocatedHours,
                                  startDate: startDate,
                                  endDate: endDate,
                                  status: "confirmed" as const,
                                };
                                await createResourceAllocation(allocationData);
                              }

                              let resourceBudgetId = enrichedBudgetData?.resourceBudget?.id;
                              if (!resourceBudgetId) {
                                console.warn(
                                  "[onAssign/ResourceBudget] resourceBudgetId not found in local state, fetching from API...",
                                );
                                resourceBudgetId =
                                  (await fetchResourceBudgetIdForProject(numericProjectId)) ?? undefined;
                              }
                              if (resourceBudgetId) {
                                const matchedRole = enrichedBudgetData?.resourceBudget?.byRole?.find(
                                  (r) => r.role === role,
                                );
                                for (const selection of selections) {
                                  const employee = employees.find((e) => e.id === selection.personId);
                                  try {
                                    await createPersonBudget({
                                      resourceBudgetId,
                                      personId: selection.personId,
                                      personName: employee?.name || selection.personName,
                                      roleId: matchedRole?.roleId,
                                      budgetHours: selection.allocatedHours,
                                      costRate: 0,
                                    });
                                  } catch (pbError) {
                                    console.warn("Failed to create PersonBudget record:", pbError);
                                  }
                                }
                              } else {
                                console.warn(
                                  "[onAssign/ResourceBudget] No resourceBudgetId available — skipping PersonBudget creation",
                                );
                              }
                            } catch (error) {
                              console.error("Failed to create resource allocations:", error);
                              toast.error("Failed to assign team members to project");
                              return;
                            }
                          }

                          if (enrichedBudgetData?.resourceBudget?.byRole) {
                            const updatedRoles = enrichedBudgetData.resourceBudget.byRole.map((r) => {
                              if (r.role === role) {
                                return { ...r, allocatedHours: r.allocatedHours + totalAssignedHours };
                              }
                              return r;
                            });

                            const newPersonBudgets = selections.map((selection) => {
                              const employee = employees.find((e) => e.id === selection.personId);
                              return {
                                id: -Date.now() - selection.personId,
                                personId: selection.personId,
                                personName: employee?.name || selection.personName,
                                role: role,
                                budgetHours: selection.allocatedHours,
                                actualHours: 0,
                                utilisation: 0,
                                costRate: 0,
                                budgetCost: 0,
                                actualCost: 0,
                              };
                            });

                            const existingByPerson = enrichedBudgetData.resourceBudget.byPerson || [];

                            setLocalBudgetData({
                              ...enrichedBudgetData,
                              resourceBudget: {
                                ...enrichedBudgetData.resourceBudget,
                                byRole: updatedRoles,
                                byPerson: [...existingByPerson, ...newPersonBudgets],
                              },
                            });
                          }

                          toast.success(
                            `Assigned ${selections.length} team member${selections.length !== 1 ? "s" : ""} to ${role}`,
                          );
                        }}
                        onRaiseRequest={(roleName) => {
                          setRaiseRequestPrefilledRole(roleName);
                          setRaiseRequestModalOpen(true);
                        }}
                        pendingRequestRoles={pendingRequestRoles}
                      />
                    </>
                  ) : (
                    <div className="flex items-center justify-center min-h-[500px]">
                      <Card className="w-full max-w-md border-dashed">
                        <CardContent className="flex flex-col items-center justify-center text-center p-12">
                          <div className="rounded-full bg-muted p-4 mb-4">
                            <Clock className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <h3 className="text-lg font-semibold mb-2">No Resource Budget</h3>
                          <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                            Plan team capacity and track hours by creating a resource budget for this project.
                          </p>
                          <Button onClick={() => setEditResourceBudgetPageOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Resource Budget
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* Cost Centres Tab */}
              <TabsContent value="cost-centres" className="space-y-4">
                {/* Header Section */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3 ml-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCostCentreColumnConfigOpen(true)}
                      className="gap-2"
                    >
                      <Settings2 className="h-4 w-4" />
                      Configure Columns
                    </Button>
                    {!isStructureView && (
                      <Button size="sm" onClick={() => setCreateCostCentrePanelOpen(true)} className="gap-2">
                        <Plus className="h-4 w-4" />
                        New
                      </Button>
                    )}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="relative px-3" aria-label="Recycle Bin">
                            <Trash2 className="h-4 w-4" />
                            <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 flex items-center justify-center text-[10px] font-medium bg-muted text-muted-foreground rounded-full border">
                              0
                            </span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Recycle Bin</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {isStructureView && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfigureStructureOpen(true)}
                        className="gap-2"
                      >
                        <Settings2 className="h-4 w-4" />
                        Configure Structure
                      </Button>
                    )}
                    <div className="flex items-center gap-2">
                      <label htmlFor="structure-view-toggle" className="text-sm font-medium cursor-pointer">
                        Structure View
                      </label>
                      <Switch
                        id="structure-view-toggle"
                        checked={isStructureView}
                        onCheckedChange={setIsStructureView}
                      />
                    </div>
                  </div>
                </div>

                {/* Content Display Area */}
                {isStructureView ? (
                  // Structure View (Tree/Hierarchy)
                  <CostCentresHierarchicalTable
                    data={hierarchicalCostCentres}
                    columns={costCentreColumns}
                    useCardWrapper={false}
                    onRowClick={(costCentre) => {
                      setSelectedCostCentre(costCentre);
                      setCostCentreDetailsPanelOpen(true);
                    }}
                  />
                ) : (
                  // List/Table View
                  <CostCentresTable
                    data={costCentres}
                    columns={costCentreColumns}
                    useCardWrapper={false}
                    onRowClick={(costCentre) => {
                      setSelectedCostCentre(costCentre);
                      setCostCentreDetailsPanelOpen(true);
                    }}
                  />
                )}
              </TabsContent>

              {/* Column Configurator Modal */}
              <CostCentresColumnConfigurator
                open={costCentreColumnConfigOpen}
                onOpenChange={setCostCentreColumnConfigOpen}
                columns={costCentreColumns}
                onSave={(newColumns) => {
                  setCostCentreColumns(newColumns);
                  // Persist to localStorage as fallback
                  localStorage.setItem('cost-centre-column-preferences', JSON.stringify(newColumns));
                  // Persist to API in live mode
                  const mode = getDataMode();
                  if (mode === 'live') {
                    saveColumnPreferences(newColumns as unknown as Parameters<typeof saveColumnPreferences>[0], 'cost-centre-column-preferences').catch((error) => {
                      console.error('Failed to save cost centre column preferences to API:', error);
                    });
                  }
                }}
              />

              {/* Create Cost Centre Panel */}
              <CreateCostCentrePanel
                open={createCostCentrePanelOpen}
                onOpenChange={setCreateCostCentrePanelOpen}
                existingCodes={[...costCentres.map((cc) => cc.code), ...hierarchicalCostCentres.map((cc) => cc.code)]}
                onSave={async (newCostCentre) => {
                  const mode = getDataMode();

                  if (mode === "live" && projectId) {
                    // Live mode - call the API to persist the cost centre
                    try {
                      const createdCostCentre = await createCostCentre({
                        projectId: numericProjectId,
                        code: newCostCentre.code,
                        name: newCostCentre.name,
                        type: newCostCentre.type,
                        status: newCostCentre.status,
                        billable: newCostCentre.billable,
                        owner: newCostCentre.owner,
                        description: newCostCentre.description,
                        costsBudget: newCostCentre.costsBudget,
                        revenueBudget: newCostCentre.revenueBudget,
                        hoursBudget: newCostCentre.hoursBudget,
                      });

                      // Use the ID from the API response
                      const costCentreWithId: CostCentre = {
                        ...newCostCentre,
                        id: createdCostCentre.id || -Date.now(),
                      };

                      // Add to both flat and hierarchical lists
                      setCostCentres((prev) => [...prev, costCentreWithId]);
                      setHierarchicalCostCentres((prev) => [...prev, { ...costCentreWithId, children: [] }]);

                      console.log("Created new cost centre via API:", costCentreWithId);
                    } catch (error) {
                      console.error("Failed to create cost centre:", error);
                      // Still add to local state for demo purposes, but log the error
                      const id = -Date.now();
                      const costCentreWithId: CostCentre = {
                        ...newCostCentre,
                        id,
                      };
                      setCostCentres((prev) => [...prev, costCentreWithId]);
                      setHierarchicalCostCentres((prev) => [...prev, { ...costCentreWithId, children: [] }]);
                    }
                  } else {
                    // Demo mode - just update local state
                    const id = -Date.now();
                    const costCentreWithId: CostCentre = {
                      ...newCostCentre,
                      id,
                    };

                    // Add to both flat and hierarchical lists
                    setCostCentres((prev) => [...prev, costCentreWithId]);
                    setHierarchicalCostCentres((prev) => [...prev, { ...costCentreWithId, children: [] }]);

                    console.log("Created new cost centre (demo mode):", costCentreWithId);
                  }
                }}
              />

              {/* Purchases Tab */}
              <TabsContent value="purchases" className="space-y-4">
                <PurchasesCommitmentsTab projectId={numericProjectId} projectCode={project?.code} employees={employees} />
              </TabsContent>

              {/* Team Tab */}
              <TabsContent value="team" className="space-y-4">
                {/* Resourcing warning: unbooked timesheeters — only shown when scheduler is connected */}
                {/* TODO: Replace mockUnbookedTimesheetersCount with live data from API */}
                {schedulerConnected && mockUnbookedTimesheetersCount > 0 && (
                  <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                    <div className="flex-1">
                      <span className="font-medium">
                        {mockUnbookedTimesheetersCount === 1
                          ? '1 person has'
                          : `${mockUnbookedTimesheetersCount} people have`}{' '}
                        logged time on this project without a scheduler booking.
                      </span>{' '}
                      This may indicate untracked resource demand.
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-amber-700 hover:text-amber-900 hover:bg-amber-100 h-auto p-0"
                      onClick={() => {
                        setTimeTabPrefilterNames(mockUnbookedTimesheetersNames);
                        setActiveTab('time');
                      }}
                    >
                      View in Time →
                    </Button>
                  </div>
                )}

                {/* Unfulfilled Roles Table — hidden when scheduler connected (Resource Budget tab handles requests) */}
                {enrichedBudgetData?.resourceBudget?.byRole && project && !schedulerConnected && (
                  <UnfulfilledRolesTable
                    roles={enrichedBudgetData.resourceBudget.byRole}
                    projectId={numericProjectId}
                    projectStartDate={project.startDate}
                    projectEndDate={project.endDate}
                    availablePeople={employees}
                    existingAllocations={resourceAllocations}
                    onAssign={async (role, selections) => {
                      // Handle assignment - update resource allocations and budget data
                      const dataMode = getDataMode();

                      // Calculate total hours being assigned
                      const totalAssignedHours = selections.reduce((sum, s) => sum + s.allocatedHours, 0);

                      // In live mode, create resource allocations via API
                      const createdAllocationIds: Map<number, string> = new Map();
                      if (dataMode === "live") {
                        try {
                          const now = new Date();
                          const startDate = project?.startDate || now.toISOString().split("T")[0];
                          const endDate =
                            project?.endDate ||
                            new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

                          // Create resource allocations for each selection
                          for (const selection of selections) {
                            const allocationData = {
                              projectId: numericProjectId,
                              employeeId: selection.personId,
                              role: role,
                              allocatedHours: selection.allocatedHours,
                              startDate: startDate,
                              endDate: endDate,
                              status: "confirmed" as const,
                            };

                            const createdAllocation = await createResourceAllocation(allocationData);
                            createdAllocationIds.set(selection.personId, createdAllocation.id);
                          }
                          // Also create PersonBudget records so Rate Card can show assigned people
                          let resourceBudgetId = enrichedBudgetData?.resourceBudget?.id;
                          if (!resourceBudgetId) {
                            console.warn(
                              "[onAssign/Team] resourceBudgetId not found in local state, fetching from API...",
                            );
                            resourceBudgetId = (await fetchResourceBudgetIdForProject(numericProjectId)) ?? undefined;
                          }
                          if (resourceBudgetId) {
                            const matchedRole = enrichedBudgetData?.resourceBudget?.byRole?.find(
                              (r) => r.role === role,
                            );
                            for (const selection of selections) {
                              const employee = employees.find((e) => e.id === selection.personId);
                              try {
                                await createPersonBudget({
                                  resourceBudgetId,
                                  personId: selection.personId,
                                  personName: employee?.name || selection.personName,
                                  roleId: matchedRole?.roleId,
                                  budgetHours: selection.allocatedHours,
                                  costRate: 0,
                                });
                              } catch (pbError) {
                                console.warn("Failed to create PersonBudget record:", pbError);
                              }
                            }
                          } else {
                            console.warn(
                              "[onAssign/Team] No resourceBudgetId available — skipping PersonBudget creation",
                            );
                          }
                        } catch (error) {
                          console.error("Failed to create resource allocations:", error);
                          toast.error("Failed to assign team members to project");
                          return;
                        }
                      }

                      // Update budget data to reflect the new assignments
                      if (enrichedBudgetData?.resourceBudget?.byRole) {
                        const updatedRoles = enrichedBudgetData.resourceBudget.byRole.map((r) => {
                          if (r.role === role) {
                            return {
                              ...r,
                              allocatedHours: r.allocatedHours + totalAssignedHours,
                            };
                          }
                          return r;
                        });

                        const newPersonBudgets = selections.map((selection) => {
                          const employee = employees.find((e) => e.id === selection.personId);
                          return {
                            id: -Date.now() - selection.personId,
                            personId: selection.personId,
                            personName: employee?.name || selection.personName,
                            role: role,
                            budgetHours: selection.allocatedHours,
                            actualHours: 0,
                            utilisation: 0,
                            costRate: 0,
                            budgetCost: 0,
                            actualCost: 0,
                          };
                        });

                        const existingByPerson = enrichedBudgetData.resourceBudget.byPerson || [];

                        setLocalBudgetData({
                          ...enrichedBudgetData,
                          resourceBudget: {
                            ...enrichedBudgetData.resourceBudget,
                            byRole: updatedRoles,
                            byPerson: [...existingByPerson, ...newPersonBudgets],
                          },
                        });
                      }

                      // Update team members - either add new or update existing
                      setTeamMembers((prev) => {
                        const updatedMembers = [...prev];
                        const newMembers: TeamMember[] = [];

                        selections.forEach((selection) => {
                          // Check if team member already exists (by employeeId)
                          const existingIndex = updatedMembers.findIndex((tm) => tm.employeeId === selection.personId);

                          const employee = employees.find((e) => e.id === selection.personId);
                          const now = new Date();
                          const startDate = project?.startDate || now.toISOString();
                          const endDate =
                            project?.endDate || new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();

                          if (existingIndex !== -1) {
                            // Team member already exists - update their allocated hours
                            const existing = updatedMembers[existingIndex];
                            const newAllocatedHours = existing.allocatedHours + selection.allocatedHours;
                            const remaining = newAllocatedHours - existing.actualHours;
                            const utilisationPercent = (existing.actualHours / newAllocatedHours) * 100;

                            // Determine updated status
                            let status: TeamMember["status"];
                            if (utilisationPercent > 100) {
                              status = "Over Allocated";
                            } else if (utilisationPercent > 85) {
                              status = "At Risk";
                            } else {
                              status = "On Track";
                            }

                            // Update existing team member
                            updatedMembers[existingIndex] = {
                              ...existing,
                              allocatedHours: newAllocatedHours,
                              remaining,
                              utilisationPercent,
                              status,
                            };
                          } else {
                            // Team member doesn't exist - create new entry
                            // In live mode, actual hours start at 0; in demo mode, generate mock data
                            const actualHours =
                              dataMode === "live"
                                ? 0
                                : Math.floor(selection.allocatedHours * (0.3 + Math.random() * 0.4));
                            const remaining = selection.allocatedHours - actualHours;
                            const utilisationPercent =
                              selection.allocatedHours > 0 ? (actualHours / selection.allocatedHours) * 100 : 0;

                            // Determine status
                            let status: TeamMember["status"];
                            if (utilisationPercent > 100) {
                              status = "Over Allocated";
                            } else if (utilisationPercent > 85) {
                              status = "At Risk";
                            } else {
                              status = "On Track";
                            }

                            // Generate avatar URL
                            const name = employee?.name || selection.personName;
                            const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=128`;

                            // Use the created allocation ID in live mode, or generate a temp ID in demo mode
                            const allocationId =
                              createdAllocationIds.get(selection.personId) ||
                              `tm-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

                            newMembers.push({
                              id: allocationId,
                              employeeId: selection.personId,
                              name: name,
                              email: employee?.email || "",
                              role: employee?.role || role, // Use employee's actual role, fallback to unfulfilled role
                              avatar: avatarUrl,
                              allocatedHours: selection.allocatedHours,
                              actualHours,
                              remaining,
                              startDate,
                              endDate,
                              status,
                              utilisationPercent,
                            });
                          }
                        });

                        // Return updated members plus any new members
                        return [...updatedMembers, ...newMembers];
                      });

                      toast.success(
                        `Assigned ${selections.length} team member${selections.length !== 1 ? "s" : ""} to ${role} role`,
                        {
                          description: selections.map((s) => `${s.personName}: ${s.allocatedHours}h`).join(", "),
                        },
                      );
                    }}
                  />
                )}

                {/* Team Management Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Team Management</CardTitle>
                    <CardDescription>Comprehensive team member tracking and allocation</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    {(() => {
                      const getAvatarColorClass = (name: string) => {
                        const colors = [
                          "bg-teal-100 text-teal-700",
                          "bg-success-100 text-success-700",
                          "bg-error-100 text-error-700",
                          "bg-warning-100 text-warning-700",
                          "bg-purple-100 text-purple-700",
                          "bg-info-100 text-info-700",
                          "bg-indigo-100 text-indigo-700",
                          "bg-cyan-100 text-cyan-700",
                        ];
                        const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
                        return colors[hash % colors.length];
                      };

                      const teamColDefs: ColumnDef<TeamMember, unknown>[] = [
                        {
                          id: 'avatar',
                          header: '',
                          enableSorting: false,
                          cell: ({ row }) => (
                            <Avatar className="h-8 w-8 rounded-md">
                              <AvatarFallback className={`text-xs rounded-md ${getAvatarColorClass(row.original.name)}`}>
                                {getInitials(row.original.name)}
                              </AvatarFallback>
                            </Avatar>
                          ),
                        },
                        {
                          id: 'name',
                          header: 'Name',
                          accessorKey: 'name',
                          cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
                        },
                        { id: 'role', header: 'Role', accessorKey: 'role' },
                        {
                          id: 'allocatedHours',
                          header: 'Allocated Hrs',
                          accessorKey: 'allocatedHours',
                          cell: ({ row }) => (
                            <span className="font-sans">
                              {row.original.allocatedHours === 0
                                ? <span className="text-muted-foreground italic">Not allocated</span>
                                : `${row.original.allocatedHours.toFixed(0)}h`}
                            </span>
                          ),
                        },
                        {
                          id: 'actualHours',
                          header: 'Actual Hrs',
                          accessorKey: 'actualHours',
                          cell: ({ row }) => <span className="font-sans">{row.original.actualHours.toFixed(0)}h</span>,
                        },
                        {
                          id: 'remaining',
                          header: 'Remaining',
                          accessorKey: 'remaining',
                          cell: ({ row }) => (
                            <span className={`font-sans ${row.original.remaining < 0 ? 'text-error-600' : ''}`}>
                              {row.original.remaining.toFixed(0)}h
                            </span>
                          ),
                        },
                        {
                          id: 'status',
                          header: 'Status',
                          accessorKey: 'status',
                          enableSorting: false,
                          cell: ({ row }) => (
                            <Badge variant={getStatusBadgeVariant(row.original.status, row.original.utilisationPercent)}>
                              {row.original.status}
                            </Badge>
                          ),
                        },
                        ...(schedulerConnected ? [{
                          id: 'bookingStatus',
                          header: 'Booking Status',
                          enableSorting: false,
                          cell: ({ row }: { row: { original: TeamMember } }) => {
                            const bookingStatus = mockTeamBookingStatuses.find(
                              (b) => b.personName === row.original.name,
                            )?.bookingStatus ?? 'NoBooking';
                            const pillStyle = bookingStatus === 'Confirmed'
                              ? 'bg-success-100 text-success-700 border border-success-200'
                              : bookingStatus === 'Planned'
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-amber-100 text-amber-700 border border-amber-200';
                            const label = bookingStatus === 'Confirmed' ? 'Confirmed'
                              : bookingStatus === 'Planned' ? 'Planned' : 'No Booking';
                            const pillIcon = bookingStatus === 'Confirmed'
                              ? <CheckCircle className="h-3 w-3 shrink-0" />
                              : bookingStatus === 'Planned'
                                ? <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500 inline-block" />
                                : <AlertTriangle className="h-3 w-3 shrink-0" />;
                            return (
                              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${pillStyle}`}>
                                {pillIcon}{label}
                              </span>
                            );
                          },
                        } as ColumnDef<TeamMember, unknown>] : []),
                        {
                          id: 'actions',
                          header: '',
                          enableSorting: false,
                          cell: ({ row }) => (
                            row.original.actualHours === 0 ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                      onClick={() => handleRemoveTeamMember(row.original)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Remove from project</p></TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : null
                          ),
                        },
                      ];

                      return (
                        <DataTableEnhanced
                          columns={teamColDefs}
                          data={paginatedTeamMembers}
                          columnVisibility={Object.fromEntries(teamColDefs.map(c => [c.id!, true]))}
                          columnSorts={teamColumnSorts}
                          onColumnSortChange={(s) => { setTeamColumnSorts(s); setTeamPage(1); }}
                          emptyMessage={<span className="text-muted-foreground">No team members found</span>}
                          paginationContent={
                            <PaginationControls
                              pagination={teamPagination}
                              onPageChange={setTeamPage}
                              onPageSizeChange={(s) => { setTeamPageSize(s); setTeamPage(1); }}
                            />
                          }
                        />
                      );
                    })()}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ------------------------------------------------------------------ */}
              {/* Resourcing Tab                                                      */}
              {/* Visible only when a scheduler is connected (feature flag gate)      */}
              {/* ------------------------------------------------------------------ */}
              {schedulerConnected && (
                <TabsContent value="resourcing" className="space-y-4">
                  {/* Loading state */}
                  {schedulingLoading && (
                    <Card>
                      <CardContent className="py-12">
                        <div className="flex flex-col items-center justify-center gap-3">
                          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">Loading scheduling data...</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Error state */}
                  {!schedulingLoading && schedulingConnectionStatus === 'error' && (
                    <Card className="border-amber-200 bg-amber-50">
                      <CardContent className="py-8">
                        <div className="flex flex-col items-center justify-center gap-3">
                          <AlertTriangle className="h-8 w-8 text-amber-500" />
                          <p className="text-sm font-medium text-amber-800">
                            Unable to connect to the resource scheduler.
                          </p>
                          <p className="text-xs text-amber-600">
                            Check your integration settings or try again later.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Re-trigger the scheduling data fetch
                              setSchedulingLoading(true);
                              setSchedulingError(null);
                              fetchSchedulingBookings(projectId).then((response) => {
                                setSchedulingBookings(response.bookings);
                                setSchedulingConnectionStatus(response.connectionStatus);
                                setSchedulingSchedulerName(response.schedulerName);
                              }).catch(() => {
                                setSchedulingConnectionStatus('error');
                                setSchedulingBookings([]);
                              }).finally(() => {
                                setSchedulingLoading(false);
                              });
                            }}
                          >
                            Retry
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Not configured state */}
                  {!schedulingLoading && schedulingConnectionStatus === 'not_configured' && (
                    <Card>
                      <CardContent className="py-12">
                        <div className="flex flex-col items-center justify-center gap-4">
                          <CalendarDays className="h-10 w-10 text-muted-foreground/50" />
                          <div className="text-center">
                            <p className="text-sm font-medium">No Resource Scheduler Connected</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Connect a resource scheduler in Settings &rarr; Integrations to see booking data for this project.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/settings/integrations')}
                          >
                            Go to Integrations
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Connected state — show real data */}
                  {!schedulingLoading && schedulingConnectionStatus === 'connected' && (() => {
                    const dataMode = getDataMode();
                    const isLive = dataMode === 'live';
                    // Phase 1: In live mode, roleCoverage, warnings, summary, and resourceRequests
                    // are not yet served by the API — show empty defaults so mock data doesn't leak.
                    // In demo/tour mode, keep using mock data for a complete UI experience.
                    const emptySchedulingSummary: SchedulingSummary = {
                      totalBudgetHours: 0, totalScheduledHours: 0, coveragePercent: 0,
                      totalBookings: schedulingBookings.length, confirmedBookings: 0, plannedBookings: 0,
                      unmetRoles: [], bookingsEndDate: '', projectEndDate: '',
                      healthStatus: 'FullyResourced', warnings: [],
                    };
                    return (
                    <ResourcingTabContent
                      bookings={schedulingBookings}
                      roleCoverage={isLive ? [] : mockRoleCoverage}
                      warnings={isLive ? [] : mockSchedulingWarnings}
                      summary={isLive ? emptySchedulingSummary : mockSchedulingSummary}
                      schedulerName={schedulingSchedulerName ?? mockSchedulerConnection.displayName}
                      resourceRequests={isLive ? liveResourceRequests : mockResourceRequests}
                      hasResourceBudget={Boolean(enrichedBudgetData?.resourceBudget)}
                      onNavigateToResourceBudget={() => setActiveTab('resource-budget')}
                      onRaiseRequest={(roleName) => {
                        setRaiseRequestPrefilledRole(roleName);
                        setRaiseRequestModalOpen(true);
                      }}
                    />
                    );
                  })()}
                </TabsContent>
              )}

              {/* Milestones Tab */}
              <TabsContent value="milestones" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Project Milestones</CardTitle>
                    <CardDescription>Key deliverables and payment milestones</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Milestone</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Completed</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Payment</TableHead>
                          <TableHead>Paid</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {milestones.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground">
                              No milestones defined
                            </TableCell>
                          </TableRow>
                        ) : (
                          milestones.map((milestone) => (
                            <TableRow key={milestone.id}>
                              <TableCell className="font-medium">{milestone.name}</TableCell>
                              <TableCell>{formatDate(milestone.dueDate)}</TableCell>
                              <TableCell>
                                {milestone.completedDate ? formatDate(milestone.completedDate) : "-"}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    milestone.status === "completed"
                                      ? "default"
                                      : milestone.status === "overdue"
                                        ? "destructive"
                                        : "outline"
                                  }
                                >
                                  {milestone.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                {milestone.paymentAmount ? (
                                  <SensitiveData>{formatCurrency(milestone.paymentAmount)}</SensitiveData>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell>
                                {milestone.paymentReceived ? (
                                  <CheckCircle className="h-4 w-4 text-success-500" />
                                ) : (
                                  <Clock className="h-4 w-4 text-muted-foreground" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Time Tab */}
              <TabsContent value="time" className="space-y-4">
                <TimeEntriesTab
                  projectId={numericProjectId}
                  projectCode={project?.code}
                  timeEntries={timeEntries}
                  employees={employees}
                  initialEmployeeNames={timeTabPrefilterNames}
                  initialBillableOnly={timeTabPrefilterBillable}
                />
              </TabsContent>

              {/* Expenses Tab */}
              <TabsContent value="expenses" className="space-y-4">
                <div className="flex items-center gap-6 mb-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="include-approved-expenses"
                      checked={includeApprovedExpenses}
                      onCheckedChange={(checked) => setIncludeApprovedExpenses(checked === true)}
                    />
                    <label
                      htmlFor="include-approved-expenses"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Approved Expenses
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="include-credit-card-expenses"
                      checked={includeCreditCardExpenses}
                      onCheckedChange={(checked) => setIncludeCreditCardExpenses(checked === true)}
                    />
                    <label
                      htmlFor="include-credit-card-expenses"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Credit Card Expenses
                    </label>
                  </div>
                  {expensesLoading && <span className="text-sm text-muted-foreground">Loading...</span>}
                </div>
                <ExpenseEntriesTab projectId={numericProjectId} expenses={expenses} employees={employees} />
              </TabsContent>

              {/* Change Requests Tab */}
              <TabsContent value="changes" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Change Requests</CardTitle>
                    <CardDescription>Scope changes and their impact on budget and schedule</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Requested By</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Budget Impact</TableHead>
                          <TableHead className="text-right">Schedule Impact</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {changeRequests.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground">
                              No change requests
                            </TableCell>
                          </TableRow>
                        ) : (
                          changeRequests.map((cr) => (
                            <TableRow key={cr.id}>
                              <TableCell className="font-medium">{cr.title}</TableCell>
                              <TableCell>{cr.requestedBy}</TableCell>
                              <TableCell>{formatDate(cr.requestedDate)}</TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    cr.status === "approved"
                                      ? "default"
                                      : cr.status === "rejected"
                                        ? "destructive"
                                        : "outline"
                                  }
                                >
                                  {cr.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <SensitiveData>{formatCurrency(cr.impactBudget)}</SensitiveData>
                              </TableCell>
                              <TableCell className="text-right">
                                {cr.impactSchedule > 0 ? `+${cr.impactSchedule}` : cr.impactSchedule} days
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Settings Tab - Removed, now accessible via Configure button in Billing tab */}

              {/* Audit Tab — Phase 17 */}
              <TabsContent value="audit" className="space-y-4">
                <ProjectAuditTab
                  projectId={numericProjectId || undefined}
                  projectCode={project?.code ?? String(project?.id ?? '')}
                  projectType={project?.type ?? ''}
                  billingTriggerType={project?.billingTriggerType}
                  completionAuditEntries={completionAuditEntries}
                  fixedFeeInvoices={project?.type === 'fixed-price' ? auditInvoices : []}
                  initialCategoryFilter={auditCategoryFilter as "all" | "completion" | "status" | "financial" | "billing" | "invoice" | "budget"}
                  extraEvents={clientAuditEvents}
                />
              </TabsContent>
            </ScrollableTabsContainer>
          </div>
        </Tabs>
      </TooltipProvider>

      {/* Configure Structure Modal */}
      <ConfigureStructureModal
        open={configureStructureOpen}
        onOpenChange={setConfigureStructureOpen}
        projectName={project?.name || ""}
        costCentres={hierarchicalCostCentres}
        onSave={async (updatedStructure) => {
          // TODO: Implement API call to save structure
          console.log("Saving structure:", updatedStructure);
          setHierarchicalCostCentres(updatedStructure);
          // Show success toast
        }}
      />

      {/* Cost Centre Details Panel */}
      <CostCentreDetailsPanel
        costCentre={selectedCostCentre}
        projectId={numericProjectId}
        open={costCentreDetailsPanelOpen}
        onOpenChange={setCostCentreDetailsPanelOpen}
        onEdit={(updatedCostCentre) => {
          // Update the cost centre in the state to refresh the table
          setCostCentres((prev) => prev.map((cc) => (cc.id === updatedCostCentre.id ? updatedCostCentre : cc)));
          setHierarchicalCostCentres((prev) =>
            prev.map((cc) => (cc.id === updatedCostCentre.id ? { ...cc, ...updatedCostCentre } : cc)),
          );
          // Also update the selected cost centre if it's the same one
          if (selectedCostCentre?.id === updatedCostCentre.id) {
            setSelectedCostCentre(updatedCostCentre);
          }
        }}
        onDelete={(costCentreId) => {
          // Delete the cost centre
          setCostCentres((prev) => prev.filter((cc) => cc.id !== costCentreId));
          setHierarchicalCostCentres((prev) => prev.filter((cc) => cc.id !== costCentreId));
          console.log("Deleted cost centre:", costCentreId);
          // In a real app, this would call an API to delete the cost centre
        }}
        onMarkInactive={(costCentreId) => {
          // Mark the cost centre as inactive
          console.log("Mark inactive cost centre:", costCentreId);
          // In a real app, this would call an API to update the cost centre status
        }}
        onExpandToFullView={() => {
          // Close the side drawer and open the full modal
          setCostCentreDetailsPanelOpen(false);
          setCostCentreFullModalOpen(true);
        }}
      />

      {/* Cost Centre Full Modal */}
      <CostCentreFullModal
        costCentre={selectedCostCentre}
        projectId={numericProjectId}
        allCostCentres={costCentres}
        open={costCentreFullModalOpen}
        onOpenChange={setCostCentreFullModalOpen}
        onCostCentreChange={(costCentre) => {
          setSelectedCostCentre(costCentre);
        }}
        onMinimize={() => {
          setCostCentreFullModalOpen(false);
          setCostCentreDetailsPanelOpen(true);
        }}
      />

      {/* PM Reassignment Confirmation Dialog */}
      <AlertDialog open={showPmConfirmDialog} onOpenChange={setShowPmConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reassign Project Manager?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reassign this project to{" "}
              <strong>{pendingPmChange?.employee.name}</strong>?
              The previous project manager will lose their primary project association.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelPmChange}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmPmChange}>Confirm Reassignment</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AM Reassignment Confirmation Dialog */}
      <AlertDialog open={showAmConfirmDialog} onOpenChange={setShowAmConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reassign Account Manager?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to assign{" "}
              <strong>{pendingAmChange?.employee.name}</strong> as the Account Manager for this project?
              {accountManager && (
                <> <strong>{accountManager.name}</strong> will no longer be the Account Manager.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelAmChange}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmAmChange}>Confirm Reassignment</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel}>Discard Changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Budget Edit Modals */}
      {budgetData && (
        <>
          <EditBudgetModal
            open={salesBudgetEditModalOpen}
            onOpenChange={setSalesBudgetEditModalOpen}
            budgetData={budgetData}
            projectType={project?.type || "fixed-price"}
            billingTriggerType={project?.billingTriggerType}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSave={handleSaveBudget}
            initialTab="sales"
          />

          <EditBudgetModal
            open={costBudgetEditModalOpen}
            onOpenChange={setCostBudgetEditModalOpen}
            budgetData={budgetData}
            projectType={project?.type || "fixed-price"}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSave={handleSaveBudget}
            initialTab="cost"
          />

          <EditBudgetModal
            open={resourceBudgetEditModalOpen}
            onOpenChange={setResourceBudgetEditModalOpen}
            budgetData={budgetData}
            projectType={project?.type || "fixed-price"}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSave={handleSaveBudget}
            initialTab="resource"
          />

          <BudgetHistoryModal
            open={budgetHistoryModalOpen}
            onOpenChange={setBudgetHistoryModalOpen}
            revisions={budgetData.revisions || []}
            projectName={
              project?.code
                ? `${project.code} - ${project.name}`
                : project?.name || budgetData.projectName || `Project ${projectId}`
            }
            onRestore={handleRestoreRevision}
          />

          <BudgetExportModal
            open={budgetExportModalOpen}
            onOpenChange={setBudgetExportModalOpen}
            budgetData={budgetData}
          />

          {/* Resource Request Modal — Resource Scheduling integration */}
          {/* TODO: Remove mock data dependency when backend API is ready */}
          <ResourceRequestModal
            open={raiseRequestModalOpen}
            onOpenChange={setRaiseRequestModalOpen}
            prefilledRole={raiseRequestPrefilledRole}
            projectName={project?.name}
            projectCode={project?.code}
            projectId={projectId}
            projectStartDate={project?.startDate}
            onSubmitSuccess={handleRequestSubmitSuccess}
          />
        </>
      )}

      {/* Billing Settings Drawer */}
      <Sheet open={billingSettingsDrawerOpen} onOpenChange={handleDrawerOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-4">
          <SheetHeader className="mb-4">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle>Billing Configuration</SheetTitle>
                <SheetDescription>Configure billing settings for this project</SheetDescription>
              </div>
              <div className="flex items-center gap-2">
                {!drawerEditMode ? (
                  <Button onClick={handleDrawerEdit} size="sm" variant="outline">
                    <Settings2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button onClick={handleDrawerCancel} size="sm" variant="outline" disabled={drawerSaving}>
                      Cancel
                    </Button>
                    <Button onClick={handleDrawerSave} size="sm" disabled={drawerSaving}>
                      {drawerSaving ? "Saving..." : "Save"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-3">
            {/* Note: This drawer shows the same settings as the Settings tab, but in a convenient side panel */}
            {project?.type === "time-and-materials" ? (
              <>
                {/* Billing Configuration Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Billing Configuration</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Billing Frequency */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-billing-frequency" className="text-base font-semibold">
                        Billing Frequency
                      </Label>
                      <Select
                        value={drawerSettings.billingFrequency}
                        onValueChange={(value) => handleDrawerSettingsChange("billingFrequency", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-billing-frequency" className="w-full">
                          <SelectValue placeholder="Select billing frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Separator />

                    {/* Invoice Template */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-invoice-template" className="text-base font-semibold">
                        Invoice Template
                      </Label>
                      <Select
                        value={drawerSettings.invoiceTemplateId ? drawerSettings.invoiceTemplateId.toString() : (invoiceTemplates.find(t => t.isDefault)?.id || invoiceTemplates[0]?.id || '').toString()}
                        onValueChange={(value) => handleDrawerSettingsChange("invoiceTemplateId", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-invoice-template" className="w-full">
                          <SelectValue placeholder="Select invoice template" />
                        </SelectTrigger>
                        <SelectContent>
                          {invoiceTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id.toString()}>
                              {t.name}{t.isDefault ? ' (Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Choose the template to use when generating invoices for this project
                      </p>
                      <Button 
                        variant="link" 
                        size="sm" 
                        className="h-auto p-0 text-xs"
                        onClick={() => navigate('/settings/templates')}
                      >
                        Manage Templates →
                      </Button>
                    </div>

                    <Separator />

                    {/* Auto Generate Draft Invoice */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="drawer-auto-generate-toggle" className="text-base font-semibold">
                            Auto Generate Draft Invoice
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Automatically create draft invoices based on a schedule
                          </p>
                        </div>
                        <Switch
                          id="drawer-auto-generate-toggle"
                          checked={drawerSettings.autoGenerateEnabled}
                          onCheckedChange={(checked) => handleDrawerSettingsChange("autoGenerateEnabled", checked)}
                          disabled={!drawerEditMode}
                        />
                      </div>

                      {drawerSettings.autoGenerateEnabled && (
                        <div className="ml-0 space-y-2 pt-2">
                          {/* Monthly Pattern Options */}
                          {drawerSettings.billingFrequency === "monthly" && (
                            <div className="space-y-1.5">
                              <Label className="text-sm font-medium">Generate invoice on:</Label>
                              <RadioGroup
                                value={drawerSettings.monthlyPattern}
                                onValueChange={(value) => handleDrawerSettingsChange("monthlyPattern", value)}
                                className="space-y-2"
                                disabled={!drawerEditMode}
                              >
                                <div className="space-y-2">
                                  <RadioGroupItem value="specific-day" id="drawer-specific-day">
                                    Specific day of month
                                  </RadioGroupItem>
                                  {drawerSettings.monthlyPattern === "specific-day" && (
                                    <div className="space-y-1 ml-7">
                                      <Input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={drawerSettings.monthlyDay}
                                        disabled={!drawerEditMode}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          if (value === "") {
                                            handleDrawerSettingsChange("monthlyDay", "");
                                            return;
                                          }
                                          const numValue = parseInt(value, 10);
                                          if (!isNaN(numValue) && numValue >= 1 && numValue <= 31) {
                                            handleDrawerSettingsChange("monthlyDay", value);
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const value = e.target.value;
                                          if (value === "" || parseInt(value, 10) < 1 || parseInt(value, 10) > 31) {
                                            handleDrawerSettingsChange("monthlyDay", "1");
                                          }
                                        }}
                                        className="w-32"
                                        placeholder="1-31"
                                        aria-invalid={
                                          drawerSettings.monthlyDay === "" ||
                                          parseInt(drawerSettings.monthlyDay, 10) < 1 ||
                                          parseInt(drawerSettings.monthlyDay, 10) > 31
                                        }
                                      />
                                      <p className="text-xs text-muted-foreground">Enter a day between 1 and 31</p>
                                    </div>
                                  )}
                                </div>

                                <RadioGroupItem value="last-day" id="drawer-last-day">
                                  Last day of the month
                                </RadioGroupItem>

                                <RadioGroupItem value="first-working-day" id="drawer-first-working-day">
                                  First working day of the month (Mon-Fri)
                                </RadioGroupItem>

                                <RadioGroupItem value="last-working-day" id="drawer-last-working-day">
                                  Last working day of the month (Mon-Fri)
                                </RadioGroupItem>
                              </RadioGroup>
                            </div>
                          )}

                          {/* Weekly Pattern Options */}
                          {drawerSettings.billingFrequency === "weekly" && (
                            <div className="space-y-1.5">
                              <Label htmlFor="drawer-weekly-day" className="text-sm font-medium">
                                Generate invoice on:
                              </Label>
                              <Select
                                value={drawerSettings.weeklyDay}
                                onValueChange={(value) => handleDrawerSettingsChange("weeklyDay", value)}
                                disabled={!drawerEditMode}
                              >
                                <SelectTrigger id="drawer-weekly-day" className="w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="monday">Monday</SelectItem>
                                  <SelectItem value="tuesday">Tuesday</SelectItem>
                                  <SelectItem value="wednesday">Wednesday</SelectItem>
                                  <SelectItem value="thursday">Thursday</SelectItem>
                                  <SelectItem value="friday">Friday</SelectItem>
                                  <SelectItem value="saturday">Saturday</SelectItem>
                                  <SelectItem value="sunday">Sunday</SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground">
                                Draft invoices will be generated every week on this day
                              </p>
                            </div>
                          )}

                          {/* Preview of next invoice date */}
                          {(() => {
                            const nextDate = getNextInvoiceDateForDrawer();
                            if (nextDate) {
                              return (
                                <div className="rounded-lg bg-muted/50 border border-muted p-4 text-sm">
                                  <div className="flex items-start gap-3">
                                    <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                                    <div>
                                      <span className="font-semibold text-foreground">Preview</span>
                                      <p className="text-muted-foreground mt-1">
                                        Next invoice will generate on{" "}
                                        <span className="font-medium text-foreground">
                                          {nextDate.toLocaleDateString("en-US", {
                                            weekday: "long",
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                          })}
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Recharge Configuration Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Recharge Configuration</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Expense Recharge */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-expense-recharge" className="text-base font-semibold">Expense Recharge</Label>
                      <Select
                        value={drawerSettings.expenseRecharge}
                        onValueChange={(value) => {
                          handleDrawerSettingsChange("expenseRecharge", value);
                          if (value !== "markup") {
                            handleDrawerSettingsChange("expenseMarkupPercent", "");
                          }
                        }}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-expense-recharge">
                          <SelectValue placeholder="Select expense recharge method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="at-cost">At Cost</SelectItem>
                          <SelectItem value="markup">Mark up %</SelectItem>
                          <SelectItem value="not-rechargeable">Not Rechargeable</SelectItem>
                        </SelectContent>
                      </Select>

                      {drawerSettings.expenseRecharge === "markup" && (
                        <div className="ml-7 mt-1.5 space-y-1.5">
                          <Label htmlFor="drawer-expense-markup-percent" className="text-sm">
                            Markup Percentage
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="drawer-expense-markup-percent"
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              placeholder="0.00"
                              value={drawerSettings.expenseMarkupPercent}
                              onChange={(e) => handleDrawerSettingsChange("expenseMarkupPercent", e.target.value)}
                              className="w-32"
                              disabled={!drawerEditMode}
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Expenses will be billed at cost plus this percentage
                          </p>
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Purchase Recharge */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-purchase-recharge" className="text-base font-semibold">Purchase Recharge</Label>
                      <Select
                        value={drawerSettings.purchaseRecharge}
                        onValueChange={(value) => {
                          handleDrawerSettingsChange("purchaseRecharge", value);
                          if (value !== "markup") {
                            handleDrawerSettingsChange("purchaseMarkupPercent", "");
                          }
                        }}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-purchase-recharge">
                          <SelectValue placeholder="Select purchase recharge method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="at-cost">At Cost</SelectItem>
                          <SelectItem value="markup">Mark up %</SelectItem>
                          <SelectItem value="not-rechargeable">Not Rechargeable</SelectItem>
                        </SelectContent>
                      </Select>

                      {drawerSettings.purchaseRecharge === "markup" && (
                        <div className="ml-7 mt-1.5 space-y-1.5">
                          <Label htmlFor="drawer-purchase-markup-percent" className="text-sm">
                            Markup Percentage
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="drawer-purchase-markup-percent"
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              placeholder="0.00"
                              value={drawerSettings.purchaseMarkupPercent}
                              onChange={(e) => handleDrawerSettingsChange("purchaseMarkupPercent", e.target.value)}
                              className="w-32"
                              disabled={!drawerEditMode}
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Purchases will be billed at cost plus this percentage
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Invoice Settings Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Banknote className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Invoice Settings</CardTitle>
                    </div>
                    <CardDescription>Configure invoice caps and billing limits for this project</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Not to Exceed Cap */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="drawer-not-to-exceed-cap" className="text-base font-semibold">
                            Invoice Cap
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Set a maximum amount that can be billed on each invoice
                          </p>
                        </div>
                        <Switch
                          id="drawer-not-to-exceed-cap"
                          checked={drawerSettings.notToExceedCapEnabled}
                          onCheckedChange={(checked) => {
                            handleDrawerSettingsChange("notToExceedCapEnabled", checked);
                            if (!checked) {
                              handleDrawerSettingsChange("notToExceedCapAmount", "");
                            }
                          }}
                          disabled={!drawerEditMode}
                        />
                      </div>

                      {drawerSettings.notToExceedCapEnabled && (
                        <div className="ml-0 mt-1.5 space-y-1.5">
                          <Label htmlFor="drawer-cap-amount" className="text-sm">
                            Maximum Amount Per Invoice
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="drawer-cap-amount"
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={drawerSettings.notToExceedCapAmount}
                              onChange={(e) => handleDrawerSettingsChange("notToExceedCapAmount", e.target.value)}
                              className="w-40"
                              disabled={!drawerEditMode}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Any invoice exceeding this amount will require manual review
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : project?.type === "fixed-price" ? (
              <>
                {/* Fixed Fee — Billing Configuration Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Billing Configuration</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Billing Trigger Type */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-billing-trigger-type" className="text-base font-semibold">Billing Trigger Type</Label>
                      <Select
                        value={drawerSettings.billingTriggerType || project.billingTriggerType || ''}
                        onValueChange={(value) => handleDrawerSettingsChange("billingTriggerType", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-billing-trigger-type" className="w-full">
                          <SelectValue placeholder="Select trigger type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="date">Date-based</SelectItem>
                          <SelectItem value="percent-complete">% Complete</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Determines when billing milestones are evaluated.
                      </p>
                    </div>

                    {/* Task 13.1 — Completion % Calculation — only for percent-complete projects */}
                    {(drawerSettings.billingTriggerType || project.billingTriggerType) === 'percent-complete' && (
                      <>
                        <Separator />
                        <div className="space-y-2">
                          <Label className="text-base font-semibold">Completion % Calculation</Label>
                          <p className="text-sm text-muted-foreground">
                            Controls how the project's completion percentage is determined for milestone evaluation.
                          </p>
                          <Select
                            value={project.completionPercentMode ?? 'based-on-time'}
                            onValueChange={(value) => {
                              if (drawerEditMode) {
                                handleCompletionModeChange(value as 'manual' | 'based-on-time');
                              }
                            }}
                            disabled={!drawerEditMode}
                          >
                            <SelectTrigger id="drawer-completion-mode" className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="based-on-time">Based on Approved Time</SelectItem>
                              <SelectItem value="manual">Manual</SelectItem>
                            </SelectContent>
                          </Select>
                          {project.completionPercentMode === 'based-on-time' ? (
                            <p className="text-xs text-muted-foreground">
                              Completion % is automatically calculated from approved hours ÷ budget hours. Any existing override will be cleared when switching to this mode.
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Completion % is frozen at the current value. The PM must update it manually via the Project Overview.
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    <Separator />

                    {/* Invoice Template */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-fixed-invoice-template" className="text-base font-semibold">
                        Invoice Template
                      </Label>
                      <Select
                        value={drawerSettings.invoiceTemplateId ? drawerSettings.invoiceTemplateId.toString() : (invoiceTemplates.find(t => t.isDefault)?.id || invoiceTemplates[0]?.id || '').toString()}
                        onValueChange={(value) => handleDrawerSettingsChange("invoiceTemplateId", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-fixed-invoice-template" className="w-full">
                          <SelectValue placeholder="Select invoice template" />
                        </SelectTrigger>
                        <SelectContent>
                          {invoiceTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id.toString()}>
                              {t.name}{t.isDefault ? ' (Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Choose the template to use when generating invoices for this project
                      </p>
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => navigate('/settings/templates')}
                      >
                        Manage Templates →
                      </Button>
                    </div>

                    <Separator />

                    {/* Default Cost Centre — required for finance system posting */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-fixed-cost-centre" className="text-base font-semibold">
                        Default Cost Centre
                      </Label>
                      {costCentres.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          No cost centres are linked to this project yet. Add them in the Cost Centres tab before configuring billing.
                        </p>
                      ) : (
                        <>
                          <Select
                            value={drawerSettings.defaultCostCentreCode ?? ""}
                            onValueChange={(value) => {
                              handleDrawerSettingsChange("defaultCostCentreCode", value);
                              setDefaultCostCentreError(false);
                            }}
                            disabled={!drawerEditMode}
                          >
                            <SelectTrigger
                              id="drawer-fixed-cost-centre"
                              className={cn("w-full", defaultCostCentreError && "border-destructive")}
                            >
                              <SelectValue placeholder="Select a cost centre…" />
                            </SelectTrigger>
                            <SelectContent>
                              {costCentres.map((cc) => (
                                <SelectItem key={cc.id} value={cc.code}>
                                  {cc.code} – {cc.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {defaultCostCentreError ? (
                            <p className="text-xs text-destructive">
                              A default cost centre is required for finance system posting.
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Required for finance system posting. Applied to all invoices generated from this project.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">
                  Billing settings are not available for this project type.
                </p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Invoice Generation Wizard */}
      <InvoiceGenerationWizard
        open={invoiceWizardOpen}
        onOpenChange={(open) => {
          setInvoiceWizardOpen(open);
          // Reset Fixed Fee milestone when wizard closes so it doesn't carry over
          if (!open) setFixedFeeMilestone(null);
        }}
        selectedWipItems={wipItems.filter((item) => selectedWipItems.includes(item.id)) as { id: string; date: string; resource: string; task: string; description: string; type: 'Time' | 'Expense' | 'Purchase'; quantity: number; unit: string; rate: number; amount: number; costCentre: string; reference: string; costRate: number; }[]}
        projectName={project?.name || ""}
        projectCode={project?.code || ""}
        client={client}
        company={currentCompany}
        expenseRecharge={projectSettings.expenseRecharge}
        expenseMarkupPercent={projectSettings.expenseMarkupPercent}
        purchaseRecharge={projectSettings.purchaseRecharge}
        purchaseMarkupPercent={projectSettings.purchaseMarkupPercent}
        notToExceedCapEnabled={projectSettings.notToExceedCapEnabled}
        notToExceedCapAmount={projectSettings.notToExceedCapAmount}
        invoiceTemplateId={projectSettings.invoiceTemplateId != null ? projectSettings.invoiceTemplateId : undefined}
        billingEvent={fixedFeeMilestone != null ? { name: fixedFeeMilestone.name, amount: fixedFeeMilestone.amount, dueDate: fixedFeeMilestone.dueDate } : undefined}
        onTemplateChange={async (templateId) => {
          // Task 2.12c — persist the template change back to the project's billing config
          setProjectSettings((prev) => ({ ...prev, invoiceTemplateId: templateId.toString() }));
          if (numericProjectId) {
            await updateProjectBillingConfig(numericProjectId, { invoiceTemplateId: templateId });
          }
        }}
        onInvoiceGenerated={async (invoice) => {
          if (dataMode !== "live") {
            console.log("Invoice generated (demo mode):", invoice);
            setSelectedWipItems([]);
            return;
          }

          try {
            setBillingActionLoading(true);

            const invoiceDateStr = invoice.date ? new Date(invoice.date).toISOString() : undefined;
            const dueDateStr = invoice.dueDate ? new Date(invoice.dueDate).toISOString() : undefined;

            let createdInvoice: Awaited<ReturnType<typeof createBillingDraft>>;

            if (invoice.isFixedFee && invoice.billingEvent) {
              // ── Fixed Fee path: create a draft with the fixed-fee amount directly ──
              const description = invoice.fixedFeeDescription || invoice.billingEvent.name;
              createdInvoice = await createBillingDraft(
                numericProjectId,
                [],           // no WIP items for fixed fee
                description,
                undefined,    // notes
                invoiceDateStr,
                dueDateStr,
                {
                  isFixedFee: true,
                  fixedFeeAmount: invoice.billingEvent.amount,
                  costCentreCode: projectSettings.defaultCostCentreCode ?? undefined,
                  milestoneId: fixedFeeMilestone?.id,
                },
              );
            } else {
              // ── WIP-based path (existing behaviour) ──
              const wipItemIds = invoice.lineItems
                .map((li: { originalWipId?: string }) => {
                  if (!li.originalWipId) return null;
                  const numId = parseInt(li.originalWipId.replace("wip-", ""), 10);
                  return isNaN(numId) ? null : numId;
                })
                .filter((id: number | null): id is number => id !== null);

              if (wipItemIds.length === 0) {
                console.warn("No valid WIP item IDs found in wizard line items");
                return;
              }

              const description = invoice.reference || undefined;
              const notes = invoice.notes || undefined;
              createdInvoice = await createBillingDraft(numericProjectId, wipItemIds, description, notes, invoiceDateStr, dueDateStr);

              // Apply any edits the user made in the wizard (quantity/rate/description changes)
              const edits: Array<{ wipLedgerId: number; quantity?: number; rate?: number; netAmount?: number; description?: string }> = [];
              for (const li of invoice.lineItems) {
                if (!li.originalWipId) continue;
                const numId = parseInt(li.originalWipId.replace("wip-", ""), 10);
                if (isNaN(numId)) continue;

                const originalWip = wipItems.find((w) => w.id === li.originalWipId);
                if (!originalWip) continue;

                const edit: { wipLedgerId: number; quantity?: number; rate?: number; netAmount?: number; description?: string } = { wipLedgerId: numId };
                let hasChanges = false;

                if (li.quantity !== originalWip.quantity) {
                  edit.quantity = li.quantity;
                  hasChanges = true;
                }
                if (li.rate !== originalWip.rate) {
                  edit.rate = li.rate;
                  hasChanges = true;
                }
                if (li.amount !== originalWip.amount) {
                  edit.netAmount = li.amount;
                  hasChanges = true;
                }

                if (hasChanges) {
                  edits.push(edit);
                }
              }

              if (edits.length > 0) {
                await updateBillingDraft(createdInvoice.id, { edits });
              }
            }

            // Post the draft to Access Financials to create the sales transaction
            const result = await postBillingDraftToFinancials(createdInvoice.id);

            if (result.success) {
              toast.success("Invoice generated and posted to Access Financials", {
                description: result.financialsIds?.length
                  ? `Transaction ID${result.financialsIds.length > 1 ? "s" : ""}: ${result.financialsIds.join(", ")}`
                  : undefined,
              });
              setSelectedWipItems([]);
              await refetchBillingData();
            } else if (result.validationErrors && result.validationErrors.length > 0) {
              // Validation errors from Financials — invoice stays as draft
              const errorMessages = result.validationErrors
                .map((e) => {
                  const details = e.details?.map((d) => `${d.field}: ${d.value}`).join(", ");
                  return details ? `${e.message} (${details})` : e.message;
                })
                .join("\n");
              toast.error("Financials validation failed", {
                description: errorMessages,
                duration: 10000,
              });
              // Still refetch to show the draft invoice that was created
              setSelectedWipItems([]);
              await refetchBillingData();
            } else {
              // Unexpected error — invoice stays as draft
              toast.error("Failed to post invoice to Financials", {
                description: result.error || "An unexpected error occurred. The invoice has been saved as a draft.",
                duration: 8000,
              });
              setSelectedWipItems([]);
              await refetchBillingData();
            }
          } catch (error) {
            console.error("Failed to generate invoice:", error);
            handleApiError(error, `/api/billing/${numericProjectId}/drafts`, "POST");
          } finally {
            setBillingActionLoading(false);
          }
        }}
      />

      {/* Create Draft Dialog */}
      <CreateDraftDialog
        open={createDraftDialogOpen}
        onOpenChange={setCreateDraftDialogOpen}
        selectedItemCount={selectedWipItems.length}
        onConfirm={handleCreateDraft}
        loading={billingActionLoading}
      />

      {/* Invoice Details Sheet */}
      <InvoiceDetailsSheet
        invoiceId={selectedInvoiceId}
        open={invoiceDetailsSheetOpen}
        onOpenChange={setInvoiceDetailsSheetOpen}
        onDeleteDraft={handleDeleteDraft}
        onReturnToDraft={() => refetchBillingData()}
      />

      {/* Invoice Column Configurator */}
      <ColumnConfiguratorDrawer
        open={invoiceColumnConfigOpen}
        onOpenChange={setInvoiceColumnConfigOpen}
        columns={invoiceColumns}
        onSave={(updatedColumns) => {
          updateInvoiceColumns(updatedColumns);
        }}
        defaultColumns={DEFAULT_INVOICE_COLUMNS}
      />

      {/* WIP Column Configurator */}
      <ColumnConfiguratorDrawer
        open={wipColumnConfigOpen}
        onOpenChange={setWipColumnConfigOpen}
        columns={wipColumns}
        onSave={(updatedColumns) => {
          updateWipColumns(updatedColumns);
        }}
        defaultColumns={DEFAULT_WIP_COLUMNS}
      />

      {/* Client Side Panel */}
      <ClientSidePanel
        client={activePanelClientId !== null ? clients.find((c) => c.id === activePanelClientId) ?? null : null}
        open={activePanelClientId !== null}
        onClose={closeClientPanel}
        companyId={currentCompany?.id}
      />

      {/* Client Change Confirmation Dialog */}
      <ClientChangeConfirmDialog
        open={clientChangeDialogOpen}
        previousClient={previousClientForDialog}
        newClient={pendingClientChange?.selectedClient ?? null}
        actualHours={project?.actualHours ?? 0}
        onConfirm={handleClientChangeConfirm}
        onCancel={handleClientChangeCancel}
      />
    </div>
  );
}
